import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Trash2, Filter, Banknote, DollarSign, ArrowRightLeft, Receipt, Check, Calendar, RefreshCw, Edit, Search, FileText, Play, CreditCard, Landmark, ArrowUpRight, ArrowDownRight, Eye, EyeOff, Calculator, TrendingUp, PieChart, History, Target, AlertTriangle, Upload, Download, Percent, Lock, Printer } from 'lucide-react';
import { formatDateDisplay, formatCurrency } from '@/utils/formatters';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useWebSocket } from '@/hooks/useWebSocket';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';
import { useFinanceiroAccess, FinanceiroAccessModal } from '@/components/ScreenLock';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// Formatador de moeda com opção de ocultar
const formatMoney = (value, hidden = false) => {
  if (hidden) return '•••••';
  return `R$ ${Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * Agrupa transações por dia, ordenadas do dia mais recente para o mais antigo.
 * Retorna array de { dateKey, dateLabel, weekdayLabel, humanLabel, totalEntrada, totalSaida, items }.
 */
const WEEKDAYS_PT = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
const WEEKDAYS_PT_SHORT = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];

const groupTransactionsByDay = (list) => {
  const groups = new Map();
  for (const t of list) {
    const raw = t.data || t.dataRecebimento || t.data_pagamento || t.createdAt;
    if (!raw) continue;
    const dateKey = String(raw).substring(0, 10); // YYYY-MM-DD
    if (!groups.has(dateKey)) groups.set(dateKey, []);
    groups.get(dateKey).push(t);
  }

  const todayKey = new Date().toISOString().substring(0, 10);
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayKey = yesterday.toISOString().substring(0, 10);

  return Array.from(groups.entries())
    .sort((a, b) => (a[0] < b[0] ? 1 : a[0] > b[0] ? -1 : 0))
    .map(([dateKey, items]) => {
      const [y, m, d] = dateKey.split('-').map(Number);
      const dateObj = new Date(y, m - 1, d);
      const weekdayIdx = dateObj.getDay();
      const dateLabel = `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
      let humanLabel;
      if (dateKey === todayKey) humanLabel = 'Hoje';
      else if (dateKey === yesterdayKey) humanLabel = 'Ontem';
      else humanLabel = WEEKDAYS_PT[weekdayIdx];

      let totalEntrada = 0;
      let totalSaida = 0;
      for (const t of items) {
        const isDespesa = t.tipo === 'despesa' || t.operacao === 'saida';
        const valor = Number(t.valorLiquido ?? (t.valor || 0) + (t.valor_adicional || 0)) || 0;
        if (isDespesa) totalSaida += valor;
        else totalEntrada += valor;
      }

      return {
        dateKey,
        dateLabel,
        weekdayLabel: WEEKDAYS_PT_SHORT[weekdayIdx],
        humanLabel,
        totalEntrada,
        totalSaida,
        items,
      };
    });
};

/**
 * Cabeçalho visual do dia — usado em todas as listagens agrupadas.
 * Renderiza um bloco com faixa colorida, data grande, dia da semana e totais.
 */
const DayHeader = ({ group, columnsForTable = null, colorAccent = 'primary' }) => {
  const accentBar = {
    primary: 'before:bg-primary',
    yellow: 'before:bg-yellow-500',
    blue: 'before:bg-blue-500',
  }[colorAccent] || 'before:bg-primary';

  const inner = (
    <div
      className={`relative flex flex-wrap items-center justify-between gap-2 py-2.5 px-3 sm:px-4 rounded-md bg-gradient-to-r from-muted/70 via-muted/40 to-transparent border-y border-border/60 ${accentBar} before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:rounded-l-md`}
      data-testid={`day-header-${group.dateKey}`}
    >
      <div className="flex items-baseline gap-2 min-w-0">
        <span className="text-base sm:text-lg font-bold tracking-tight text-foreground">
          {group.dateLabel}
        </span>
        <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-muted-foreground px-1.5 py-0.5 rounded bg-background/60 border border-border/60">
          {group.weekdayLabel}
        </span>
        <span className="text-xs text-muted-foreground hidden sm:inline">
          • {group.humanLabel}
        </span>
        <span className="text-xs text-muted-foreground">
          • {group.items.length} {group.items.length === 1 ? 'lançamento' : 'lançamentos'}
        </span>
      </div>
      <div className="flex items-center gap-3 text-xs font-mono">
        {group.totalEntrada > 0 && (
          <span className="text-green-500 font-semibold">
            + R$ {group.totalEntrada.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        )}
        {group.totalSaida > 0 && (
          <span className="text-red-500 font-semibold">
            − R$ {group.totalSaida.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        )}
      </div>
    </div>
  );

  if (columnsForTable) {
    return (
      <tr className="bg-transparent">
        <td colSpan={columnsForTable} className="p-0 pt-3 pb-1">
          {inner}
        </td>
      </tr>
    );
  }
  return inner;
};

export default function Transactions() {
  // Hook de proteção do financeiro
  const { isVerified, showModal, setShowModal, verifyPassword, needsVerification } = useFinanceiroAccess();

  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loans, setLoans] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loanDialogOpen, setLoanDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState(''); // Estado para pesquisa
  
  // Estado para ocultar valores
  const [hideValues, setHideValues] = useState(() => {
    const saved = localStorage.getItem('hideFinancialValues');
    return saved === 'true';
  });
  
  const toggleHideValues = () => {
    const newValue = !hideValues;
    setHideValues(newValue);
    localStorage.setItem('hideFinancialValues', newValue.toString());
  };
  
  // Estado para edição de transações
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editFormData, setEditFormData] = useState({
    descricao: '',
    valor: '',
    data: '',
    categoria: '',
    conta_id: ''
  });
  
  // Estados para Contas a Receber
  const [receivables, setReceivables] = useState([]);
  const [receivablesStats, setReceivablesStats] = useState(null);
  const [activeTab, setActiveTab] = useState('extrato'); // 'extrato', 'pagar', 'receber', 'futuros', 'prolabore', 'cheques', 'lucro'
  
  // Estados para Recebimento de Cartões
  const [futureReceivables, setFutureReceivables] = useState(null);
  
  // Estados para Aba de Lucro
  const [orders, setOrders] = useState([]);
  const [lucroSelectedMonth, setLucroSelectedMonth] = useState('');
  
  // Estados para Pró-labore
  const [prolaboreDialogOpen, setProlaboreDialogOpen] = useState(false);
  const [prolabores, setProlabores] = useState([]);
  const [prolaboreForm, setProlaboreForm] = useState({
    beneficiario: '',
    valor: '',
    data: new Date().toISOString().split('T')[0],
    conta_id: '',
    observacao: ''
  });
  
  // Estados para Cheques
  const [cheques, setCheques] = useState([]);
  const [chequesStats, setChequesStats] = useState(null);
  const [chequeDialogOpen, setChequeDialogOpen] = useState(false);
  const [chequeForm, setChequeForm] = useState({
    numero: '',
    valor: '',
    data_pagamento: '',
    beneficiario: '',
    observacao: '',
    conta_id: ''
  });
  const [compensarDialogOpen, setCompensarDialogOpen] = useState(false);
  const [chequeToCompensar, setChequeToCompensar] = useState(null);
  const [compensarContaId, setCompensarContaId] = useState('');
  
  // Estados para Contas a Pagar
  const [bills, setBills] = useState([]);
  const [pendingWithRecurring, setPendingWithRecurring] = useState([]);
  const [recurringBills, setRecurringBills] = useState([]);
  const [billCategories, setBillCategories] = useState([]);
  const [billDialogOpen, setBillDialogOpen] = useState(false);
  const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);
  const [installmentDialogOpen, setInstallmentDialogOpen] = useState(false);
  const [billPayDialogOpen, setBillPayDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [selectedRecurring, setSelectedRecurring] = useState(null);
  const [generateValor, setGenerateValor] = useState('');
  const [billPayContaId, setBillPayContaId] = useState('');
  const [billPayValor, setBillPayValor] = useState('');
  const [billPayComprovante, setBillPayComprovante] = useState(null);
  const [uploadingComprovante, setUploadingComprovante] = useState(false);
  const [billFilterMonth, setBillFilterMonth] = useState('');
  const [extratoFilterMonth, setExtratoFilterMonth] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }); // Mês atual por padrão
  const [splitPayment, setSplitPayment] = useState(false);
  const [splitPayments, setSplitPayments] = useState([
    { conta_id: '', valor: '' },
    { conta_id: '', valor: '' }
  ]);
  const [tipoAdicionalPagamento, setTipoAdicionalPagamento] = useState('nenhum');
  const [valorAdicionalPagamento, setValorAdicionalPagamento] = useState('');
  const [paidBillsPage, setPaidBillsPage] = useState(1);
  const PAID_BILLS_PER_PAGE = 15;

  // Mapeamento automático de categoria para classificação DRE
  const CATEGORIA_TO_DRE = {
    // Custos Fixos
    'Aluguel': 'custo_fixo',
    'Salários': 'custo_fixo',
    'Pro-labore': 'custo_fixo',
    'Pró-labore': 'custo_fixo',
    'Contador': 'custo_fixo',
    'Seguro': 'custo_fixo',
    'Software': 'custo_fixo',
    'Plano de Saúde': 'custo_fixo',
    // Despesas Operacionais
    'Impostos': 'despesa_operacional',
    'Energia': 'despesa_operacional',
    'Água': 'despesa_operacional',
    'Internet': 'despesa_operacional',
    'Telefone': 'despesa_operacional',
    'Manutenção': 'despesa_operacional',
    'Limpeza': 'despesa_operacional',
    'Material de Escritório': 'despesa_operacional',
    'Taxas Bancárias': 'despesa_operacional',
    // Custos Variáveis
    'Combustível': 'custo_variavel',
    'Frete': 'custo_variavel',
    'Matéria-prima': 'custo_variavel',
    'Fornecedores': 'custo_variavel',
    'Insumos': 'custo_variavel',
    'Madeira': 'estoque',
    // Investimentos
    'Equipamentos': 'investimento',
    'Investimento': 'investimento',
    'Veículos': 'investimento',
    'Reforma': 'investimento',
    // Receitas (para transações de entrada)
    'receita': 'receita',
    'receita_servicos': 'receita',
    'recebimento': 'receita',
    'transferencia_entrada': 'receita',
    'outras_receitas': 'receita',
  };

  // Função para auto-classificar baseado na categoria
  const getAutoClassificacaoDRE = (categoria) => {
    if (!categoria) return '';
    return CATEGORIA_TO_DRE[categoria] || '';
  };

  const [billFormData, setBillFormData] = useState({
    descricao: '',
    valor: '',
    dataVencimento: '',
    categoria: '',
    classificacao_dre: ''
  });
  const [recurringFormData, setRecurringFormData] = useState({
    descricao: '',
    valor: '',
    categoria: '',
    tipo_valor: 'fixo',
    dia_vencimento: '10',
    classificacao_dre: ''
  });
  const [installmentFormData, setInstallmentFormData] = useState({
    descricao: '',
    valorTotal: '',
    numParcelas: '2',
    dataPrimeiraParcela: '',
    categoria: '',
    classificacao_dre: ''
  });
  const [parcelas, setParcelas] = useState([]);
  const [showParcelasPreview, setShowParcelasPreview] = useState([]);

  // Estados para Contas Bancárias
  const [bankAccountDialogOpen, setBankAccountDialogOpen] = useState(false);
  const [editingBankAccount, setEditingBankAccount] = useState(null);
  const [bankAccountFormData, setBankAccountFormData] = useState({
    nome: '',
    banco: '',
    saldo: '',
    conta_cartao: false,
    limite_cheque_especial: ''
  });
  const [extratoDialogOpen, setExtratoDialogOpen] = useState(false);
  const [extratoAccount, setExtratoAccount] = useState(null);
  const [extratoTransactionsList, setExtratoTransactionsList] = useState([]);
  const [extratoLoading, setExtratoLoading] = useState(false);
  const [extratoFilter, setExtratoFilter] = useState('all');
  const [extratoPeriodo, setExtratoPeriodo] = useState('all');
  const [extratoDataInicio, setExtratoDataInicio] = useState('');
  const [extratoDataFim, setExtratoDataFim] = useState('');
  const [extratoCurrentPage, setExtratoCurrentPage] = useState(1);
  const [extratoItemsPerPage] = useState(30);
  
  // Paginação da tabela de transações principal
  const [transCurrentPage, setTransCurrentPage] = useState(1);
  const [transItemsPerPage] = useState(20);
  
  // Modal para conta avulsa
  const [receivableDialogOpen, setReceivableDialogOpen] = useState(false);
  const [receivableForm, setReceivableForm] = useState({
    cliente_nome: '',
    cliente_id: '',
    valor: '',
    data_vencimento: '',
    observacao: '',
    num_parcelas: 1
  });
  const [parcelasPreview, setParcelasPreview] = useState([]);
  
  // Estados para seleção de cliente
  const [customers, setCustomers] = useState([]);
  const [clienteMode, setClienteMode] = useState('existente'); // 'existente', 'novo', 'avulso'
  const [clienteSearch, setClienteSearch] = useState('');
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);
  const [novoCliente, setNovoCliente] = useState({
    nome: '',
    telefone: '',
    email: '',
    cpf: '',
    endereco: '',
    tipo: 'PF'
  });

  // Modal para marcar como pago
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [receivableToPay, setReceivableToPay] = useState(null);
  const [payForm, setPayForm] = useState({
    forma_pagamento: 'PIX',
    conta_id: ''
  });

  const [formData, setFormData] = useState({
    operacao: 'entrada',
    tipo: 'pix',
    valor: '',
    descricao: '',
    operadora: 'visa',
    parcelas: 1,
    data: new Date().toISOString().split('T')[0],
    conta_id: '',
    categoria: '',
    classificacao_dre: '',
    tipo_adicional: 'nenhum', // 'nenhum', 'tarifa', 'juros'
    valor_adicional: ''
  });
  
  // Filtro por período
  const [filtroPeriodo, setFiltroPeriodo] = useState('all');
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  
  // Categorias de transação
  const [transactionCategories, setTransactionCategories] = useState([]);

  // Estados para Empréstimos Pessoais (Conta Corrente com Terceiros)
  const [personalLoans, setPersonalLoans] = useState([]);
  const [personalLoanDialogOpen, setPersonalLoanDialogOpen] = useState(false);
  const [editingPersonalLoan, setEditingPersonalLoan] = useState(null);
  const [personalLoanFormData, setPersonalLoanFormData] = useState({
    credor_nome: '',
    credor_telefone: '',
    valor_inicial: '',
    data_inicio: new Date().toISOString().split('T')[0],
    conta_id: '',  // Conta que recebe o valor do empréstimo
    observacoes: '',
    tipo_emprestimo: 'normal',  // 'normal' ou 'agiota'
    taxa_juros_mensal: '',  // Taxa de juros mensal em %
    tipo_juros: 'composto'  // 'simples' (sobre valor bruto) ou 'composto' (juros sobre juros)
  });
  const [movementDialogOpen, setMovementDialogOpen] = useState(false);
  const [selectedLoanForMovement, setSelectedLoanForMovement] = useState(null);
  const [movementFormData, setMovementFormData] = useState({
    tipo: 'pagamento',
    valor: '',
    data: new Date().toISOString().split('T')[0],
    conta_id: '',
    observacao: ''
  });
  const [loanMovements, setLoanMovements] = useState([]);
  const [viewingLoanMovements, setViewingLoanMovements] = useState(null);

  // Estados para Simples Nacional (DAS)
  const [simplesCalculo, setSimplesCalculo] = useState(null);
  const [simplesHistorico, setSimplesHistorico] = useState([]);
  const [simplesFaturamentos, setSimplesFaturamentos] = useState([]);
  const [simplesDasPagos, setSimplesDasPagos] = useState([]);
  const [simplesLoading, setSimplesLoading] = useState(false);
  const [simplesAno, setSimplesAno] = useState(new Date().getFullYear());
  const [simplesMes, setSimplesMes] = useState(new Date().getMonth() + 1);
  const [faturamentoDialogOpen, setFaturamentoDialogOpen] = useState(false);
  const [faturamentoForm, setFaturamentoForm] = useState({ ano: new Date().getFullYear(), mes: new Date().getMonth() + 1, valor: '', observacao: '' });
  const [dasPagoDialogOpen, setDasPagoDialogOpen] = useState(false);
  const [dasPagoForm, setDasPagoForm] = useState({ ano: new Date().getFullYear(), mes: new Date().getMonth() + 1, valor_pago: '', data_pagamento: '', observacao: '' });
  const [simulacaoDialogOpen, setSimulacaoDialogOpen] = useState(false);
  const [simulacaoForm, setSimulacaoForm] = useState({ faturamento_mes: '', meses_futuros: 6 });
  const [simulacaoResultado, setSimulacaoResultado] = useState(null);
  
  // Upload DAS PDF states
  const [dasUploadDialogOpen, setDasUploadDialogOpen] = useState(false);
  const [dasUploadLoading, setDasUploadLoading] = useState(false);
  const [dasExtractedData, setDasExtractedData] = useState(null);
  const [dasConfirmDialogOpen, setDasConfirmDialogOpen] = useState(false);

  const [loanFormData, setLoanFormData] = useState({
    valor: '',
    juros: '',
    data_recebimento: new Date().toISOString().split('T')[0],
    data_pagamento: '',
    conta_id: '',
    descricao: ''
  });

  const [transferFormData, setTransferFormData] = useState({
    conta_origem_id: '',
    conta_destino_id: '',
    valor: '',
    descricao: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Filtro combinado: tipo + período + pesquisa
  useEffect(() => {
    let filtered = transactions;
    
    // Filtro por pesquisa (descrição, categoria, conta)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(t => {
        const descricao = (t.descricao || '').toLowerCase();
        const categoria = (t.categoria || '').toLowerCase();
        const conta = bankAccounts.find(a => a.id === t.conta_id);
        const contaNome = (conta?.nome || '').toLowerCase();
        const valor = t.valor?.toString() || '';
        return descricao.includes(term) || categoria.includes(term) || contaNome.includes(term) || valor.includes(term);
      });
    }
    
    // Filtro por tipo
    if (filterType !== 'all') {
      filtered = filtered.filter(t => t.tipo === filterType);
    }
    
    // Filtro por período
    if (filtroPeriodo !== 'all') {
      filtered = filtered.filter(t => {
        if (!t.data) return true;
        const transDate = new Date(t.data);
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        
        if (filtroPeriodo === 'custom') {
          if (filtroDataInicio && transDate < new Date(filtroDataInicio)) return false;
          if (filtroDataFim) {
            const fimDate = new Date(filtroDataFim);
            fimDate.setHours(23, 59, 59, 999);
            if (transDate > fimDate) return false;
          }
          return true;
        } else {
          const dias = parseInt(filtroPeriodo);
          const startDate = new Date();
          startDate.setDate(startDate.getDate() - dias);
          startDate.setHours(0, 0, 0, 0);
          return transDate >= startDate;
        }
      });
    }
    
    setFilteredTransactions(filtered);
  }, [filterType, filtroPeriodo, filtroDataInicio, filtroDataFim, transactions, searchTerm, bankAccounts]);

  // WebSocket para atualizações em tempo real
  const handleWebSocketMessage = useCallback((message) => {
    if (message.type === 'data_updated') {
      console.log('Dados atualizados via WebSocket:', message.data);
      // Recarregar dados quando houver atualização
      fetchData();
      
      // Mostrar notificação visual se houver mensagem
      if (message.message) {
        const entity = message.data?.entity;
        // Mostrar toast apenas para entidades relevantes nesta página
        if (entity === 'transactions' || entity === 'bills' || entity === 'personal_loans') {
          toast.info(message.message, {
            description: 'Sincronizado automaticamente',
            duration: 4000,
          });
        }
      }
    }
  }, []);

  useWebSocket(handleWebSocketMessage, []);

  const fetchData = async () => {
    try {
      // Função helper para fazer request com fallback
      const safeRequest = async (url, fallback = []) => {
        try {
          const res = await axios.get(url);
          return res.data;
        } catch (err) {
          console.warn(`Erro ao carregar ${url}:`, err.message);
          return fallback;
        }
      };

      // Carregar dados essenciais em paralelo (com fallback para cada um)
      const safeGet = async (url, fallback = []) => {
        try {
          const res = await axios.get(url);
          return res.data || fallback;
        } catch (e) {
          console.warn(`Erro ao carregar ${url}:`, e.message);
          return fallback;
        }
      };
      
      const [transactions, loans, personalLoans, accounts, receivables, receivablesStats, customers, ordersData] = await Promise.all([
        safeGet(`${API}/transactions`, []),
        safeGet(`${API}/loans`, []),
        safeGet(`${API}/personal-loans`, []),
        safeGet(`${API}/bank-accounts`, []),
        safeGet(`${API}/receivables`, []),
        safeGet(`${API}/receivables/stats`, {}),
        safeGet(`${API}/customers`, []),
        safeGet(`${API}/orders`, [])
      ]);

      setTransactions(transactions);
      setFilteredTransactions(transactions);
      setLoans(loans);
      setPersonalLoans(personalLoans);
      setBankAccounts(accounts);
      setReceivables(receivables);
      setReceivablesStats(receivablesStats);
      setCustomers(customers);
      
      // Processar pedidos para aba de lucro - converter objeto para array se necessário
      const ordersArray = Array.isArray(ordersData) ? ordersData : Object.values(ordersData || {}).flat();
      setOrders(ordersArray);

      // Carregar dados secundários com fallback (não bloqueia a página se falhar)
      const safeRequestBills = async () => {
        try {
          // Buscar IDs primeiro
          const idsRes = await axios.get(`${API}/bills/ids`);
          const ids = idsRes.data || [];
          if (ids.length === 0) return [];
          
          // Buscar cada bill individualmente (em paralelo)
          const promises = ids.map(item => 
            axios.get(`${API}/bills/one/${item.id}`).then(r => r.data).catch(() => null)
          );
          const results = await Promise.all(promises);
          
          // Filtrar nulls e ordenar
          const bills = results.filter(b => b !== null);
          bills.sort((a, b) => (a.dataVencimento || '').localeCompare(b.dataVencimento || ''));
          return bills;
        } catch (err) {
          console.warn('[BILLS] falhou');
        }
        return [];
      };

      const safeRequestPending = async () => {
        try {
          // Buscar IDs pendentes
          const idsRes = await axios.get(`${API}/bills/ids`);
          const pendingIds = (idsRes.data || []).filter(i => i.status === 'pendente');
          if (pendingIds.length === 0) return [];
          
          // Buscar cada bill individualmente (em paralelo)
          const promises = pendingIds.map(item => 
            axios.get(`${API}/bills/one/${item.id}`).then(r => r.data).catch(() => null)
          );
          const results = await Promise.all(promises);
          
          // Filtrar nulls e ordenar
          const bills = results.filter(b => b !== null);
          bills.sort((a, b) => (a.dataVencimento || '').localeCompare(b.dataVencimento || ''));
          return bills;
        } catch (err) {
          console.warn('[PENDING] falhou');
        }
        return [];
      };

      const [futureData, prolaboreData, categoriesData, chequesData, chequesStatsData, billsData, pendingData, recurringData] = await Promise.all([
        safeRequest(`${API}/transactions/future-receivables`, []),
        safeRequest(`${API}/prolabores`, []),
        safeRequest(`${API}/settings/categories/bill`, { categories: [] }),
        safeRequest(`${API}/cheques`, []),
        safeRequest(`${API}/cheques/stats`, {}),
        safeRequestBills(),
        safeRequestPending(),
        safeRequest(`${API}/recurring-bills`, [])
      ]);

      setFutureReceivables(futureData);
      setProlabores(prolaboreData);
      setTransactionCategories(categoriesData?.categories || []);
      setBillCategories(categoriesData?.categories || []);
      setCheques(chequesData);
      setChequesStats(chequesStatsData);
      setBills(billsData);
      setPendingWithRecurring(pendingData);
      setRecurringBills(recurringData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados do Simples Nacional
  const fetchSimplesData = async () => {
    setSimplesLoading(true);
    try {
      const [calculoRes, historicoRes, faturamentosRes, dasPagosRes] = await Promise.all([
        axios.get(`${API}/simples/calculo?ano=${simplesAno}&mes=${simplesMes}`).catch(() => ({ data: null })),
        axios.get(`${API}/simples/historico`).catch(() => ({ data: [] })),
        axios.get(`${API}/simples/faturamento`).catch(() => ({ data: [] })),
        axios.get(`${API}/simples/das-pago`).catch(() => ({ data: [] }))
      ]);
      setSimplesCalculo(calculoRes.data);
      setSimplesHistorico(historicoRes.data);
      setSimplesFaturamentos(faturamentosRes.data);
      setSimplesDasPagos(dasPagosRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados do Simples:', error);
    } finally {
      setSimplesLoading(false);
    }
  };

  // Carregar dados do Simples quando mudar aba ou período
  useEffect(() => {
    if (activeTab === 'simples') {
      fetchSimplesData();
    }
  }, [activeTab, simplesAno, simplesMes]);

  // Salvar faturamento mensal
  const handleSaveFaturamento = async () => {
    if (!faturamentoForm.valor) {
      toast.error('Informe o valor do faturamento');
      return;
    }
    try {
      await axios.post(`${API}/simples/faturamento`, {
        ano: parseInt(faturamentoForm.ano),
        mes: parseInt(faturamentoForm.mes),
        valor: parseFloat(faturamentoForm.valor),
        observacao: faturamentoForm.observacao
      });
      toast.success('Faturamento salvo!');
      setFaturamentoDialogOpen(false);
      setFaturamentoForm({ ano: new Date().getFullYear(), mes: new Date().getMonth() + 1, valor: '', observacao: '' });
      fetchSimplesData();
    } catch (error) {
      toast.error('Erro ao salvar faturamento');
    }
  };

  // Excluir faturamento
  const handleDeleteFaturamento = async (ano, mes) => {
    if (!window.confirm('Excluir este faturamento?')) return;
    try {
      await axios.delete(`${API}/simples/faturamento/${ano}/${mes}`);
      toast.success('Faturamento excluído!');
      fetchSimplesData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  // Salvar DAS pago
  const handleSaveDasPago = async () => {
    if (!dasPagoForm.valor_pago || !dasPagoForm.data_pagamento) {
      toast.error('Preencha valor e data do pagamento');
      return;
    }
    try {
      await axios.post(`${API}/simples/das-pago`, {
        ano: parseInt(dasPagoForm.ano),
        mes: parseInt(dasPagoForm.mes),
        valor_pago: parseFloat(dasPagoForm.valor_pago),
        data_pagamento: dasPagoForm.data_pagamento,
        observacao: dasPagoForm.observacao
      });
      toast.success('DAS pago registrado!');
      setDasPagoDialogOpen(false);
      setDasPagoForm({ ano: new Date().getFullYear(), mes: new Date().getMonth() + 1, valor_pago: '', data_pagamento: '', observacao: '' });
      fetchSimplesData();
    } catch (error) {
      toast.error('Erro ao registrar DAS');
    }
  };

  // Excluir DAS pago
  const handleDeleteDasPago = async (ano, mes) => {
    if (!window.confirm('Excluir este DAS pago?')) return;
    try {
      await axios.delete(`${API}/simples/das-pago/${ano}/${mes}`);
      toast.success('DAS excluído!');
      fetchSimplesData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  // Upload PDF do DAS
  const handleDasUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Selecione um arquivo PDF');
      return;
    }
    
    setDasUploadLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(`${API}/simples/upload-das`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.success && res.data.data) {
        setDasExtractedData(res.data.data);
        setDasUploadDialogOpen(false);
        setDasConfirmDialogOpen(true);
        toast.success('PDF processado! Confira os dados extraídos.');
      } else {
        toast.error('Não foi possível extrair os dados do PDF');
      }
    } catch (error) {
      console.error('Erro no upload:', error);
      toast.error(error.response?.data?.detail || 'Erro ao processar PDF');
    } finally {
      setDasUploadLoading(false);
      // Limpar input
      event.target.value = '';
    }
  };

  // Confirmar dados extraídos do DAS
  const handleConfirmDasUpload = async () => {
    if (!dasExtractedData?.mes || !dasExtractedData?.ano || !dasExtractedData?.valor_total) {
      toast.error('Dados incompletos. Preencha manualmente.');
      return;
    }
    
    try {
      const params = new URLSearchParams({
        ano: dasExtractedData.ano,
        mes: dasExtractedData.mes,
        valor_pago: dasExtractedData.valor_total,
        data_pagamento: dasExtractedData.data_vencimento || new Date().toISOString().split('T')[0],
        observacao: `Importado via PDF - Período: ${dasExtractedData.periodo_apuracao || ''}`
      });
      
      // Adicionar tributos se existirem
      if (dasExtractedData.tributos) {
        params.append('tributos', JSON.stringify(dasExtractedData.tributos));
      }
      
      // Adicionar URL do PDF se existir
      if (dasExtractedData.pdf_url) {
        params.append('pdf_url', dasExtractedData.pdf_url);
        params.append('pdf_filename', dasExtractedData.pdf_filename || 'DAS.pdf');
      }
      
      await axios.post(`${API}/simples/confirmar-das?${params.toString()}`);
      
      toast.success(`DAS de ${dasExtractedData.periodo_apuracao} importado com sucesso!`);
      setDasConfirmDialogOpen(false);
      setDasExtractedData(null);
      fetchSimplesData();
    } catch (error) {
      console.error('Erro ao confirmar DAS:', error);
      toast.error('Erro ao salvar DAS');
    }
  };

  // Executar simulação
  const handleSimulacao = async () => {
    if (!simulacaoForm.faturamento_mes) {
      toast.error('Informe o faturamento mensal para simular');
      return;
    }
    try {
      const res = await axios.post(`${API}/simples/simulacao?faturamento_mes=${parseFloat(simulacaoForm.faturamento_mes)}&meses_futuros=${simulacaoForm.meses_futuros}`);
      setSimulacaoResultado(res.data);
    } catch (error) {
      toast.error('Erro na simulação');
    }
  };

  // Abrir modal para marcar como pago
  const handleOpenPayDialog = (receivable) => {
    setReceivableToPay(receivable);
    setPayForm({ forma_pagamento: 'PIX', conta_id: '' });
    setPayDialogOpen(true);
  };

  // Confirmar pagamento
  const handleConfirmPay = async () => {
    if (!payForm.conta_id) {
      toast.error('Selecione a conta de recebimento');
      return;
    }
    
    try {
      await axios.put(`${API}/receivables/${receivableToPay.id}/pay?conta_id=${payForm.conta_id}&forma=${payForm.forma_pagamento}`);
      toast.success('Parcela marcada como paga!');
      setPayDialogOpen(false);
      setReceivableToPay(null);
      fetchData();
    } catch (error) {
      toast.error('Erro ao marcar como paga');
    }
  };

  // Excluir conta a receber
  const handleDeleteReceivable = async (receivable) => {
    if (!window.confirm(`Excluir parcela ${receivable.numero_parcela}/${receivable.total_parcelas} de ${receivable.cliente_nome}?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/receivables/${receivable.id}`);
      toast.success('Parcela excluída!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir parcela');
    }
  };

  // Gerar preview de parcelas
  const generateParcelasPreview = () => {
    if (!receivableForm.valor || !receivableForm.data_vencimento || receivableForm.num_parcelas < 1) {
      setParcelasPreview([]);
      return;
    }
    
    const valorTotal = parseFloat(receivableForm.valor);
    const valorParcela = valorTotal / receivableForm.num_parcelas;
    const dataBase = new Date(receivableForm.data_vencimento);
    
    const parcelas = [];
    for (let i = 0; i < receivableForm.num_parcelas; i++) {
      const dataVenc = new Date(dataBase);
      dataVenc.setMonth(dataVenc.getMonth() + i);
      
      parcelas.push({
        numero: i + 1,
        valor: valorParcela,
        data_vencimento: dataVenc.toISOString().split('T')[0]
      });
    }
    
    setParcelasPreview(parcelas);
  };

  // Atualizar preview quando campos mudam
  useEffect(() => {
    generateParcelasPreview();
  }, [receivableForm.valor, receivableForm.data_vencimento, receivableForm.num_parcelas]);

  // Função para busca de cliente
  const searchMatch = (text, search) => {
    if (!text || !search) return false;
    return text.toLowerCase().includes(search.toLowerCase());
  };

  // Formatar telefone
  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  // Salvar novo cliente
  const handleSaveNovoCliente = async () => {
    if (!novoCliente.nome) {
      toast.error('Nome do cliente é obrigatório');
      return;
    }
    try {
      const { cpf, ...rest } = novoCliente;
      const response = await axios.post(`${API}/customers`, { ...rest, cpf_cnpj: cpf || undefined });
      const newCustomer = response.data;
      setCustomers([...customers, newCustomer]);
      setReceivableForm({ ...receivableForm, cliente_id: newCustomer.id, cliente_nome: newCustomer.nome });
      setClienteMode('existente');
      setClienteSearch(newCustomer.nome);
      setNovoCliente({ nome: '', telefone: '', email: '', cpf: '', endereco: '', tipo: 'PF' });
      toast.success('Cliente cadastrado!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar cliente');
    }
  };

  // Criar conta a receber avulsa (com parcelas)
  const handleCreateReceivable = async (e) => {
    e.preventDefault();
    
    // Determinar nome do cliente baseado no modo
    let clienteNome = '';
    if (clienteMode === 'existente' && receivableForm.cliente_id) {
      const cliente = customers.find(c => c.id === receivableForm.cliente_id);
      clienteNome = cliente?.nome || '';
    } else if (clienteMode === 'novo' && novoCliente.nome) {
      // Salvar cliente novo primeiro
      try {
        const { cpf, ...rest } = novoCliente;
        const response = await axios.post(`${API}/customers`, { ...rest, cpf_cnpj: cpf || undefined });
        clienteNome = response.data.nome;
        setCustomers([...customers, response.data]);
      } catch (error) {
        toast.error('Erro ao cadastrar cliente');
        return;
      }
    } else if (clienteMode === 'avulso') {
      clienteNome = receivableForm.cliente_nome;
    }
    
    if (!clienteNome || !receivableForm.valor || !receivableForm.data_vencimento) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    try {
      // Se tem parcelas editadas, usar elas; senão usar o preview
      const parcelasToCreate = parcelasPreview.length > 0 ? parcelasPreview : [{
        numero: 1,
        valor: parseFloat(receivableForm.valor),
        data_vencimento: receivableForm.data_vencimento
      }];
      
      for (const parcela of parcelasToCreate) {
        await axios.post(`${API}/receivables`, {
          cliente_nome: clienteNome,
          valor: parcela.valor,
          data_vencimento: parcela.data_vencimento,
          observacao: receivableForm.observacao,
          numero_parcela: parcela.numero,
          total_parcelas: parcelasToCreate.length
        });
      }
      
      toast.success(`${parcelasToCreate.length} parcela(s) criada(s)!`);
      setReceivableDialogOpen(false);
      setReceivableForm({ cliente_nome: '', cliente_id: '', valor: '', data_vencimento: '', observacao: '', num_parcelas: 1 });
      setClienteSearch('');
      setNovoCliente({ nome: '', telefone: '', email: '', cpf: '', endereco: '', tipo: 'PF' });
      setClienteMode('existente');
      setParcelasPreview([]);
      fetchData();
    } catch (error) {
      toast.error('Erro ao criar conta a receber');
    }
  };

  // Criar pró-labore
  const handleCreateProlabore = async (e) => {
    e.preventDefault();
    if (!prolaboreForm.beneficiario || !prolaboreForm.valor || !prolaboreForm.data) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    try {
      await axios.post(`${API}/prolabores`, {
        beneficiario: prolaboreForm.beneficiario,
        valor: parseFloat(prolaboreForm.valor),
        data: prolaboreForm.data,
        conta_id: prolaboreForm.conta_id || null,
        observacao: prolaboreForm.observacao
      });
      toast.success('Pró-labore registrado!');
      setProlaboreDialogOpen(false);
      setProlaboreForm({
        beneficiario: '',
        valor: '',
        data: new Date().toISOString().split('T')[0],
        conta_id: '',
        observacao: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Erro ao registrar pró-labore');
    }
  };

  // Deletar pró-labore
  const handleDeleteProlabore = async (id) => {
    if (!window.confirm('Deseja realmente excluir este pró-labore? O saldo será revertido.')) return;
    try {
      await axios.delete(`${API}/prolabores/${id}`);
      toast.success('Pró-labore excluído!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir pró-labore');
    }
  };

  // ===================== FUNÇÕES DE CHEQUES =====================
  
  const handleCreateCheque = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/cheques`, {
        numero: chequeForm.numero,
        valor: parseFloat(chequeForm.valor),
        data_pagamento: chequeForm.data_pagamento,
        beneficiario: chequeForm.beneficiario,
        observacao: chequeForm.observacao || null,
        conta_id: chequeForm.conta_id || null
      });
      toast.success('Cheque cadastrado!');
      setChequeDialogOpen(false);
      setChequeForm({ numero: '', valor: '', data_pagamento: '', beneficiario: '', observacao: '', conta_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Erro ao cadastrar cheque');
    }
  };

  const handleCompensarCheque = async () => {
    if (!chequeToCompensar) return;
    try {
      await axios.post(`${API}/cheques/${chequeToCompensar.id}/compensar`, null, {
        params: { conta_id: compensarContaId || null }
      });
      toast.success('Cheque compensado!');
      setCompensarDialogOpen(false);
      setChequeToCompensar(null);
      setCompensarContaId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao compensar cheque');
    }
  };

  const handleDevolverCheque = async (chequeId) => {
    if (!window.confirm('Marcar este cheque como devolvido?')) return;
    try {
      await axios.post(`${API}/cheques/${chequeId}/devolver`);
      toast.success('Cheque marcado como devolvido');
      fetchData();
    } catch (error) {
      toast.error('Erro ao devolver cheque');
    }
  };

  const handleDeleteCheque = async (chequeId) => {
    if (!window.confirm('Deseja excluir este cheque?')) return;
    try {
      await axios.delete(`${API}/cheques/${chequeId}`);
      toast.success('Cheque excluído!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir cheque');
    }
  };

  // ===================== FUNÇÕES DE CONTAS A PAGAR =====================
  
  // Função para verificar se uma data está vencida (sem problemas de timezone)
  const isDateOverdue = (dateStr) => {
    if (!dateStr) return false;
    const datePart = dateStr.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    const dataVencimento = new Date(year, month - 1, day);
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    return dataVencimento < hoje;
  };

  // Função para verificar se uma data é HOJE (vence no dia)
  const isDueToday = (dateStr) => {
    if (!dateStr) return false;
    const datePart = dateStr.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    const hoje = new Date();
    return (
      year === hoje.getFullYear() &&
      month === hoje.getMonth() + 1 &&
      day === hoje.getDate()
    );
  };

  const handleBillSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/bills`, {
        ...billFormData,
        valor: parseFloat(billFormData.valor)
      });
      toast.success('Conta adicionada com sucesso!');
      setBillDialogOpen(false);
      fetchData();
      setBillFormData({ descricao: '', valor: '', dataVencimento: '', categoria: '', classificacao_dre: '' });
    } catch (error) {
      toast.error('Erro ao adicionar conta');
    }
  };

  const handleRecurringSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/recurring-bills`, {
        ...recurringFormData,
        valor: recurringFormData.tipo_valor === 'fixo' ? parseFloat(recurringFormData.valor) : null,
        dia_vencimento: parseInt(recurringFormData.dia_vencimento)
      });
      toast.success('Conta recorrente criada!');
      setRecurringDialogOpen(false);
      fetchData();
      setRecurringFormData({ descricao: '', valor: '', categoria: '', tipo_valor: 'fixo', dia_vencimento: '10', classificacao_dre: '' });
    } catch (error) {
      toast.error('Erro ao criar conta recorrente');
    }
  };

  const gerarParcelas = () => {
    const { descricao, valorTotal, numParcelas, dataPrimeiraParcela, categoria, classificacao_dre } = installmentFormData;
    if (!descricao || !valorTotal || !numParcelas || !dataPrimeiraParcela) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    const total = parseFloat(valorTotal);
    const qtdParcelas = parseInt(numParcelas);
    const valorParcela = Math.floor((total / qtdParcelas) * 100) / 100;
    const resto = Math.round((total - (valorParcela * qtdParcelas)) * 100) / 100;
    const novasParcelas = [];
    const dataBase = new Date(dataPrimeiraParcela + 'T12:00:00');
    for (let i = 0; i < qtdParcelas; i++) {
      const dataParcela = new Date(dataBase);
      dataParcela.setMonth(dataParcela.getMonth() + i);
      let valor = valorParcela;
      if (i === qtdParcelas - 1) valor = valorParcela + resto;
      novasParcelas.push({
        numero: i + 1,
        descricao: `${descricao} - ${i + 1}/${qtdParcelas}`,
        valor: Math.round(valor * 100) / 100,
        dataVencimento: dataParcela.toISOString().split('T')[0],
        categoria: categoria,
        classificacao_dre: classificacao_dre
      });
    }
    setParcelas(novasParcelas);
    setShowParcelasPreview(true);
  };

  const atualizarParcela = (index, campo, valor) => {
    const novasParcelas = [...parcelas];
    novasParcelas[index] = {
      ...novasParcelas[index],
      [campo]: campo === 'valor' ? parseFloat(valor) || 0 : valor
    };
    setParcelas(novasParcelas);
  };

  const handleInstallmentSubmit = async () => {
    if (parcelas.length === 0) {
      toast.error('Gere as parcelas primeiro');
      return;
    }
    try {
      for (const parcela of parcelas) {
        await axios.post(`${API}/bills`, {
          descricao: parcela.descricao,
          valor: parcela.valor,
          dataVencimento: parcela.dataVencimento,
          categoria: parcela.categoria,
          classificacao_dre: parcela.classificacao_dre
        });
      }
      toast.success(`${parcelas.length} parcelas criadas com sucesso!`);
      setInstallmentDialogOpen(false);
      setShowParcelasPreview(false);
      setParcelas([]);
      setInstallmentFormData({ descricao: '', valorTotal: '', numParcelas: '2', dataPrimeiraParcela: '', categoria: '', classificacao_dre: '' });
      fetchData();
    } catch (error) {
      toast.error('Erro ao criar parcelas');
    }
  };

  const handleGenerateBill = async () => {
    if (!selectedRecurring) return;
    try {
      const params = selectedRecurring.tipo_valor === 'variavel' ? `?valor=${parseFloat(generateValor)}` : '';
      await axios.post(`${API}/recurring-bills/${selectedRecurring.id}/generate${params}`);
      toast.success('Conta gerada com sucesso!');
      setGenerateDialogOpen(false);
      setSelectedRecurring(null);
      setGenerateValor('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar conta');
    }
  };

  const handleBillPay = async () => {
    if (!selectedBill) return;
    const valorFinal = selectedBill.valor || (billPayValor ? parseFloat(billPayValor) : null);
    if (!valorFinal) {
      toast.error('Informe o valor da conta');
      return;
    }
    const valorAdicional = tipoAdicionalPagamento !== 'nenhum' && valorAdicionalPagamento ? parseFloat(valorAdicionalPagamento) : 0;
    
    try {
      // Upload do comprovante se existir
      let comprovanteUrl = null;
      if (billPayComprovante) {
        setUploadingComprovante(true);
        const formData = new FormData();
        formData.append('file', billPayComprovante);
        try {
          const uploadRes = await axios.post(`${API}/bills/${selectedBill.id}/comprovante`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          comprovanteUrl = uploadRes.data.comprovante_url;
        } catch (uploadErr) {
          console.error('Erro no upload do comprovante:', uploadErr);
          toast.error('Erro ao enviar comprovante, mas continuando com o pagamento...');
        }
        setUploadingComprovante(false);
      }

      if (splitPayment) {
        const pagamentosValidos = splitPayments.filter(p => p.conta_id && p.valor);
        if (pagamentosValidos.length < 2) {
          toast.error('Informe pelo menos duas contas para pagamento dividido');
          return;
        }
        const totalSplit = pagamentosValidos.reduce((sum, p) => sum + parseFloat(p.valor || 0), 0);
        const valorComAdicional = valorFinal + valorAdicional;
        if (Math.abs(totalSplit - valorComAdicional) > 0.01) {
          toast.error(`A soma dos valores (R$ ${totalSplit.toFixed(2)}) deve ser igual ao valor total (R$ ${valorComAdicional.toFixed(2)})`);
          return;
        }
        await axios.put(`${API}/bills/${selectedBill.id}/pay-split`, {
          valor_total: !selectedBill.valor ? valorFinal : null,
          pagamentos: pagamentosValidos.map(p => ({ conta_id: p.conta_id, valor: parseFloat(p.valor) })),
          tipo_adicional: tipoAdicionalPagamento !== 'nenhum' ? tipoAdicionalPagamento : null,
          valor_adicional: valorAdicional > 0 ? valorAdicional : null,
          comprovante_url: comprovanteUrl
        });
        toast.success('Conta paga com pagamento dividido!');
      } else {
        await axios.put(`${API}/bills/${selectedBill.id}/pay`, {
          conta_id: billPayContaId || null,
          valor: !selectedBill.valor ? valorFinal : null,
          tipo_adicional: tipoAdicionalPagamento !== 'nenhum' ? tipoAdicionalPagamento : null,
          valor_adicional: valorAdicional > 0 ? valorAdicional : null,
          comprovante_url: comprovanteUrl
        });
        toast.success('Conta marcada como paga!');
      }
      if (selectedBill.recurring_bill_id) toast.info('Próxima conta gerada automaticamente!');
      setBillPayDialogOpen(false);
      setSelectedBill(null);
      setBillPayContaId('');
      setBillPayValor('');
      setBillPayComprovante(null);
      setSplitPayment(false);
      setSplitPayments([{ conta_id: '', valor: '' }, { conta_id: '', valor: '' }]);
      setTipoAdicionalPagamento('nenhum');
      setValorAdicionalPagamento('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao marcar conta como paga');
    }
  };

  const handleDeleteRecurring = async (id) => {
    if (!window.confirm('Deseja excluir esta conta recorrente?')) return;
    try {
      await axios.delete(`${API}/recurring-bills/${id}`);
      toast.success('Conta recorrente excluída!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleDeleteBill = async (id) => {
    if (!window.confirm('Deseja realmente excluir esta conta?')) return;
    try {
      await axios.delete(`${API}/bills/${id}`);
      toast.success('Conta excluída!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir conta');
    }
  };

  // Filtrar contas por mês
  const filterBillsByMonth = (billsList) => {
    if (!billFilterMonth) return billsList;
    return billsList.filter(b => {
      const billMonth = b.dataVencimento?.substring(0, 7) || b.dataPagamento?.substring(0, 7);
      return billMonth === billFilterMonth;
    });
  };

  // Cálculos de Contas a Pagar
  const pendingBills = filterBillsByMonth(pendingWithRecurring).sort((a, b) => {
    // Ordem: (1) vence hoje, (2) vencidas, (3) demais por data crescente
    const aDate = a.dataVencimento || '';
    const bDate = b.dataVencimento || '';
    const aToday = isDueToday(aDate) ? 0 : isDateOverdue(aDate) ? 1 : 2;
    const bToday = isDueToday(bDate) ? 0 : isDateOverdue(bDate) ? 1 : 2;
    if (aToday !== bToday) return aToday - bToday;
    return aDate.localeCompare(bDate);
  });
  // Ordenar contas pagas da mais recente para a mais antiga
  const paidBills = filterBillsByMonth(bills.filter(b => b.status === 'pago'))
    .sort((a, b) => {
      const dateA = a.dataPagamento || a.dataVencimento || '';
      const dateB = b.dataPagamento || b.dataVencimento || '';
      return dateB.localeCompare(dateA); // Mais recente primeiro
    });
  const totalPaidBillsPages = Math.ceil(paidBills.length / PAID_BILLS_PER_PAGE);
  const paginatedPaidBills = paidBills.slice(
    (paidBillsPage - 1) * PAID_BILLS_PER_PAGE,
    paidBillsPage * PAID_BILLS_PER_PAGE
  );
  const totalPendingBills = pendingBills.reduce((acc, b) => acc + (b.valor || 0), 0);
  const totalPaidBills = paidBills.reduce((acc, b) => acc + b.valor, 0);
  const currentMonthBills = new Date().toISOString().substring(0, 7);
  const pendingThisMonth = pendingBills.filter(bill => bill.dataVencimento?.substring(0, 7) === currentMonthBills);
  const totalPendingThisMonth = pendingThisMonth.reduce((acc, b) => acc + (b.valor || 0), 0);
  // Contas que vencem HOJE (destaque)
  const dueTodayBills = pendingBills.filter(bill => !bill.is_recurring_template && isDueToday(bill.dataVencimento));
  const totalDueToday = dueTodayBills.reduce((acc, b) => acc + (b.valor || 0), 0);
  const availableBillMonths = [...new Set(bills.map(b => (b.dataVencimento || b.dataPagamento)?.substring(0, 7)).filter(Boolean))].sort().reverse();

  // ===================== FUNÇÕES DE CONTAS BANCÁRIAS =====================
  
  const totalBalance = bankAccounts.reduce((acc, a) => acc + a.saldo, 0);
  const totalLimite = bankAccounts.reduce((acc, a) => acc + (a.limite_cheque_especial || 0), 0);
  const totalDisponivel = totalBalance + totalLimite;

  const handleBankAccountSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...bankAccountFormData,
        saldo: parseFloat(bankAccountFormData.saldo) || 0,
        limite_cheque_especial: parseFloat(bankAccountFormData.limite_cheque_especial) || 0
      };

      if (editingBankAccount) {
        await axios.put(`${API}/bank-accounts/${editingBankAccount.id}`, payload);
        toast.success('Conta atualizada!');
      } else {
        await axios.post(`${API}/bank-accounts`, payload);
        toast.success('Conta adicionada!');
      }

      setBankAccountDialogOpen(false);
      setEditingBankAccount(null);
      setBankAccountFormData({ nome: '', banco: '', saldo: '', conta_cartao: false, limite_cheque_especial: '' });
      fetchData();
    } catch (error) {
      toast.error('Erro ao salvar conta');
    }
  };

  const handleEditBankAccount = (account) => {
    setEditingBankAccount(account);
    setBankAccountFormData({
      nome: account.nome,
      banco: account.banco,
      saldo: account.saldo.toString(),
      conta_cartao: account.conta_cartao || false,
      limite_cheque_especial: (account.limite_cheque_especial || 0).toString()
    });
    setBankAccountDialogOpen(true);
  };

  const handleDeleteBankAccount = async (id) => {
    if (!window.confirm('Deseja realmente excluir esta conta?')) return;
    try {
      await axios.delete(`${API}/bank-accounts/${id}`);
      toast.success('Conta excluída!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir conta');
    }
  };

  const handleOpenExtratoConta = async (account) => {
    setExtratoAccount(account);
    setExtratoDialogOpen(true);
    setExtratoLoading(true);
    setExtratoFilter('all');
    setExtratoPeriodo('all');
    setExtratoDataInicio('');
    setExtratoDataFim('');
    
    try {
      const response = await axios.get(`${API}/bank-accounts/${account.id}/transactions`);
      setExtratoTransactionsList(response.data);
    } catch (error) {
      toast.error('Erro ao carregar extrato');
      setExtratoTransactionsList([]);
    } finally {
      setExtratoLoading(false);
    }
  };

  // Filtrar transações do extrato por tipo e período
  const filteredExtratoTransactions = extratoTransactionsList.filter(t => {
    if (extratoFilter === 'entrada' && (t.tipo === 'despesa' || t.operacao === 'saida')) return false;
    if (extratoFilter === 'saida' && t.tipo !== 'despesa' && t.operacao !== 'saida') return false;
    
    if (extratoPeriodo !== 'all' && t.data) {
      const transDate = new Date(t.data);
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      
      if (extratoPeriodo === 'custom') {
        if (extratoDataInicio && transDate < new Date(extratoDataInicio)) return false;
        if (extratoDataFim) {
          const fimDate = new Date(extratoDataFim);
          fimDate.setHours(23, 59, 59, 999);
          if (transDate > fimDate) return false;
        }
      } else {
        const dias = parseInt(extratoPeriodo);
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - dias);
        startDate.setHours(0, 0, 0, 0);
        if (transDate < startDate) return false;
      }
    }
    
    return true;
  });

  const extratoTotais = {
    entradas: filteredExtratoTransactions
      .filter(t => t.tipo !== 'despesa' && t.operacao !== 'saida')
      .reduce((acc, t) => acc + t.valor, 0),
    saidas: filteredExtratoTransactions
      .filter(t => t.tipo === 'despesa' || t.operacao === 'saida')
      .reduce((acc, t) => acc + t.valor + (t.valor_adicional || 0), 0),
    juros: filteredExtratoTransactions
      .filter(t => t.tipo_adicional === 'juros')
      .reduce((acc, t) => acc + (t.valor_adicional || 0), 0),
    tarifas: filteredExtratoTransactions
      .filter(t => t.tipo_adicional === 'tarifa')
      .reduce((acc, t) => acc + (t.valor_adicional || 0), 0)
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Calcular valor adicional (tarifa ou juros) se houver
      const valorAdicional = formData.tipo_adicional !== 'nenhum' && formData.valor_adicional 
        ? parseFloat(formData.valor_adicional) 
        : 0;

      const payload = {
        ...formData,
        valor: parseFloat(formData.valor),
        parcelas: parseInt(formData.parcelas),
        conta_id: formData.conta_id || undefined,
        categoria: formData.categoria || undefined,
        classificacao_dre: formData.classificacao_dre || undefined,
        // Campos para juros/tarifa separados
        tipo_adicional: formData.tipo_adicional !== 'nenhum' ? formData.tipo_adicional : undefined,
        valor_adicional: valorAdicional > 0 ? valorAdicional : undefined
      };

      if (formData.operacao === 'saida' || formData.tipo === 'pix' || formData.tipo === 'especie') {
        delete payload.operadora;
        delete payload.parcelas;
      }

      await axios.post(`${API}/transactions`, payload);
      toast.success('Transação adicionada com sucesso!');
      setDialogOpen(false);
      fetchData();
      setFormData({
        operacao: 'entrada',
        tipo: 'pix',
        valor: '',
        descricao: '',
        operadora: 'visa',
        parcelas: 1,
        data: new Date().toISOString().split('T')[0],
        conta_id: '',
        categoria: '',
        classificacao_dre: '',
        tipo_adicional: 'nenhum',
        valor_adicional: ''
      });
    } catch (error) {
      toast.error('Erro ao adicionar transação');
    }
  };

  const handleLoanSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/loans`, {
        ...loanFormData,
        valor: parseFloat(loanFormData.valor),
        juros: parseFloat(loanFormData.juros),
        conta_id: loanFormData.conta_id || undefined
      });
      toast.success('Empréstimo registrado! Uma conta a pagar foi criada automaticamente.');
      setLoanDialogOpen(false);
      fetchData();
      setLoanFormData({
        valor: '',
        juros: '',
        data_recebimento: new Date().toISOString().split('T')[0],
        data_pagamento: '',
        conta_id: '',
        descricao: ''
      });
    } catch (error) {
      toast.error('Erro ao registrar empréstimo');
    }
  };

  const handleDeleteLoan = async (id) => {
    if (!window.confirm('Deseja excluir este empréstimo? A transação e conta a pagar associadas também serão removidas.')) return;
    try {
      await axios.delete(`${API}/loans/${id}`);
      toast.success('Empréstimo excluído!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir empréstimo');
    }
  };

  // ========== FUNÇÕES PARA EMPRÉSTIMOS PESSOAIS (CONTA CORRENTE) ==========
  const handlePersonalLoanSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPersonalLoan) {
        await axios.put(`${API}/personal-loans/${editingPersonalLoan.id}`, {
          credor_nome: personalLoanFormData.credor_nome,
          credor_telefone: personalLoanFormData.credor_telefone,
          observacoes: personalLoanFormData.observacoes,
          taxa_juros_mensal: personalLoanFormData.taxa_juros_mensal ? parseFloat(personalLoanFormData.taxa_juros_mensal) : null,
          tipo_juros: personalLoanFormData.tipo_juros || 'composto'
        });
        toast.success('Empréstimo atualizado!');
      } else {
        await axios.post(`${API}/personal-loans`, {
          credor_nome: personalLoanFormData.credor_nome,
          credor_telefone: personalLoanFormData.credor_telefone,
          valor_inicial: parseFloat(personalLoanFormData.valor_inicial),
          data_inicio: personalLoanFormData.data_inicio,
          conta_id: personalLoanFormData.conta_id || null,
          observacoes: personalLoanFormData.observacoes,
          tipo_emprestimo: personalLoanFormData.tipo_emprestimo,
          taxa_juros_mensal: personalLoanFormData.tipo_emprestimo === 'agiota' && personalLoanFormData.taxa_juros_mensal 
            ? parseFloat(personalLoanFormData.taxa_juros_mensal) 
            : null,
          tipo_juros: personalLoanFormData.tipo_emprestimo === 'agiota' 
            ? (personalLoanFormData.tipo_juros || 'composto') 
            : 'composto'
        });
        toast.success('Empréstimo registrado!');
      }
      setPersonalLoanDialogOpen(false);
      setEditingPersonalLoan(null);
      fetchData();
      setPersonalLoanFormData({
        credor_nome: '',
        credor_telefone: '',
        valor_inicial: '',
        data_inicio: new Date().toISOString().split('T')[0],
        conta_id: '',
        observacoes: '',
        tipo_emprestimo: 'normal',
        taxa_juros_mensal: '',
        tipo_juros: 'composto'
      });
    } catch (error) {
      toast.error('Erro ao salvar empréstimo');
    }
  };

  const handleEditPersonalLoan = (loan) => {
    setEditingPersonalLoan(loan);
    setPersonalLoanFormData({
      credor_nome: loan.credor_nome,
      credor_telefone: loan.credor_telefone || '',
      valor_inicial: loan.valor_inicial,
      data_inicio: loan.data_inicio,
      observacoes: loan.observacoes || '',
      tipo_emprestimo: loan.tipo_emprestimo || 'normal',
      taxa_juros_mensal: loan.taxa_juros_mensal || '',
      tipo_juros: loan.tipo_juros || 'composto'
    });
    setPersonalLoanDialogOpen(true);
  };

  const handleAplicarJuros = async (loanId) => {
    try {
      const res = await axios.post(`${API}/personal-loans/${loanId}/aplicar-juros`);
      toast.success(`Juros de R$ ${res.data.valor_juros.toFixed(2)} aplicados!`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aplicar juros');
    }
  };

  const handlePrintLoanReport = async (loanArg = null) => {
    // Se chamado com loan (do card), busca movimentações. Caso contrário, usa o modal aberto.
    let loan = loanArg || viewingLoanMovements;
    let movs = [];
    if (loanArg) {
      try {
        const res = await axios.get(`${API}/personal-loans/${loanArg.id}/movements`);
        movs = res.data || [];
      } catch (e) {
        toast.error('Erro ao carregar movimentações do empréstimo');
        return;
      }
    } else {
      movs = loanMovements;
    }
    if (!loan) return;
    const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const fmtDate = (d) => {
      if (!d) return '-';
      const s = String(d).split('T')[0];
      const [y, m, day] = s.split('-');
      return `${day}/${m}/${y}`;
    };

    // Ordenar movimentações da mais antiga para a mais recente
    movs = [...movs].sort((a, b) => (a.data || '').localeCompare(b.data || ''));

    // Saldo running (evolução da dívida)
    let saldoRunning = 0;
    const linhas = movs.map((m) => {
      if (m.tipo === 'entrada' || m.tipo === 'juros') {
        saldoRunning += m.valor;
      } else if (m.tipo === 'pagamento') {
        saldoRunning -= m.valor;
      }
      const tipoLabel = m.tipo === 'entrada' ? 'Pegou' : m.tipo === 'juros' ? 'Juros' : 'Pagou';
      const cor = m.tipo === 'pagamento' ? '#16a34a' : m.tipo === 'juros' ? '#dc2626' : '#2563eb';
      const sinal = m.tipo === 'pagamento' ? '-' : '+';
      return `
        <tr>
          <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:12px;">${fmtDate(m.data)}</td>
          <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:12px;color:${cor};font-weight:600;">${tipoLabel}</td>
          <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#6b7280;">${(m.observacao || '').replace(/</g, '&lt;')}</td>
          <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:12px;text-align:right;color:${cor};font-weight:600;font-family:monospace;">${sinal} R$ ${fmt(m.valor)}</td>
          <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:12px;text-align:right;font-family:monospace;">R$ ${fmt(saldoRunning)}</td>
        </tr>`;
    }).join('');

    const totalJuros = loan.total_juros || 0;
    const tipoJurosLabel = loan.tipo_juros === 'simples' ? 'Simples (sobre valor bruto)' : 'Compostos (juros sobre juros)';
    const badgeJuros = loan.tipo_emprestimo === 'agiota' && loan.taxa_juros_mensal
      ? `<span style="display:inline-block;padding:2px 8px;border:1px solid #dc2626;color:#dc2626;border-radius:4px;font-size:11px;margin-left:8px;">${loan.taxa_juros_mensal}% a.m. • ${tipoJurosLabel}</span>`
      : '';

    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8" />
<title>Relatório - Empréstimo ${loan.credor_nome}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; margin: 24px; color: #111827; }
  h1 { font-size: 20px; margin: 0 0 4px 0; }
  h2 { font-size: 14px; margin: 16px 0 8px 0; color: #374151; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; }
  .header { border-bottom: 2px solid #111827; padding-bottom: 12px; margin-bottom: 16px; }
  .meta { color: #6b7280; font-size: 12px; }
  .totais { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin: 12px 0 20px; }
  .card { border: 1px solid #e5e7eb; border-radius: 6px; padding: 10px; text-align: center; }
  .card .label { font-size: 11px; color: #6b7280; text-transform: uppercase; margin-bottom: 4px; }
  .card .val { font-size: 16px; font-weight: 700; font-family: monospace; }
  .purple { color: #7c3aed; }
  .blue { color: #2563eb; }
  .green { color: #16a34a; }
  .red { color: #dc2626; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  thead th { background: #f3f4f6; padding: 8px; font-size: 12px; text-align: left; border-bottom: 2px solid #d1d5db; }
  thead th.right { text-align: right; }
  tfoot td { padding: 8px; font-size: 12px; font-weight: 700; border-top: 2px solid #111827; }
  .footer { margin-top: 24px; font-size: 11px; color: #6b7280; text-align: center; border-top: 1px solid #e5e7eb; padding-top: 12px; }
  @media print { body { margin: 12mm; } }
</style>
</head>
<body>
  <div class="header">
    <h1>Relatório de Empréstimo</h1>
    <div class="meta">
      <strong>${loan.credor_nome}</strong>${loan.credor_telefone ? ' • ' + loan.credor_telefone : ''}
      ${badgeJuros}
    </div>
    <div class="meta" style="margin-top:4px;">
      Início: ${fmtDate(loan.data_inicio)} • Status: ${loan.status === 'ativo' ? 'Ativo' : 'Quitado'}
      ${loan.observacoes ? ' • ' + String(loan.observacoes).replace(/</g, '&lt;') : ''}
    </div>
    <div class="meta" style="margin-top:4px;">Emitido em ${new Date().toLocaleString('pt-BR')}</div>
  </div>

  <div class="totais">
    <div class="card"><div class="label">Saldo Devedor</div><div class="val purple">R$ ${fmt(loan.saldo_devedor)}</div></div>
    <div class="card"><div class="label">Total Pego</div><div class="val blue">R$ ${fmt(loan.total_pego)}</div></div>
    <div class="card"><div class="label">Total Pago</div><div class="val green">R$ ${fmt(loan.total_pago)}</div></div>
    <div class="card"><div class="label">Total Juros</div><div class="val red">R$ ${fmt(totalJuros)}</div></div>
  </div>

  <h2>Movimentações (${movs.length})</h2>
  <table>
    <thead>
      <tr>
        <th style="width:90px;">Data</th>
        <th style="width:80px;">Tipo</th>
        <th>Observação</th>
        <th class="right" style="width:110px;">Valor</th>
        <th class="right" style="width:110px;">Saldo</th>
      </tr>
    </thead>
    <tbody>
      ${linhas || '<tr><td colspan="5" style="padding:12px;text-align:center;color:#6b7280;font-size:12px;">Nenhuma movimentação registrada</td></tr>'}
    </tbody>
    <tfoot>
      <tr>
        <td colspan="4" style="text-align:right;">Saldo Devedor Final:</td>
        <td style="text-align:right;font-family:monospace;">R$ ${fmt(loan.saldo_devedor)}</td>
      </tr>
    </tfoot>
  </table>

  <div class="footer">Serraria Guedes • Relatório gerado pelo sistema de gestão</div>

  <script>
    window.onload = function() { setTimeout(function() { window.print(); }, 300); };
  </script>
</body>
</html>`;

    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) {
      toast.error('Bloqueador de pop-ups impediu a impressão. Permita pop-ups e tente novamente.');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const handleDeletePersonalLoan = async (id) => {
    if (!window.confirm('Deseja excluir este empréstimo e todo seu histórico de movimentações?')) return;
    try {
      await axios.delete(`${API}/personal-loans/${id}`);
      toast.success('Empréstimo excluído!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir empréstimo');
    }
  };

  const handleOpenMovementDialog = (loan, tipo = 'pagamento') => {
    setSelectedLoanForMovement(loan);
    setMovementFormData({
      tipo: tipo,
      valor: '',
      data: new Date().toISOString().split('T')[0],
      conta_id: '',
      observacao: ''
    });
    setMovementDialogOpen(true);
  };

  const handleMovementSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/personal-loans/${selectedLoanForMovement.id}/movements`, {
        tipo: movementFormData.tipo,
        valor: parseFloat(movementFormData.valor),
        data: movementFormData.data,
        conta_id: movementFormData.conta_id || null,  // Envia conta_id para entrada E pagamento
        observacao: movementFormData.observacao
      });
      toast.success(movementFormData.tipo === 'pagamento' ? 'Pagamento registrado!' : 'Entrada registrada!');
      setMovementDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Erro ao registrar movimentação');
    }
  };

  const handleViewMovements = async (loan) => {
    try {
      const res = await axios.get(`${API}/personal-loans/${loan.id}/movements`);
      setLoanMovements(res.data);
      setViewingLoanMovements(loan);
    } catch (error) {
      toast.error('Erro ao carregar movimentações');
    }
  };

  const handleDeleteMovement = async (loanId, movementId) => {
    if (!window.confirm('Deseja excluir esta movimentação? O saldo será recalculado.')) return;
    try {
      await axios.delete(`${API}/personal-loans/${loanId}/movements/${movementId}`);
      toast.success('Movimentação excluída!');
      // Recarregar movimentações
      const res = await axios.get(`${API}/personal-loans/${loanId}/movements`);
      setLoanMovements(res.data);
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir movimentação');
    }
  };

  const getWhatsAppLink = (phone) => {
    if (!phone) return null;
    const numbers = phone.replace(/\D/g, '');
    return `https://wa.me/55${numbers}`;
  };

  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/bank-accounts/transfer`, {
        ...transferFormData,
        valor: parseFloat(transferFormData.valor)
      });
      toast.success('Transferência realizada com sucesso!');
      setTransferDialogOpen(false);
      fetchData();
      setTransferFormData({
        conta_origem_id: '',
        conta_destino_id: '',
        valor: '',
        descricao: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao realizar transferência');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir esta transação?')) return;
    try {
      await axios.delete(`${API}/transactions/${id}`);
      toast.success('Transação excluída!');
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao excluir transação');
    }
  };

  // Funções para editar transação
  const handleOpenEdit = (transaction) => {
    setEditingTransaction(transaction);
    setEditFormData({
      descricao: transaction.descricao || '',
      valor: transaction.valor?.toString() || '',
      data: transaction.data?.split('T')[0] || '',
      categoria: transaction.categoria || '',
      conta_id: transaction.conta_id || ''
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/transactions/${editingTransaction.id}`, {
        descricao: editFormData.descricao,
        valor: parseFloat(editFormData.valor),
        data: editFormData.data,
        categoria: editFormData.categoria || undefined,
        conta_id: editFormData.conta_id || undefined
      });
      toast.success('Transação atualizada!');
      setEditDialogOpen(false);
      setEditingTransaction(null);
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar transação');
    }
  };

  const totalGross = filteredTransactions.reduce((acc, t) => acc + t.valor, 0);
  const totalNet = filteredTransactions.reduce((acc, t) => acc + t.valorLiquido, 0);
  const totalFees = totalGross - totalNet;
  
  // Totais de Juros e Tarifas
  const totalJuros = filteredTransactions
    .filter(t => t.tipo_adicional === 'juros' && t.valor_adicional)
    .reduce((acc, t) => acc + (t.valor_adicional || 0), 0);
  const totalTarifas = filteredTransactions
    .filter(t => t.tipo_adicional === 'tarifa' && t.valor_adicional)
    .reduce((acc, t) => acc + (t.valor_adicional || 0), 0);
  const totalJurosTarifas = totalJuros + totalTarifas;

  // Cálculos para os cards de Extrato por mês
  const extratoTransactions = transactions.filter(t => {
    const transMonth = t.data?.substring(0, 7);
    return transMonth === extratoFilterMonth;
  });
  
  // Entradas: transações que NÃO são despesa e NÃO são saída e NÃO são transferência entre contas
  const totalEntradas = extratoTransactions
    .filter(t => t.tipo !== 'despesa' && t.operacao !== 'saida' && t.categoria !== 'Transferência')
    .reduce((acc, t) => acc + (t.valor || 0), 0);
  
  // Saídas: transações de despesa OU com operacao='saida', excluindo transferências entre contas
  const totalSaidas = extratoTransactions
    .filter(t => (t.tipo === 'despesa' || t.operacao === 'saida') && t.categoria !== 'Transferência')
    .reduce((acc, t) => acc + (t.valor || 0), 0);

  // Cálculos específicos de Cartões (Crédito e Débito) do mês
  const transacoesCartao = extratoTransactions.filter(t => 
    t.tipo === 'credito' || t.tipo === 'debito'
  );
  // Vendas = valor bruto original (antes das taxas)
  const vendasCartao = transacoesCartao.reduce((acc, t) => acc + (t.valor_bruto_original || t.valor || 0), 0);
  // Taxas = valor bruto - valor líquido
  const taxasCartao = transacoesCartao.reduce((acc, t) => {
    const bruto = t.valor_bruto_original || t.valor || 0;
    const liquido = t.valorLiquido || t.valor || 0;
    return acc + (bruto - liquido);
  }, 0);
  // Líquido = soma dos valores líquidos
  const liquidoCartao = transacoesCartao.reduce((acc, t) => acc + (t.valorLiquido || t.valor || 0), 0);
  
  // Dados do gráfico de vendas de cartão por MÊS
  const vendasCartaoPorMes = useMemo(() => {
    const porMes = {};
    transacoesCartao.forEach(t => {
      const mes = t.data?.substring(0, 7) || ''; // "YYYY-MM"
      if (!mes) return;
      if (!porMes[mes]) porMes[mes] = { credito: 0, debito: 0, total: 0, qtd_credito: 0, qtd_debito: 0 };
      const valor = t.valor_bruto_original || t.valor || 0;
      if (t.tipo === 'credito') {
        porMes[mes].credito += valor;
        porMes[mes].qtd_credito += 1;
      } else {
        porMes[mes].debito += valor;
        porMes[mes].qtd_debito += 1;
      }
      porMes[mes].total += valor;
    });
    
    return Object.entries(porMes)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-12) // Últimos 12 meses
      .map(([data, valores]) => ({
        mes: data.split('-').reverse().join('/'), // "MM/YYYY"
        credito: valores.credito,
        debito: valores.debito,
        total: valores.total,
        qtd_credito: valores.qtd_credito,
        qtd_debito: valores.qtd_debito,
        qtd_total: valores.qtd_credito + valores.qtd_debito
      }));
  }, [transacoesCartao]);
  
  // Entradas Futuras: Contas a Receber pendentes do mês + Recebimento de Cartões do mês
  const contasReceberMes = receivables
    .filter(r => r.status === 'pendente' && (r.data_vencimento?.substring(0, 7) === extratoFilterMonth || r.dataVencimento?.substring(0, 7) === extratoFilterMonth))
    .reduce((acc, r) => acc + (r.valor || 0), 0);
  
  const recebimentoCartoesMes = (futureReceivables?.futuros || [])
    .filter(f => f.dataRecebimento?.substring(0, 7) === extratoFilterMonth)
    .reduce((acc, f) => acc + (f.valorLiquido || f.valor || 0), 0);
  
  const entradasFuturas = contasReceberMes + recebimentoCartoesMes;
  const qtdEntradasFuturas = receivables.filter(r => r.status === 'pendente' && (r.data_vencimento?.substring(0, 7) === extratoFilterMonth || r.dataVencimento?.substring(0, 7) === extratoFilterMonth)).length +
    (futureReceivables?.futuros || []).filter(f => f.dataRecebimento?.substring(0, 7) === extratoFilterMonth).length;
  
  // Despesas Futuras: Contas a Pagar pendentes do mês + Cheques pendentes do mês
  const contasPagarMes = pendingWithRecurring
    .filter(b => !b.is_recurring_template && b.dataVencimento?.substring(0, 7) === extratoFilterMonth)
    .reduce((acc, b) => acc + (b.valor || 0), 0);
  
  const chequesMes = cheques
    .filter(c => c.status === 'pendente' && c.data_pagamento?.substring(0, 7) === extratoFilterMonth)
    .reduce((acc, c) => acc + (c.valor || 0), 0);
  
  const despesasFuturas = contasPagarMes + chequesMes;
  const qtdDespesasFuturas = pendingWithRecurring.filter(b => !b.is_recurring_template && b.dataVencimento?.substring(0, 7) === extratoFilterMonth).length +
    cheques.filter(c => c.status === 'pendente' && c.data_pagamento?.substring(0, 7) === extratoFilterMonth).length;
  
  // Lista de meses disponíveis (2 meses futuros + 12 meses passados)
  const availableExtratoMonths = [];
  const today = new Date();
  for (let i = -2; i <= 12; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    availableExtratoMonths.push(`${year}-${month}`);
  }
  // Ordenar do mais recente para o mais antigo
  availableExtratoMonths.sort((a, b) => b.localeCompare(a));

  // Se precisa verificar e não está verificado, mostrar tela de bloqueio
  if (needsVerification && !isVerified) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Card className="w-full max-w-md">
            <CardContent className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center">
                <Lock className="h-8 w-8 text-amber-500" />
              </div>
              <div className="text-center space-y-2">
                <h2 className="text-xl font-semibold">Acesso Protegido</h2>
                <p className="text-muted-foreground">
                  O módulo financeiro requer autenticação.
                </p>
              </div>
              <Button onClick={() => setShowModal(true)} data-testid="unlock-financeiro-btn">
                <Lock className="h-4 w-4 mr-2" />
                Desbloquear Financeiro
              </Button>
            </CardContent>
          </Card>
        </div>
        <FinanceiroAccessModal 
          open={showModal} 
          onOpenChange={setShowModal} 
          onVerify={verifyPassword} 
        />
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold font-manrope tracking-tight">Financeiro</h1>
            <p className="text-muted-foreground mt-1">Gerencie suas finanças</p>
          </div>

          {/* Alerta de Recebimentos de Cartões Prontos */}
          {futureReceivables && futureReceivables.qtd_prontos > 0 && (
            <div 
              className="flex items-center gap-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg cursor-pointer hover:bg-blue-500/20 transition-colors"
              onClick={() => setActiveTab('futuros')}
            >
              <div className="p-2 bg-blue-500/20 rounded-full">
                <DollarSign className="h-5 w-5 text-blue-500" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-blue-500">
                  {futureReceivables.qtd_prontos} recebimento(s) de cartão pronto(s) para processar
                </p>
                <p className="text-sm text-muted-foreground">
                  Total: R$ {futureReceivables.total_pronto?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} aguardando crédito na conta
                </p>
              </div>
              <Button size="sm" className="bg-blue-500 hover:bg-blue-600">
                <RefreshCw className="h-4 w-4 mr-2" />
                Processar
              </Button>
            </div>
          )}
          
          {/* Botões de ação - responsivos */}
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
            <Dialog open={loanDialogOpen} onOpenChange={setLoanDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="add-loan-button" className="text-xs sm:text-sm">
                  <Banknote className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Receber</span> Empréstimo
                </Button>
              </DialogTrigger>
              <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-[1000px] sm:pb-6 rounded-2xl sm:rounded-lg">
                <DialogHeader>
                  <DialogTitle className="font-manrope">Registrar Empréstimo</DialogTitle>
                  <DialogDescription>
                    O valor será adicionado às suas receitas e uma conta a pagar será criada para o pagamento futuro
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleLoanSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="loan-valor">Valor do Empréstimo</Label>
                    <Input
                      id="loan-valor"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={loanFormData.valor}
                      onChange={(e) => setLoanFormData({ ...loanFormData, valor: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="loan-juros">Juros (R$)</Label>
                    <div className="relative">
                      <Input
                        id="loan-juros"
                        type="number"
                        step="0.01"
                        placeholder="Ex: 150.00"
                        value={loanFormData.juros}
                        onChange={(e) => setLoanFormData({ ...loanFormData, juros: e.target.value })}
                        required
                      />
                      <DollarSign className="absolute right-3 top-3 h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>

                  {loanFormData.valor && loanFormData.juros && (
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Valor total a pagar:</p>
                      <p className="text-xl font-bold text-green-500">
                        R$ {(parseFloat(loanFormData.valor) + parseFloat(loanFormData.juros)).toFixed(2)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Principal: R$ {parseFloat(loanFormData.valor).toFixed(2)} + Juros: R$ {parseFloat(loanFormData.juros).toFixed(2)}
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="loan-data-recebimento">Data do Recebimento</Label>
                    <Input
                      id="loan-data-recebimento"
                      type="date"
                      value={loanFormData.data_recebimento}
                      onChange={(e) => setLoanFormData({ ...loanFormData, data_recebimento: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="loan-data-pagamento">Data do Pagamento</Label>
                    <Input
                      id="loan-data-pagamento"
                      type="date"
                      value={loanFormData.data_pagamento}
                      onChange={(e) => setLoanFormData({ ...loanFormData, data_pagamento: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="loan-conta">Conta de Destino</Label>
                    <Select value={loanFormData.conta_id} onValueChange={(value) => setLoanFormData({ ...loanFormData, conta_id: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a conta..." />
                      </SelectTrigger>
                      <SelectContent>
                        {bankAccounts.map(acc => (
                          <SelectItem key={acc.id} value={acc.id}>{acc.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="loan-descricao">Descrição (opcional)</Label>
                    <Input
                      id="loan-descricao"
                      placeholder="Ex: Empréstimo do Banco X"
                      value={loanFormData.descricao}
                      onChange={(e) => setLoanFormData({ ...loanFormData, descricao: e.target.value })}
                    />
                  </div>

                  <Button type="submit" className="w-full">
                    Registrar Empréstimo
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="transfer-button" className="text-xs sm:text-sm">
                  <ArrowRightLeft className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Transferir</span>
                  <span className="sm:hidden">Transfer.</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-[1000px] sm:pb-6 rounded-2xl sm:rounded-lg">
                <DialogHeader>
                  <DialogTitle className="font-manrope">Transferência entre Contas</DialogTitle>
                  <DialogDescription>
                    Transfira valores entre suas contas bancárias
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleTransferSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Conta de Origem</Label>
                    <Select 
                      value={transferFormData.conta_origem_id} 
                      onValueChange={(value) => setTransferFormData({ ...transferFormData, conta_origem_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        {bankAccounts.map(acc => (
                          <SelectItem key={acc.id} value={acc.id}>
                            {acc.nome} (R$ {acc.saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Conta de Destino</Label>
                    <Select 
                      value={transferFormData.conta_destino_id} 
                      onValueChange={(value) => setTransferFormData({ ...transferFormData, conta_destino_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        {bankAccounts.filter(acc => acc.id !== transferFormData.conta_origem_id).map(acc => (
                          <SelectItem key={acc.id} value={acc.id}>
                            {acc.nome} (R$ {acc.saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="transfer-valor">Valor</Label>
                    <Input
                      id="transfer-valor"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={transferFormData.valor}
                      onChange={(e) => setTransferFormData({ ...transferFormData, valor: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="transfer-descricao">Descrição (opcional)</Label>
                    <Input
                      id="transfer-descricao"
                      placeholder="Ex: Transferência para investimento"
                      value={transferFormData.descricao}
                      onChange={(e) => setTransferFormData({ ...transferFormData, descricao: e.target.value })}
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full"
                    disabled={!transferFormData.conta_origem_id || !transferFormData.conta_destino_id || !transferFormData.valor}
                  >
                    <ArrowRightLeft className="h-4 w-4 mr-2" />
                    Realizar Transferência
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-transaction-button" className="shadow-[0_0_10px_rgba(52,211,153,0.2)] text-xs sm:text-sm col-span-2 sm:col-span-1">
                  <Plus className="h-4 w-4 sm:mr-2" />
                  Nova Transação
                </Button>
              </DialogTrigger>
            <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-[1000px] sm:pb-6 rounded-2xl sm:rounded-lg">
              <DialogHeader>
                <DialogTitle className="font-manrope">Nova Transação</DialogTitle>
                <DialogDescription>Registre uma entrada ou saída no sistema</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 overflow-x-hidden">
                {/* Tipo de Operação: Entrada ou Saída */}
                <div className="space-y-2">
                  <Label>Tipo de Operação</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant={formData.operacao === 'entrada' ? 'default' : 'outline'}
                      className={formData.operacao === 'entrada' ? 'bg-primary' : ''}
                      onClick={() => setFormData({ ...formData, operacao: 'entrada', tipo: 'pix' })}
                    >
                      ↑ Entrada
                    </Button>
                    <Button
                      type="button"
                      variant={formData.operacao === 'saida' ? 'default' : 'outline'}
                      className={formData.operacao === 'saida' ? 'bg-destructive hover:bg-destructive/90' : ''}
                      onClick={() => setFormData({ ...formData, operacao: 'saida', tipo: 'pix' })}
                    >
                      ↓ Saída
                    </Button>
                  </div>
                </div>

                {/* Forma de Pagamento (apenas para entrada) */}
                {formData.operacao === 'entrada' && (
                  <div className="space-y-2">
                    <Label>Forma de Pagamento</Label>
                    <Select value={formData.tipo} onValueChange={(value) => setFormData({ ...formData, tipo: value })}>
                      <SelectTrigger data-testid="tipo-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pix">PIX</SelectItem>
                        <SelectItem value="especie">Espécie</SelectItem>
                        <SelectItem value="debito">Débito</SelectItem>
                        <SelectItem value="credito">Crédito</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {formData.operacao === 'entrada' && (formData.tipo === 'debito' || formData.tipo === 'credito') && (
                  <div className="space-y-2">
                    <Label>Operadora</Label>
                    <Select value={formData.operadora} onValueChange={(value) => setFormData({ ...formData, operadora: value })}>
                      <SelectTrigger data-testid="operadora-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mastercard">Mastercard</SelectItem>
                        <SelectItem value="visa">Visa</SelectItem>
                        <SelectItem value="elo">Elo</SelectItem>
                        <SelectItem value="amex">Amex</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {formData.operacao === 'entrada' && formData.tipo === 'credito' && (
                  <div className="space-y-2">
                    <Label>Parcelas</Label>
                    <Select value={formData.parcelas.toString()} onValueChange={(value) => setFormData({ ...formData, parcelas: parseInt(value) })}>
                      <SelectTrigger data-testid="parcelas-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => (
                          <SelectItem key={n} value={n.toString()}>{n}x</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Conta Bancária */}
                <div className="space-y-2">
                  <Label>Conta Bancária</Label>
                  <Select value={formData.conta_id || 'none'} onValueChange={(value) => setFormData({ ...formData, conta_id: value === 'none' ? '' : value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a conta..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Não informar</SelectItem>
                      {bankAccounts.map(acc => (
                        <SelectItem key={acc.id} value={acc.id}>
                          {acc.nome} (R$ {acc.saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {formData.operacao === 'entrada' 
                      ? 'O saldo da conta será aumentado' 
                      : 'O saldo da conta será diminuído'
                    }
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="valor">Valor</Label>
                  <Input
                    id="valor"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.valor}
                    onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                    required
                    data-testid="valor-input"
                  />
                </div>

                {/* Campo de Juros/Tarifa para Saídas */}
                {formData.operacao === 'saida' && (
                  <div className="space-y-3 p-3 bg-muted/30 rounded-lg border">
                    <div className="space-y-2">
                      <Label className="text-sm">Esta saída teve juros ou tarifa?</Label>
                      <Select 
                        value={formData.tipo_adicional} 
                        onValueChange={(value) => setFormData({ 
                          ...formData, 
                          tipo_adicional: value,
                          valor_adicional: value === 'nenhum' ? '' : formData.valor_adicional 
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="nenhum">Nenhum</SelectItem>
                          <SelectItem value="tarifa">Tarifa Bancária</SelectItem>
                          <SelectItem value="juros">Juros</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {formData.tipo_adicional !== 'nenhum' && (
                      <div className="space-y-2">
                        <Label className="text-sm">
                          Valor {formData.tipo_adicional === 'tarifa' ? 'da Tarifa' : 'dos Juros'}
                        </Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={formData.valor_adicional}
                          onChange={(e) => setFormData({ ...formData, valor_adicional: e.target.value })}
                        />
                        {formData.valor && formData.valor_adicional && (
                          <p className="text-xs text-muted-foreground">
                            Total: <span className="font-bold text-red-500">
                              R$ {(parseFloat(formData.valor) + parseFloat(formData.valor_adicional)).toFixed(2)}
                            </span>
                            <span className="text-muted-foreground ml-1">
                              (Valor: R$ {parseFloat(formData.valor).toFixed(2)} + {formData.tipo_adicional === 'tarifa' ? 'Tarifa' : 'Juros'}: R$ {parseFloat(formData.valor_adicional).toFixed(2)})
                            </span>
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="descricao">Descrição</Label>
                  <Input
                    id="descricao"
                    placeholder="Descrição da transação"
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    required
                    data-testid="descricao-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Categoria</Label>
                  <Select 
                    value={formData.categoria || 'none'} 
                    onValueChange={(value) => {
                      const cat = value === 'none' ? '' : value;
                      const autoDre = getAutoClassificacaoDRE(cat);
                      setFormData({ 
                        ...formData, 
                        categoria: cat,
                        classificacao_dre: autoDre || formData.classificacao_dre 
                      });
                    }}
                  >
                    <SelectTrigger data-testid="categoria-select">
                      <SelectValue placeholder="Selecione a categoria" />
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={4}>
                      <SelectItem value="none">Sem categoria</SelectItem>
                      {formData.operacao === 'entrada' ? (
                        <>
                          <SelectItem value="receita" className="text-green-600 font-medium">
                            💰 Receita de Vendas
                          </SelectItem>
                          <SelectItem value="receita_servicos">Receita de Serviços</SelectItem>
                          <SelectItem value="recebimento">Recebimento de Cliente</SelectItem>
                          <SelectItem value="transferencia_entrada">Transferência Recebida</SelectItem>
                          <SelectItem value="outras_receitas">Outras Receitas</SelectItem>
                        </>
                      ) : (
                        <>
                          {transactionCategories.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Classificação DRE */}
                <div className="space-y-2">
                  <Label>
                    Classificação DRE 
                    {formData.classificacao_dre && <span className="text-xs text-emerald-500 ml-1">(auto)</span>}
                  </Label>
                  <Select 
                    value={formData.classificacao_dre || 'none'} 
                    onValueChange={(value) => setFormData({ ...formData, classificacao_dre: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione para DRE..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Não classificar</SelectItem>
                      {formData.operacao === 'entrada' ? (
                        <SelectItem value="receita">Receita</SelectItem>
                      ) : (
                        <>
                          <SelectItem value="custo_variavel">Custo Variável</SelectItem>
                          <SelectItem value="custo_fixo">Custo Fixo</SelectItem>
                          <SelectItem value="despesa_operacional">Despesa Operacional</SelectItem>
                          <SelectItem value="investimento">Investimento</SelectItem>
                          <SelectItem value="pagamento_obrigacao">Pagamento de Obrigação</SelectItem>
                          <SelectItem value="estoque">Estoque (Ativo)</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Classifica para o cálculo do DRE</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="data">Data</Label>
                  <Input
                    id="data"
                    type="date"
                    value={formData.data}
                    onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                    required
                    data-testid="data-input"
                  />
                </div>

                <Button type="submit" className="w-full" data-testid="submit-transaction">
                  Adicionar
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        </div>

        {/* Tabs: Extrato, Contas a Pagar, Contas a Receber, Recebimento de Cartões, Pró-labore, Cheques */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 border-b border-border pb-3">
          <Button
            variant={activeTab === 'extrato' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('extrato')}
            className="gap-1.5 text-xs sm:text-sm justify-start sm:justify-center px-2 sm:px-3 h-9"
          >
            <Banknote className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">Extrato</span>
          </Button>
          <Button
            variant={activeTab === 'pagar' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('pagar')}
            className="gap-1.5 text-xs sm:text-sm justify-start sm:justify-center px-2 sm:px-3 h-9"
          >
            <CreditCard className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">Contas a Pagar</span>
            {pendingBills.length > 0 && (
              <span className="ml-auto sm:ml-1 px-1.5 py-0.5 text-xs rounded-full bg-red-500 text-white flex-shrink-0">
                {pendingBills.length}
              </span>
            )}
          </Button>
          <Button
            variant={activeTab === 'receber' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('receber')}
            className="gap-1.5 text-xs sm:text-sm justify-start sm:justify-center px-2 sm:px-3 h-9"
          >
            <Receipt className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">Contas a Receber</span>
            {receivablesStats && receivablesStats.qtd_pendentes > 0 && (
              <span className="ml-auto sm:ml-1 px-1.5 py-0.5 text-xs rounded-full bg-yellow-500 text-black flex-shrink-0">
                {receivablesStats.qtd_pendentes}
              </span>
            )}
          </Button>
          <Button
            variant={activeTab === 'futuros' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('futuros')}
            className="gap-1.5 text-xs sm:text-sm justify-start sm:justify-center px-2 sm:px-3 h-9"
          >
            <Calendar className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">Receb. Cartões</span>
            {futureReceivables && futureReceivables.qtd_futuros > 0 && (
              <span className="ml-auto sm:ml-1 px-1.5 py-0.5 text-xs rounded-full bg-blue-500 text-white flex-shrink-0">
                {futureReceivables.qtd_futuros}
              </span>
            )}
          </Button>
          <Button
            variant={activeTab === 'prolabore' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('prolabore')}
            className="gap-1.5 text-xs sm:text-sm justify-start sm:justify-center px-2 sm:px-3 h-9"
          >
            <DollarSign className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">Pró-labore</span>
          </Button>
          <Button
            variant={activeTab === 'cheques' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('cheques')}
            className="gap-1.5 text-xs sm:text-sm justify-start sm:justify-center px-2 sm:px-3 h-9"
          >
            <FileText className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">Cheques</span>
            {chequesStats && chequesStats.qtd_pendentes > 0 && (
              <span className="ml-auto sm:ml-1 px-1.5 py-0.5 text-xs rounded-full bg-orange-500 text-white flex-shrink-0">
                {chequesStats.qtd_pendentes}
              </span>
            )}
          </Button>
          <Button
            variant={activeTab === 'contas' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('contas')}
            className="gap-1.5 text-xs sm:text-sm justify-start sm:justify-center px-2 sm:px-3 h-9"
          >
            <Landmark className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">Contas Bancárias</span>
          </Button>
          <Button
            variant={activeTab === 'emprestimos' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('emprestimos')}
            className="gap-1.5 text-xs sm:text-sm justify-start sm:justify-center px-2 sm:px-3 h-9"
            data-testid="tab-emprestimos"
          >
            <ArrowRightLeft className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">Empréstimos</span>
            {personalLoans.filter(l => l.status === 'ativo').length > 0 && (
              <span className="ml-auto sm:ml-1 px-1.5 py-0.5 text-xs rounded-full bg-purple-500 text-white flex-shrink-0">
                {personalLoans.filter(l => l.status === 'ativo').length}
              </span>
            )}
          </Button>
          <Button
            variant={activeTab === 'simples' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('simples')}
            className="gap-1.5 text-xs sm:text-sm justify-start sm:justify-center px-2 sm:px-3 h-9"
            data-testid="tab-simples"
          >
            <Calculator className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">DAS/Simples</span>
          </Button>
        </div>

        {activeTab === 'extrato' ? (
          <>
            {/* Filtro de Mês e Summary Cards */}
            <div className="space-y-4">
              {/* Filtro de Mês */}
              <div className="flex items-center gap-4">
                <Label className="text-muted-foreground">Período:</Label>
                <Select value={extratoFilterMonth} onValueChange={setExtratoFilterMonth}>
                  <SelectTrigger className="w-auto min-w-[160px]">
                    <Calendar className="h-4 w-4 mr-2 flex-shrink-0" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableExtratoMonths.map(month => {
                      const [year, monthNum] = month.split('-');
                      const monthName = new Date(parseInt(year), parseInt(monthNum) - 1, 15).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
                      return (
                        <SelectItem key={month} value={month}>
                          {monthName}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Cards Principais */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-border bg-gradient-to-br from-green-500/10 to-transparent">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Total de Entradas</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold font-mono text-green-500">
                      R$ {totalEntradas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {extratoTransactions.filter(t => t.tipo !== 'despesa' && t.operacao !== 'saida' && t.categoria !== 'Transferência').length} transações
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-border bg-gradient-to-br from-red-500/10 to-transparent">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Total de Saídas</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold font-mono text-red-500">
                      R$ {totalSaidas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {extratoTransactions.filter(t => (t.tipo === 'despesa' || t.operacao === 'saida') && t.categoria !== 'Transferência').length} transações
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-border bg-gradient-to-br from-blue-500/10 to-transparent">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Entradas Futuras</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold font-mono text-blue-500">
                      R$ {entradasFuturas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {qtdEntradasFuturas} (a receber + cartões)
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-border bg-gradient-to-br from-orange-500/10 to-transparent">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Despesas Futuras</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold font-mono text-orange-500">
                      R$ {despesasFuturas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {qtdDespesasFuturas} (a pagar + cheques)
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Cards de Receitas, Despesas e Lucro - Movidos do Dashboard */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-border bg-gradient-to-br from-green-500/10 to-transparent">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <ArrowUpRight className="h-4 w-4 text-green-500" />
                      Receitas do Mês
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold font-mono text-green-500">
                      {formatMoney(totalEntradas, hideValues)}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Líquido: {formatMoney(totalEntradas - taxasCartao, hideValues)}
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-border bg-gradient-to-br from-red-500/10 to-transparent">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <ArrowDownRight className="h-4 w-4 text-red-500" />
                      Despesas do Mês
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold font-mono text-red-500">
                      {formatMoney(totalSaidas, hideValues)}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {extratoTransactions.filter(t => (t.tipo === 'despesa' || t.operacao === 'saida') && t.categoria !== 'Transferência').length} transações
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-border bg-gradient-to-br from-blue-500/10 to-transparent">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-blue-500" />
                      Resultado do Mês
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className={`text-2xl font-bold font-mono ${(totalEntradas - totalSaidas) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {formatMoney(totalEntradas - totalSaidas, hideValues)}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Entradas - Despesas
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

        {/* Filter */}
        <Card className="border-border">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              <CardTitle className="font-manrope">Filtros</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Campo de Pesquisa */}
            <div>
              <Label className="text-xs text-muted-foreground mb-2 block">Pesquisar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Buscar por descrição, categoria, conta ou valor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-transactions"
                />
              </div>
            </div>

            {/* Filtro por Tipo */}
            <div>
              <Label className="text-xs text-muted-foreground mb-2 block">Tipo</Label>
              <div className="flex flex-wrap gap-2">
                {['all', 'pix', 'especie', 'debito', 'credito', 'emprestimo', 'despesa'].map(type => (
                  <Button
                    key={type}
                    variant={filterType === type ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilterType(type)}
                    data-testid={`filter-${type}`}
                  >
                    {type === 'all' ? 'Todos' : type.charAt(0).toUpperCase() + type.slice(1)}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Filtro por Período */}
            <div>
              <Label className="text-xs text-muted-foreground mb-2 block">Período</Label>
              <div className="flex flex-wrap gap-2 items-center">
                <Button
                  variant={filtroPeriodo === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFiltroPeriodo('all')}
                >
                  Todos
                </Button>
                <Button
                  variant={filtroPeriodo === '7' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFiltroPeriodo('7')}
                >
                  7 dias
                </Button>
                <Button
                  variant={filtroPeriodo === '30' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFiltroPeriodo('30')}
                >
                  30 dias
                </Button>
                <Button
                  variant={filtroPeriodo === '90' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFiltroPeriodo('90')}
                >
                  90 dias
                </Button>
                <Button
                  variant={filtroPeriodo === 'custom' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFiltroPeriodo('custom')}
                >
                  <Calendar className="h-4 w-4 mr-1" />
                  Personalizado
                </Button>
              </div>
            </div>

            {/* Filtro Personalizado de Datas */}
            {filtroPeriodo === 'custom' && (
              <div className="flex gap-4 p-3 bg-muted/30 rounded-lg">
                <div className="flex-1">
                  <Label className="text-xs">Data Início</Label>
                  <Input
                    type="date"
                    value={filtroDataInicio}
                    onChange={(e) => setFiltroDataInicio(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-xs">Data Fim</Label>
                  <Input
                    type="date"
                    value={filtroDataFim}
                    onChange={(e) => setFiltroDataFim(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Transactions Table */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="font-manrope">Transações ({filteredTransactions.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Mobile: Cards Layout — agrupado por dia */}
            <div className="block sm:hidden space-y-4">
              {groupTransactionsByDay(
                filteredTransactions.slice((transCurrentPage - 1) * transItemsPerPage, transCurrentPage * transItemsPerPage)
              ).map(group => (
                <div key={group.dateKey} className="space-y-3">
                  <DayHeader group={group} />
                  {group.items.map(transaction => {
                const isDespesa = transaction.tipo === 'despesa';
                const conta = bankAccounts.find(a => a.id === transaction.conta_id);
                return (
                  <div 
                    key={transaction.id} 
                    className={`border rounded-lg p-3 space-y-2 ${isDespesa ? 'border-red-500/30 bg-red-500/5' : 'border-green-500/30 bg-green-500/5'}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-medium text-sm leading-tight">{transaction.descricao}</p>
                        <p className="text-xs text-muted-foreground mt-1">{formatDateDisplay(transaction.data)}</p>
                      </div>
                      <div className="flex gap-1 ml-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleOpenEdit(transaction)}
                        >
                          <Edit className="h-3.5 w-3.5 text-blue-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDelete(transaction.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-border/50">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded ${
                          isDespesa 
                            ? 'bg-red-500/20 text-red-500' 
                            : 'bg-green-500/20 text-green-500'
                        }`}>
                          {isDespesa ? '↓ Saída' : '↑ Entrada'}
                        </span>
                        {conta && <span className="text-xs text-muted-foreground">{conta.nome}</span>}
                      </div>
                      <div className="text-right">
                        <p className={`font-mono font-bold ${isDespesa ? 'text-red-500' : 'text-green-500'}`}>
                          {isDespesa ? '-' : '+'} R$ {(transaction.valorLiquido || transaction.valor + (transaction.valor_adicional || 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                        {(transaction.taxa_cartao > 0 || transaction.valor_adicional > 0) && (
                          <p className="text-xs text-muted-foreground">
                            {transaction.valor_adicional ? (
                              <span className="text-orange-500">
                                {transaction.tipo_adicional === 'juros' ? 'Juros' : 'Tarifa'}: R$ {transaction.valor_adicional.toFixed(2)}
                              </span>
                            ) : (
                              <span>Taxa: {transaction.taxa_cartao.toFixed(2)}%</span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
                </div>
              ))}
            </div>

            {/* Desktop: Table Layout */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full" data-testid="transactions-table">
                <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                  <tr>
                    <th className="text-left p-3">Data</th>
                    <th className="text-left p-3">Tipo</th>
                    <th className="text-left p-3">Descrição</th>
                    <th className="text-left p-3">Conta</th>
                    <th className="text-left p-3">Categoria</th>
                    <th className="text-right p-3">Valor</th>
                    <th className="text-right p-3">Juros/Tarifa</th>
                    <th className="text-right p-3">Líquido</th>
                    <th className="text-center p-3">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {groupTransactionsByDay(
                    filteredTransactions.slice((transCurrentPage - 1) * transItemsPerPage, transCurrentPage * transItemsPerPage)
                  ).map(group => (
                    <React.Fragment key={group.dateKey}>
                      <DayHeader group={group} columnsForTable={9} />
                      {group.items.map(transaction => {
                    const isDespesa = transaction.tipo === 'despesa';
                    const conta = bankAccounts.find(a => a.id === transaction.conta_id);
                    return (
                      <tr key={transaction.id} className={`hover:bg-muted/50 transition-colors ${isDespesa ? 'bg-destructive/5' : ''}`}>
                        <td className="p-3">{formatDateDisplay(transaction.data)}</td>
                        <td className="p-3">
                          <span className={`capitalize px-2 py-1 rounded text-xs font-medium ${
                            isDespesa 
                              ? 'bg-red-500/20 text-red-500' 
                              : 'bg-green-500/20 text-green-500'
                          }`}>
                            {isDespesa ? '↓ Saída' : '↑ Entrada'}
                          </span>
                        </td>
                        <td className="p-3">{transaction.descricao}</td>
                        <td className="p-3 text-sm">{conta?.nome || '-'}</td>
                        <td className="p-3 text-sm">{transaction.categoria || '-'}</td>
                        <td className={`p-3 text-right font-mono ${isDespesa ? 'text-red-500' : 'text-green-500'}`}>
                          {isDespesa ? '-' : '+'} R$ {(transaction.valor_bruto_original || transaction.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="p-3 text-right font-mono">
                          {transaction.valor_adicional ? (
                            <span className="text-orange-500">
                              {transaction.tipo_adicional === 'juros' ? 'J: ' : 'T: '}
                              R$ {transaction.valor_adicional.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          ) : transaction.taxa_cartao > 0 ? (
                            <span className="text-muted-foreground">{transaction.taxa_cartao.toFixed(2)}%</span>
                          ) : transaction.taxa > 0 ? (
                            <span className="text-muted-foreground">{transaction.taxa.toFixed(2)}%</span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className={`p-3 text-right font-mono font-bold ${isDespesa ? 'text-red-500' : 'text-green-500'}`}>
                          {isDespesa ? '-' : '+'} R$ {(transaction.valorLiquido || transaction.valor + (transaction.valor_adicional || 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenEdit(transaction)}
                              data-testid={`edit-transaction-${transaction.id}`}
                            >
                              <Edit className="h-4 w-4 text-blue-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(transaction.id)}
                              data-testid={`delete-transaction-${transaction.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Paginação da tabela de transações */}
            {Math.ceil(filteredTransactions.length / transItemsPerPage) > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t gap-2">
                <div className="text-sm text-muted-foreground">
                  {((transCurrentPage - 1) * transItemsPerPage) + 1} a {Math.min(transCurrentPage * transItemsPerPage, filteredTransactions.length)} de {filteredTransactions.length}
                </div>
                <div className="flex items-center gap-1">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setTransCurrentPage(1)}
                    disabled={transCurrentPage === 1}
                  >
                    «
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setTransCurrentPage(p => Math.max(1, p - 1))}
                    disabled={transCurrentPage === 1}
                  >
                    ‹
                  </Button>
                  
                  {Array.from({ length: Math.min(5, Math.ceil(filteredTransactions.length / transItemsPerPage)) }, (_, i) => {
                    const totalPages = Math.ceil(filteredTransactions.length / transItemsPerPage);
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (transCurrentPage <= 3) {
                      pageNum = i + 1;
                    } else if (transCurrentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = transCurrentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={transCurrentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setTransCurrentPage(pageNum)}
                        className="w-8 h-8 p-0"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setTransCurrentPage(p => Math.min(Math.ceil(filteredTransactions.length / transItemsPerPage), p + 1))}
                    disabled={transCurrentPage === Math.ceil(filteredTransactions.length / transItemsPerPage)}
                  >
                    ›
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setTransCurrentPage(Math.ceil(filteredTransactions.length / transItemsPerPage))}
                    disabled={transCurrentPage === Math.ceil(filteredTransactions.length / transItemsPerPage)}
                  >
                    »
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Empréstimos Ativos */}
        {loans.length > 0 && (
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="font-manrope flex items-center gap-2">
                <Banknote className="h-5 w-5" />
                Empréstimos ({loans.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="loans-table">
                  <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                    <tr>
                      <th className="text-left p-3">Descrição</th>
                      <th className="text-right p-3">Valor</th>
                      <th className="text-right p-3">Juros</th>
                      <th className="text-right p-3">Total a Pagar</th>
                      <th className="text-left p-3">Recebimento</th>
                      <th className="text-left p-3">Vencimento</th>
                      <th className="text-center p-3">Status</th>
                      <th className="text-center p-3">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {loans.map(loan => (
                      <tr key={loan.id} className="hover:bg-muted/50 transition-colors">
                        <td className="p-3">{loan.descricao || 'Empréstimo'}</td>
                        <td className="p-3 text-right font-mono">
                          R$ {loan.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="p-3 text-right font-mono text-yellow-500">
                          {loan.juros.toFixed(2)}%
                        </td>
                        <td className="p-3 text-right font-mono font-bold text-red-500">
                          R$ {loan.valor_total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="p-3">
                          {formatDateDisplay(loan.data_recebimento)}
                        </td>
                        <td className="p-3">
                          {formatDateDisplay(loan.data_pagamento)}
                        </td>
                        <td className="p-3 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            loan.status === 'ativo' ? 'bg-yellow-500/20 text-yellow-500' : 'bg-green-500/20 text-green-500'
                          }`}>
                            {loan.status === 'ativo' ? 'Ativo' : 'Pago'}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteLoan(loan.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
          </>
        ) : null}

        {/* ==================== CONTAS A PAGAR ==================== */}
        {activeTab === 'pagar' && (
          <div className="space-y-6">
            {/* Botões de ação - Mobile optimized */}
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:justify-end">
              <Select value={billFilterMonth || 'all'} onValueChange={(v) => setBillFilterMonth(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-full sm:w-auto sm:min-w-[140px] h-9 text-sm">
                  <Filter className="h-4 w-4 mr-1 flex-shrink-0" />
                  <SelectValue placeholder="Filtrar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {availableBillMonths.map(month => (
                    <SelectItem key={month} value={month}>
                      {new Date(month + '-01').toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" className="h-9 text-sm" onClick={() => setRecurringDialogOpen(true)}>
                <RefreshCw className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Conta Fixa</span>
                <span className="sm:hidden">Fixa</span>
              </Button>
              <Button variant="outline" className="h-9 text-sm" onClick={() => setInstallmentDialogOpen(true)}>
                <Calendar className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Parcelada</span>
                <span className="sm:hidden">Parcela</span>
              </Button>
              <Button className="h-9 text-sm" onClick={() => setBillDialogOpen(true)}>
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Nova Conta</span>
                <span className="sm:hidden">Nova</span>
              </Button>
            </div>

            {/* Banner: contas que vencem hoje */}
            {dueTodayBills.length > 0 && (
              <div
                className="rounded-lg border-2 border-amber-500 bg-gradient-to-r from-amber-500/20 via-amber-500/10 to-transparent p-4 flex items-center justify-between gap-4 shadow-sm"
                data-testid="banner-vence-hoje"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-full bg-amber-500/30 flex items-center justify-center animate-pulse shrink-0">
                    <Calendar className="h-5 w-5 text-amber-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wide">
                      Vence hoje
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {dueTodayBills.length} {dueTodayBills.length === 1 ? 'conta precisa' : 'contas precisam'} ser paga{dueTodayBills.length === 1 ? '' : 's'} até o fim do dia
                    </p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono font-bold text-xl sm:text-2xl text-amber-600">
                    R$ {totalDueToday.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            )}

            {/* Cards de resumo */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card
                className={`border-2 ${dueTodayBills.length > 0 ? 'border-amber-500 bg-gradient-to-br from-amber-500/15 to-transparent' : 'border-border bg-gradient-to-br from-muted/50 to-transparent'}`}
                data-testid="kpi-vence-hoje"
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                    {dueTodayBills.length > 0 && <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />}
                    Vence Hoje
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold font-mono ${dueTodayBills.length > 0 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                    R$ {totalDueToday.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {dueTodayBills.length} conta{dueTodayBills.length === 1 ? '' : 's'} vencendo hoje
                  </p>
                </CardContent>
              </Card>
              <Card className="border-border bg-gradient-to-br from-orange-500/10 to-transparent">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Pendente Este Mês</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold font-mono text-orange-500">
                    R$ {totalPendingThisMonth.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {pendingThisMonth.length} conta(s) em {new Date().toLocaleDateString('pt-BR', { month: 'long' })}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Pendente</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold font-mono text-red-500">
                    R$ {totalPendingBills.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{pendingBills.length} contas</p>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Pago</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold font-mono text-green-500">
                    R$ {totalPaidBills.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{paidBills.length} contas</p>
                </CardContent>
              </Card>
            </div>

            {/* Sub-tabs: Pendentes, Pagas, Contas Fixas */}
            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Button 
                  variant={!billFilterMonth ? 'secondary' : 'ghost'} 
                  size="sm"
                  onClick={() => setBillFilterMonth('')}
                >
                  Pendentes ({pendingBills.length})
                </Button>
              </div>

              {/* Tabela de Contas Pendentes */}
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="font-manrope">Contas Pendentes</CardTitle>
                </CardHeader>
                <CardContent>
                  {pendingBills.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">Nenhuma conta pendente</p>
                  ) : (
                    <>
                      {/* Desktop Table */}
                      <div className="hidden md:block">
                      <table className="w-full">
                        <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                          <tr>
                            <th className="text-left p-3">Descrição</th>
                            <th className="text-left p-3">Categoria</th>
                            <th className="text-left p-3">Vencimento</th>
                            <th className="text-right p-3">Valor</th>
                            <th className="text-center p-3">Ações</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {pendingBills.map(bill => {
                            const isOverdue = isDateOverdue(bill.dataVencimento);
                            const isToday = isDueToday(bill.dataVencimento);
                            const isProlabore = bill.categoria === 'Conta Lucas' || bill.categoria === 'Conta Alexandre';
                            const isRecurringTemplate = bill.is_recurring_template;
                            const rowHighlight = isRecurringTemplate
                              ? 'bg-blue-500/5'
                              : isToday
                                ? 'bg-amber-500/15 border-l-4 border-l-amber-500'
                                : isOverdue
                                  ? 'bg-destructive/10'
                                  : '';
                            return (
                              <tr key={bill.id} className={`hover:bg-muted/50 transition-colors ${rowHighlight}`} data-testid={isToday ? `bill-due-today-${bill.id}` : undefined}>
                                <td className="p-3 font-medium">
                                  {bill.descricao}
                                  {isToday && !isRecurringTemplate && (
                                    <Badge className="ml-2 text-xs bg-amber-500 hover:bg-amber-500 text-white animate-pulse">
                                      Vence hoje
                                    </Badge>
                                  )}
                                  {bill.recurring_bill_id && (
                                    <Badge variant="outline" className={`ml-2 text-xs ${isRecurringTemplate ? 'border-blue-500 text-blue-500' : ''}`}>
                                      <RefreshCw className="h-3 w-3 mr-1" />
                                      {isRecurringTemplate ? 'Fixa (Não gerada)' : 'Fixa'}
                                    </Badge>
                                  )}
                                </td>
                                <td className="p-3">
                                  {bill.categoria ? (
                                    <Badge variant={isProlabore ? 'default' : 'secondary'} className={isProlabore ? 'bg-purple-600' : ''}>
                                      {bill.categoria}
                                    </Badge>
                                  ) : '-'}
                                </td>
                                <td className="p-3">
                                  <span className={
                                    isToday && !isRecurringTemplate ? 'text-amber-600 font-semibold' :
                                    isOverdue && !isRecurringTemplate ? 'text-destructive font-medium' : ''
                                  }>
                                    {formatDateDisplay(bill.dataVencimento)}
                                  </span>
                                  {isOverdue && !isRecurringTemplate && !isToday && <span className="text-destructive text-xs ml-1">(Vencida)</span>}
                                </td>
                                <td className="p-3 text-right font-mono font-bold text-destructive">
                                  {bill.valor ? `R$ ${bill.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 
                                    <span className="text-muted-foreground font-normal">A definir</span>
                                  }
                                </td>
                                <td className="p-3 text-center">
                                  <div className="flex justify-center gap-2">
                                    {isRecurringTemplate ? (
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        onClick={() => {
                                          const recurring = recurringBills.find(r => r.id === bill.recurring_bill_id);
                                          if (recurring) {
                                            setSelectedRecurring(recurring);
                                            if (recurring.tipo_valor === 'variavel') {
                                              setGenerateValor('');
                                            }
                                            setGenerateDialogOpen(true);
                                          }
                                        }}
                                        title="Gerar conta do mês"
                                      >
                                        <Play className="h-4 w-4 text-blue-500" />
                                      </Button>
                                    ) : (
                                      <>
                                        <Button 
                                          variant="ghost" 
                                          size="icon" 
                                          onClick={() => {
                                            setSelectedBill(bill);
                                            setBillPayContaId('');
                                            setBillPayValor(bill.valor ? '' : '');
                                            setBillPayDialogOpen(true);
                                          }}
                                        >
                                          <Check className="h-4 w-4 text-green-500" />
                                        </Button>
                                        <Button variant="ghost" size="icon" onClick={() => handleDeleteBill(bill.id)}>
                                          <Trash2 className="h-4 w-4 text-destructive" />
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Cards */}
                    <div className="md:hidden space-y-3">
                      {pendingBills.map(bill => {
                        const isOverdue = isDateOverdue(bill.dataVencimento);
                        const isToday = isDueToday(bill.dataVencimento);
                        const isProlabore = bill.categoria === 'Conta Lucas' || bill.categoria === 'Conta Alexandre';
                        const isRecurringTemplate = bill.is_recurring_template;
                        const cardCls = isRecurringTemplate
                          ? 'bg-blue-500/5 border-blue-500/30'
                          : isToday
                            ? 'border-2 border-amber-500 bg-amber-500/15'
                            : isOverdue
                              ? 'border-destructive bg-destructive/10'
                              : 'border-border';
                        return (
                          <div key={bill.id} className={`p-3 rounded-lg border ${cardCls}`} data-testid={isToday ? `bill-due-today-mobile-${bill.id}` : undefined}>
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{bill.descricao}</p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {isToday && !isRecurringTemplate && (
                                    <Badge className="text-xs bg-amber-500 hover:bg-amber-500 text-white animate-pulse">
                                      Vence hoje
                                    </Badge>
                                  )}
                                  {bill.recurring_bill_id && (
                                    <Badge variant="outline" className={`text-xs ${isRecurringTemplate ? 'border-blue-500 text-blue-500' : ''}`}>
                                      <RefreshCw className="h-3 w-3 mr-1" />
                                      {isRecurringTemplate ? 'Fixa' : 'Fixa'}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <span className="font-mono font-bold text-destructive text-sm ml-2">
                                {bill.valor ? `R$ ${bill.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : 'A definir'}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                {bill.categoria && (
                                  <Badge variant={isProlabore ? 'default' : 'secondary'} className={`text-xs ${isProlabore ? 'bg-purple-600' : ''}`}>
                                    {bill.categoria}
                                  </Badge>
                                )}
                                <span className={
                                  isToday && !isRecurringTemplate ? 'text-amber-600 font-semibold' :
                                  isOverdue && !isRecurringTemplate ? 'text-destructive font-medium' : ''
                                }>
                                  {formatDateDisplay(bill.dataVencimento)}
                                  {isOverdue && !isRecurringTemplate && !isToday && ' (Vencida)'}
                                </span>
                              </div>
                              <div className="flex gap-1">
                                {isRecurringTemplate ? (
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => {
                                    const recurring = recurringBills.find(r => r.id === bill.recurring_bill_id);
                                    if (recurring) {
                                      setSelectedRecurring(recurring);
                                      if (recurring.tipo_valor === 'variavel') setGenerateValor('');
                                      setGenerateDialogOpen(true);
                                    }
                                  }}>
                                    <Play className="h-4 w-4 text-blue-500" />
                                  </Button>
                                ) : (
                                  <>
                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => {
                                      setSelectedBill(bill);
                                      setBillPayContaId('');
                                      setBillPayValor(bill.valor ? '' : '');
                                      setBillPayDialogOpen(true);
                                    }}>
                                      <Check className="h-4 w-4 text-green-500" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDeleteBill(bill.id)}>
                                      <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Tabela de Contas Pagas */}
              {paidBills.length > 0 && (
                <Card className="border-border">
                  <CardHeader>
                    <CardTitle className="font-manrope">Contas Pagas ({paidBills.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                          <tr>
                            <th className="text-left p-3">Descrição</th>
                            <th className="text-left p-3">Categoria</th>
                            <th className="text-left p-3">Pago em</th>
                            <th className="text-right p-3">Valor</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {paginatedPaidBills.map(bill => (
                            <tr key={bill.id} className="hover:bg-muted/50 transition-colors bg-green-500/5">
                              <td className="p-3 font-medium">{bill.descricao}</td>
                              <td className="p-3">
                                {bill.categoria ? <Badge variant="secondary">{bill.categoria}</Badge> : '-'}
                              </td>
                              <td className="p-3">{formatDateDisplay(bill.dataPagamento)}</td>
                              <td className="p-3 text-right font-mono text-green-500">
                                R$ {bill.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Paginação */}
                    {totalPaidBillsPages > 1 && (
                      <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPaidBillsPage(1)}
                          disabled={paidBillsPage === 1}
                        >
                          «
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPaidBillsPage(p => Math.max(1, p - 1))}
                          disabled={paidBillsPage === 1}
                        >
                          ‹
                        </Button>
                        
                        {/* Números das páginas */}
                        {Array.from({ length: totalPaidBillsPages }, (_, i) => i + 1)
                          .filter(page => {
                            // Mostrar: primeira, última, atual, e 2 ao redor da atual
                            return page === 1 || 
                                   page === totalPaidBillsPages || 
                                   Math.abs(page - paidBillsPage) <= 1;
                          })
                          .map((page, index, arr) => (
                            <span key={page} className="flex items-center">
                              {index > 0 && arr[index - 1] !== page - 1 && (
                                <span className="px-2 text-muted-foreground">...</span>
                              )}
                              <Button
                                variant={paidBillsPage === page ? "default" : "outline"}
                                size="sm"
                                onClick={() => setPaidBillsPage(page)}
                                className="min-w-[36px]"
                              >
                                {page}
                              </Button>
                            </span>
                          ))
                        }
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPaidBillsPage(p => Math.min(totalPaidBillsPages, p + 1))}
                          disabled={paidBillsPage === totalPaidBillsPages}
                        >
                          ›
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPaidBillsPage(totalPaidBillsPages)}
                          disabled={paidBillsPage === totalPaidBillsPages}
                        >
                          »
                        </Button>
                        
                        <span className="text-sm text-muted-foreground ml-2">
                          Página {paidBillsPage} de {totalPaidBillsPages}
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Contas Fixas */}
              {recurringBills.length > 0 && (
                <Card className="border-border">
                  <CardHeader>
                    <CardTitle className="font-manrope flex items-center gap-2">
                      <RefreshCw className="h-5 w-5" />
                      Contas Fixas Recorrentes ({recurringBills.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {/* Mobile: Cards */}
                    <div className="md:hidden space-y-3">
                      {recurringBills.map(recurring => (
                        <div key={recurring.id} className="p-3 border rounded-lg bg-muted/20">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium">{recurring.descricao}</p>
                              <p className="text-xs text-muted-foreground">{recurring.categoria || 'Sem categoria'}</p>
                            </div>
                            <Badge variant={recurring.ativo ? 'default' : 'secondary'} className={recurring.ativo ? 'bg-green-600' : ''}>
                              {recurring.ativo ? 'Ativa' : 'Inativa'}
                            </Badge>
                          </div>
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="text-xs text-muted-foreground">Dia {recurring.dia_vencimento}</span>
                              <span className="mx-2">•</span>
                              <span className="font-mono font-bold">
                                {recurring.tipo_valor === 'fixo' 
                                  ? `R$ ${recurring.valor?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                  : 'Variável'
                                }
                              </span>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setSelectedRecurring(recurring); if (recurring.tipo_valor === 'variavel') setGenerateValor(''); setGenerateDialogOpen(true); }}>
                                <Play className="h-4 w-4 text-blue-500" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDeleteRecurring(recurring.id)}>
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Desktop: Tabela */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                          <tr>
                            <th className="text-left p-3">Descrição</th>
                            <th className="text-left p-3">Categoria</th>
                            <th className="text-center p-3">Dia Venc.</th>
                            <th className="text-right p-3">Valor</th>
                            <th className="text-center p-3">Status</th>
                            <th className="text-center p-3">Ações</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {recurringBills.map(recurring => (
                            <tr key={recurring.id} className="hover:bg-muted/50 transition-colors">
                              <td className="p-3 font-medium">{recurring.descricao}</td>
                              <td className="p-3">
                                {recurring.categoria ? <Badge variant="secondary">{recurring.categoria}</Badge> : '-'}
                              </td>
                              <td className="p-3 text-center">Dia {recurring.dia_vencimento}</td>
                              <td className="p-3 text-right font-mono">
                                {recurring.tipo_valor === 'fixo' 
                                  ? `R$ ${recurring.valor?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                  : <span className="text-muted-foreground">Variável</span>
                                }
                              </td>
                              <td className="p-3 text-center">
                                <Badge variant={recurring.ativo ? 'default' : 'secondary'} className={recurring.ativo ? 'bg-green-600' : ''}>
                                  {recurring.ativo ? 'Ativa' : 'Inativa'}
                                </Badge>
                              </td>
                              <td className="p-3 text-center">
                                <div className="flex justify-center gap-2">
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    onClick={() => {
                                      setSelectedRecurring(recurring);
                                      if (recurring.tipo_valor === 'variavel') setGenerateValor('');
                                      setGenerateDialogOpen(true);
                                    }}
                                    title="Gerar conta do mês"
                                  >
                                    <Play className="h-4 w-4 text-blue-500" />
                                  </Button>
                                  <Button variant="ghost" size="icon" onClick={() => handleDeleteRecurring(recurring.id)}>
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {activeTab === 'receber' && (
          /* ==================== CONTAS A RECEBER ==================== */
          <div className="space-y-6">
            {/* Stats Cards */}
            {receivablesStats && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="border-border border-yellow-500/50 bg-yellow-500/5">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-yellow-500 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      A Receber (Pendentes)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold font-mono text-yellow-500">
                      R$ {(receivablesStats.total_pendente || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {receivablesStats.qtd_pendentes} parcelas pendentes
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-border border-green-500/50 bg-green-500/5">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-green-600 flex items-center gap-2">
                      <Check className="h-4 w-4" />
                      Já Recebido
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold font-mono text-green-500">
                      R$ {(receivablesStats.total_pago || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {receivablesStats.qtd_pagas} parcelas recebidas
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">Total Geral</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold font-mono">
                      R$ {((receivablesStats.total_pendente || 0) + (receivablesStats.total_pago || 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {(receivablesStats.qtd_pendentes || 0) + (receivablesStats.qtd_pagas || 0)} parcelas
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Lista de Contas a Receber */}
            <Card className="border-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="h-5 w-5" />
                  Contas a Receber
                </CardTitle>
                <Button size="sm" onClick={() => setReceivableDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Nova Conta
                </Button>
              </CardHeader>
              <CardContent>
                {receivables.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhuma parcela cadastrada</p>
                    <p className="text-sm">As parcelas são criadas automaticamente ao gerar um pedido parcelado</p>
                  </div>
                ) : (
                  <>
                    {/* Desktop Table */}
                    <div className="hidden md:block">
                      <table className="w-full">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-3">Cliente</th>
                            <th className="text-left p-3">Pedido</th>
                            <th className="text-center p-3">Parcela</th>
                            <th className="text-right p-3">Valor</th>
                            <th className="text-left p-3">Vencimento</th>
                            <th className="text-center p-3">Status</th>
                            <th className="text-center p-3">Ações</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {receivables.filter(r => r.status === 'pendente').map(rec => {
                            const isVencida = rec.status === 'pendente' && new Date(rec.data_vencimento) < new Date();
                            return (
                              <tr key={rec.id} className={`hover:bg-muted/50 transition-colors ${isVencida ? 'bg-red-500/5' : ''}`}>
                                <td className="p-3 font-medium">{rec.cliente_nome}</td>
                                <td className="p-3">
                                  <span className="text-sm text-muted-foreground">{rec.pedido_numero || rec.pedido_id?.slice(0, 8)}</span>
                                </td>
                                <td className="p-3 text-center">
                                  <span className="px-2 py-1 rounded bg-muted text-sm">
                                    {rec.numero_parcela}/{rec.total_parcelas}
                                  </span>
                                </td>
                                <td className="p-3 text-right font-mono font-bold text-green-500">
                                  R$ {rec.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className={`p-3 ${isVencida ? 'text-red-500 font-medium' : ''}`}>
                                  {formatDateDisplay(rec.data_vencimento)}
                                  {isVencida && <span className="ml-2 text-xs">(Vencida)</span>}
                                </td>
                                <td className="p-3 text-center">
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    rec.status === 'pendente' 
                                      ? isVencida ? 'bg-red-500/20 text-red-500' : 'bg-yellow-500/20 text-yellow-500'
                                      : 'bg-green-500/20 text-green-500'
                                  }`}>
                                    {rec.status === 'pendente' ? (isVencida ? 'Vencida' : 'Pendente') : 'Pago'}
                                  </span>
                                </td>
                                <td className="p-3 text-center">
                                  <div className="flex justify-center gap-1">
                                    {rec.status === 'pendente' && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleOpenPayDialog(rec)}
                                        className="gap-1"
                                      >
                                        <Check className="h-3 w-3" />
                                        Receber
                                      </Button>
                                    )}
                                    {rec.status === 'pago' && (
                                      <span className="text-xs text-muted-foreground">
                                        {rec.data_pagamento && formatDateDisplay(rec.data_pagamento)}
                                      </span>
                                    )}
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleDeleteReceivable(rec)}
                                      className="h-8 w-8"
                                    >
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Cards */}
                    <div className="md:hidden space-y-3">
                      {receivables.map(rec => {
                        const isVencida = rec.status === 'pendente' && new Date(rec.data_vencimento) < new Date();
                        return (
                          <div key={rec.id} className={`p-3 rounded-lg border ${isVencida ? 'border-red-500 bg-red-500/5' : 'border-border'}`}>
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{rec.cliente_nome}</p>
                                <p className="text-xs text-muted-foreground">{rec.pedido_numero || rec.pedido_id?.slice(0, 8)}</p>
                              </div>
                              <div className="text-right ml-2">
                                <span className="font-mono font-bold text-green-500 text-sm">
                                  R$ {rec.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </span>
                                <span className="block px-2 py-0.5 rounded bg-muted text-xs mt-1">
                                  {rec.numero_parcela}/{rec.total_parcelas}
                                </span>
                              </div>
                            </div>
                            <div className="flex justify-between items-center">
                              <div className="flex items-center gap-2 text-xs">
                                <span className={`px-2 py-0.5 rounded font-medium ${
                                  rec.status === 'pendente' 
                                    ? isVencida ? 'bg-red-500/20 text-red-500' : 'bg-yellow-500/20 text-yellow-500'
                                    : 'bg-green-500/20 text-green-500'
                                }`}>
                                  {rec.status === 'pendente' ? (isVencida ? 'Vencida' : 'Pendente') : 'Pago'}
                                </span>
                                <span className={isVencida ? 'text-red-500' : 'text-muted-foreground'}>
                                  {formatDateDisplay(rec.data_vencimento)}
                                </span>
                              </div>
                              <div className="flex gap-1">
                                {rec.status === 'pendente' && (
                                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleOpenPayDialog(rec)}>
                                    <Check className="h-3 w-3 mr-1" />
                                    Receber
                                  </Button>
                                )}
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteReceivable(rec)}>
                                  <Trash2 className="h-3 w-3 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Tabela de Valores Já Recebidos */}
            {receivables.filter(r => r.status === 'pago').length > 0 && (
              <Card className="border-border border-green-500/30">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-green-600">
                    <Check className="h-5 w-5" />
                    Valores Já Recebidos ({receivables.filter(r => r.status === 'pago').length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Mobile: Cards */}
                  <div className="md:hidden space-y-2 max-h-[400px] overflow-y-auto">
                    {receivables
                      .filter(r => r.status === 'pago')
                      .sort((a, b) => new Date(b.data_pagamento) - new Date(a.data_pagamento))
                      .slice(0, 50)
                      .map(rec => (
                        <div key={rec.id} className="p-3 border rounded-lg bg-green-500/5">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-sm">{rec.cliente_nome}</p>
                              <p className="text-xs text-muted-foreground">{rec.pedido_numero || rec.pedido_id?.slice(0, 8)} • {rec.numero_parcela}/{rec.total_parcelas}</p>
                            </div>
                            <p className="font-mono font-bold text-green-500">R$ {rec.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                          </div>
                          <div className="flex justify-between text-xs text-muted-foreground mt-1">
                            <span>Venc: {formatDateDisplay(rec.data_vencimento)}</span>
                            <span className="text-green-600">Pago: {rec.data_pagamento && formatDateDisplay(rec.data_pagamento)}</span>
                          </div>
                        </div>
                      ))}
                  </div>
                  
                  {/* Desktop: Tabela */}
                  <div className="hidden md:block overflow-x-auto max-h-[400px]">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-background">
                        <tr className="border-b">
                          <th className="text-left py-2 px-3 font-medium">Cliente</th>
                          <th className="text-left py-2 px-3 font-medium">Pedido</th>
                          <th className="text-center py-2 px-3 font-medium">Parcela</th>
                          <th className="text-right py-2 px-3 font-medium">Valor</th>
                          <th className="text-center py-2 px-3 font-medium">Vencimento</th>
                          <th className="text-center py-2 px-3 font-medium">Pago em</th>
                        </tr>
                      </thead>
                      <tbody>
                        {receivables
                          .filter(r => r.status === 'pago')
                          .sort((a, b) => new Date(b.data_pagamento) - new Date(a.data_pagamento))
                          .slice(0, 50)
                          .map(rec => (
                            <tr key={rec.id} className="border-b border-border/50 hover:bg-muted/50">
                              <td className="py-2 px-3">{rec.cliente_nome}</td>
                              <td className="py-2 px-3 text-muted-foreground">{rec.pedido_numero || rec.pedido_id?.slice(0, 8)}</td>
                              <td className="py-2 px-3 text-center">
                                <span className="px-2 py-0.5 bg-muted rounded text-xs">
                                  {rec.numero_parcela}/{rec.total_parcelas}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-right font-mono font-bold text-green-500">
                                R$ {rec.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </td>
                              <td className="py-2 px-3 text-center text-muted-foreground">
                                {formatDateDisplay(rec.data_vencimento)}
                              </td>
                              <td className="py-2 px-3 text-center text-green-600 font-medium">
                                {rec.data_pagamento && formatDateDisplay(rec.data_pagamento)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Aba Recebimento de Cartões */}
        {activeTab === 'futuros' && (
          <div className="space-y-4">
            {/* Cards de Status - Prontos e Futuros */}
            {futureReceivables && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-border border-yellow-500/30 bg-yellow-500/5">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-yellow-500 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Prontos para Receber
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold font-mono text-yellow-500">
                      R$ {(futureReceivables.total_pronto || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {futureReceivables.qtd_prontos || 0} aguardando processamento
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-border border-blue-500/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-blue-500 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      A Receber (Futuros)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold font-mono text-blue-500">
                      R$ {(futureReceivables.total_futuro || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {futureReceivables.qtd_futuros} transações pendentes
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Cards de Resumo - Vendas, Taxas e Líquido */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Vendas no Cartão</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold font-mono text-foreground">
                    {formatMoney(vendasCartao, hideValues)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {transacoesCartao.length} vendas (crédito + débito)
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Taxas de Cartão</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold font-mono text-red-500">
                    - {formatMoney(taxasCartao, hideValues)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {vendasCartao > 0 ? ((taxasCartao / vendasCartao) * 100).toFixed(2) : '0.00'}% do total
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Líquido Cartão</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold font-mono text-green-500">
                    {formatMoney(liquidoCartao, hideValues)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Valor após taxas
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Gráfico de Vendas de Cartão por Mês */}
            {vendasCartaoPorMes.length > 0 && (
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Vendas de Cartão por Mês (Quantidade)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={vendasCartaoPorMes} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorCredito" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="colorDebito" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                        <XAxis 
                          dataKey="mes" 
                          tick={{ fill: '#9ca3af', fontSize: 11 }}
                          tickLine={false}
                          axisLine={{ stroke: '#374151' }}
                        />
                        <YAxis 
                          tick={{ fill: '#9ca3af', fontSize: 11 }}
                          tickLine={false}
                          axisLine={{ stroke: '#374151' }}
                          tickFormatter={(value) => `${value}`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1f2937', 
                            border: '1px solid #374151',
                            borderRadius: '8px',
                            color: '#fff'
                          }}
                          formatter={(value, name) => [value, name === 'qtd_credito' ? 'Crédito' : 'Débito']}
                          labelFormatter={(label) => `Mês: ${label}`}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="qtd_credito" 
                          name="Crédito"
                          stroke="#3b82f6" 
                          fillOpacity={1} 
                          fill="url(#colorCredito)" 
                        />
                        <Area 
                          type="monotone" 
                          dataKey="qtd_debito" 
                          name="Débito"
                          stroke="#22c55e" 
                          fillOpacity={1} 
                          fill="url(#colorDebito)" 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex justify-center gap-6 mt-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500" />
                      <span className="text-xs text-muted-foreground">Crédito (qtd)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                      <span className="text-xs text-muted-foreground">Débito (qtd)</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Seção de Recebimentos Prontos para Processar */}
            {futureReceivables && futureReceivables.prontos_para_receber && futureReceivables.prontos_para_receber.length > 0 && (
              <Card className="border-border border-yellow-500/50">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-yellow-500">
                        <RefreshCw className="h-5 w-5" />
                        Prontos para Creditar na Conta
                      </CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        Data de recebimento já passou - clique para creditar na conta bancária
                      </p>
                    </div>
                    <Button
                      onClick={async () => {
                        try {
                          const res = await axios.post(`${API}/transactions/process-receivables`);
                          toast.success(`${res.data.transacoes_processadas.length} recebimento(s) creditado(s)!`);
                          fetchData();
                        } catch (error) {
                          toast.error('Erro ao processar recebimentos');
                        }
                      }}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Processar Todos ({futureReceivables.prontos_para_receber.length})
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-yellow-500/10">
                        <tr>
                          <th className="text-left p-3">Descrição</th>
                          <th className="text-center p-3">Tipo</th>
                          <th className="text-right p-3">Valor Líquido</th>
                          <th className="text-left p-3">Data Recebimento</th>
                          <th className="text-center p-3 w-16">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {futureReceivables.prontos_para_receber.map(trans => (
                          <tr key={trans.id} className="hover:bg-yellow-500/10">
                            <td className="p-3 font-medium">{trans.descricao}</td>
                            <td className="p-3 text-center">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                trans.tipo === 'credito' ? 'bg-purple-500/20 text-purple-500' : 'bg-blue-500/20 text-blue-500'
                              }`}>
                                {trans.tipo === 'credito' ? 'Crédito' : 'Débito'}
                              </span>
                            </td>
                            <td className="p-3 text-right font-mono font-bold text-green-500">
                              R$ {trans.valorLiquido.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="p-3 text-muted-foreground">
                              {formatDateDisplay(trans.dataRecebimento)}
                            </td>
                            <td className="p-3 text-center">
                              {trans.order_id ? (
                                <span
                                  className="text-[10px] text-muted-foreground"
                                  title="Transação gerada por pedido — exclua pelo pedido"
                                >
                                  pedido
                                </span>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDelete(trans.id)}
                                  data-testid={`delete-pronto-${trans.id}`}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tabela de Recebimento de Cartões */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-500" />
                  Recebimento de Cartões
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Todos os cartões: D+1 (1 dia útil)
                </p>
              </CardHeader>
              <CardContent>
                {!futureReceivables || futureReceivables.futuros.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhum recebimento futuro pendente</p>
                    <p className="text-sm">Pagamentos de cartão aparecerão aqui antes de entrar na conta</p>
                  </div>
                ) : (
                  <>
                    {/* Desktop Table */}
                    <div className="hidden md:block">
                      <table className="w-full">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-3">Descrição</th>
                            <th className="text-center p-3">Tipo</th>
                            <th className="text-center p-3">Bandeira</th>
                            <th className="text-right p-3">Valor Bruto</th>
                            <th className="text-right p-3">Valor Líquido</th>
                            <th className="text-center p-3">Taxa</th>
                            <th className="text-left p-3">Data Recebimento</th>
                            <th className="text-center p-3">Status</th>
                            <th className="text-center p-3 w-16">Ações</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {futureReceivables.futuros.map(trans => (
                            <tr key={trans.id} className="hover:bg-muted/50 transition-colors">
                              <td className="p-3 font-medium">{trans.descricao}</td>
                              <td className="p-3 text-center">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  trans.tipo === 'credito' ? 'bg-purple-500/20 text-purple-500' : 'bg-blue-500/20 text-blue-500'
                                }`}>
                                  {trans.tipo === 'credito' ? 'Crédito' : 'Débito'}
                                </span>
                              </td>
                              <td className="p-3 text-center uppercase text-sm">
                                {trans.operadora || '-'}
                              </td>
                              <td className="p-3 text-right font-mono">
                                R$ {trans.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="p-3 text-right font-mono font-bold text-green-500">
                                R$ {trans.valorLiquido.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="p-3 text-center text-red-500 text-sm">
                                {trans.taxa ? `${trans.taxa}%` : '-'}
                              </td>
                              <td className="p-3">
                                {formatDateDisplay(trans.dataRecebimento)}
                                {trans.dias_para_receber > 0 && (
                                  <span className="ml-2 text-xs text-blue-500">
                                    (em {trans.dias_para_receber} dia{trans.dias_para_receber > 1 ? 's' : ''})
                                  </span>
                                )}
                              </td>
                              <td className="p-3 text-center">
                                <span className="px-2 py-1 rounded text-xs font-medium bg-yellow-500/20 text-yellow-500">
                                  Pendente
                                </span>
                              </td>
                              <td className="p-3 text-center">
                                {trans.order_id ? (
                                  <span
                                    className="text-[10px] text-muted-foreground"
                                    title="Transação gerada por pedido — exclua pelo pedido"
                                  >
                                    pedido
                                  </span>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleDelete(trans.id)}
                                    data-testid={`delete-futuro-${trans.id}`}
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Cards */}
                    <div className="md:hidden space-y-3">
                      {futureReceivables.futuros.map(trans => (
                        <div key={trans.id} className="p-3 rounded-lg border border-border">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{trans.descricao}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  trans.tipo === 'credito' ? 'bg-purple-500/20 text-purple-500' : 'bg-blue-500/20 text-blue-500'
                                }`}>
                                  {trans.tipo === 'credito' ? 'Crédito' : 'Débito'}
                                </span>
                                <span className="text-xs uppercase text-muted-foreground">{trans.operadora || '-'}</span>
                              </div>
                            </div>
                            <div className="text-right ml-2 flex items-start gap-1">
                              <div>
                                <span className="font-mono font-bold text-green-500 text-sm">
                                  R$ {trans.valorLiquido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </span>
                                {trans.taxa && (
                                  <span className="block text-xs text-red-500">Taxa: {trans.taxa}%</span>
                                )}
                              </div>
                              {!trans.order_id && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => handleDelete(trans.id)}
                                  data-testid={`delete-futuro-mobile-${trans.id}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                </Button>
                              )}
                            </div>
                          </div>
                          <div className="flex justify-between items-center text-xs text-muted-foreground">
                            <span>
                              {formatDateDisplay(trans.dataRecebimento)}
                              {trans.dias_para_receber > 0 && (
                                <span className="ml-1 text-blue-500">
                                  ({trans.dias_para_receber}d)
                                </span>
                              )}
                            </span>
                            <span className="px-2 py-0.5 rounded font-medium bg-yellow-500/20 text-yellow-500">
                              {trans.order_id ? 'Pedido' : 'Pendente'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Aba Pró-labore */}
        {activeTab === 'prolabore' && (
          <div className="space-y-4">
            {/* Header com botão */}
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Pagamentos de Pró-labore</h2>
              <Button onClick={() => setProlaboreDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Novo Pró-labore
              </Button>
            </div>

            {/* Cards de resumo */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Pró-labore (Geral)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold font-mono text-foreground">
                    R$ {prolabores.reduce((acc, p) => acc + p.valor, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Este Mês</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold font-mono text-green-500">
                    R$ {prolabores
                      .filter(p => {
                        const dataP = new Date(p.data);
                        const now = new Date();
                        return dataP.getMonth() === now.getMonth() && dataP.getFullYear() === now.getFullYear();
                      })
                      .reduce((acc, p) => acc + p.valor, 0)
                      .toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Gráfico de Linha - Pró-labore por Mês */}
            {prolabores.length > 0 && (
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Evolução do Pró-labore por Mês</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={(() => {
                          // Agrupar pró-labores por mês
                          const porMes = {};
                          prolabores.forEach(p => {
                            const data = new Date(p.data);
                            const key = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
                            if (!porMes[key]) {
                              porMes[key] = { mes: key, valor: 0 };
                            }
                            porMes[key].valor += p.valor;
                          });
                          // Ordenar por mês e formatar
                          return Object.values(porMes)
                            .sort((a, b) => a.mes.localeCompare(b.mes))
                            .map(item => ({
                              ...item,
                              mesFormatado: new Date(item.mes + '-01').toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })
                            }))
                            .slice(-12); // Últimos 12 meses
                        })()}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="colorProlabore" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="mesFormatado" 
                          axisLine={false} 
                          tickLine={false}
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                        />
                        <YAxis 
                          axisLine={false} 
                          tickLine={false}
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                          tickFormatter={(v) => `R$ ${(v/1000).toFixed(0)}k`}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => [`R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, 'Pró-labore']}
                          labelFormatter={(label) => `Mês: ${label}`}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="valor" 
                          stroke="#ef4444" 
                          strokeWidth={2}
                          fillOpacity={1} 
                          fill="url(#colorProlabore)" 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tabela de Pró-labores */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle>Histórico de Pró-labore</CardTitle>
              </CardHeader>
              <CardContent>
                {prolabores.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhum pró-labore registrado</p>
                  </div>
                ) : (
                  <>
                    {/* Desktop Table */}
                    <div className="hidden md:block">
                      <table className="w-full">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-3">Data</th>
                            <th className="text-left p-3">Beneficiário</th>
                            <th className="text-right p-3">Valor</th>
                            <th className="text-left p-3">Conta</th>
                            <th className="text-left p-3">Observação</th>
                            <th className="text-center p-3">Ações</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {prolabores.map(p => (
                            <tr key={p.id} className="hover:bg-muted/50 transition-colors">
                              <td className="p-3">{formatDateDisplay(p.data)}</td>
                              <td className="p-3 font-medium">{p.beneficiario}</td>
                              <td className="p-3 text-right font-mono text-red-500">
                                R$ {p.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="p-3 text-sm text-muted-foreground">
                                {bankAccounts.find(a => a.id === p.conta_id)?.nome || '-'}
                              </td>
                              <td className="p-3 text-sm text-muted-foreground">{p.observacao || '-'}</td>
                              <td className="p-3 text-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteProlabore(p.id)}
                                  className="text-red-500 hover:text-red-600"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Cards */}
                    <div className="md:hidden space-y-3">
                      {prolabores.map(p => (
                        <div key={p.id} className="p-3 rounded-lg border border-border">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm">{p.beneficiario}</p>
                              <p className="text-xs text-muted-foreground">{formatDateDisplay(p.data)}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-red-500 text-sm">
                                R$ {p.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </span>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => handleDeleteProlabore(p.id)}
                              >
                                <Trash2 className="h-3 w-3 text-red-500" />
                              </Button>
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {bankAccounts.find(a => a.id === p.conta_id)?.nome || '-'}
                            {p.observacao && ` • ${p.observacao}`}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Resumo por Beneficiário */}
            {prolabores.length > 0 && (
              <Card className="border-border">
                <CardHeader>
                  <CardTitle>Resumo por Beneficiário</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {Object.entries(
                      prolabores.reduce((acc, p) => {
                        acc[p.beneficiario] = (acc[p.beneficiario] || 0) + p.valor;
                        return acc;
                      }, {})
                    ).map(([beneficiario, total]) => (
                      <div key={beneficiario} className="p-4 bg-muted/50 rounded-lg">
                        <p className="text-sm text-muted-foreground">{beneficiario}</p>
                        <p className="text-xl font-bold font-mono">
                          R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Aba Cheques */}
        {activeTab === 'cheques' && (
          <div className="space-y-4">
            {/* Header com botão */}
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Cheques a Pagar</h2>
              <Button onClick={() => setChequeDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Novo Cheque
              </Button>
            </div>

            {/* Cards de resumo */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Pendente</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold font-mono text-orange-500">
                    R$ {(chequesStats?.total_pendente || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{chequesStats?.qtd_pendentes || 0} cheque(s)</p>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Compensados</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold font-mono text-green-500">
                    R$ {(chequesStats?.total_compensado || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{chequesStats?.qtd_compensados || 0} cheque(s)</p>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Vencidos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold font-mono text-red-500">
                    R$ {(chequesStats?.total_vencido || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{chequesStats?.qtd_vencidos || 0} cheque(s)</p>
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Devolvidos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold font-mono text-gray-500">
                    R$ {(chequesStats?.total_devolvido || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{chequesStats?.qtd_devolvidos || 0} cheque(s)</p>
                </CardContent>
              </Card>
            </div>

            {/* Tabela de Cheques Pendentes */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                  Cheques Pendentes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {cheques.filter(c => c.status === 'pendente').length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Check className="h-12 w-12 mx-auto mb-4 opacity-50 text-green-500" />
                    <p>Nenhum cheque pendente</p>
                  </div>
                ) : (
                  <>
                    {/* Desktop Table */}
                    <div className="hidden md:block">
                      <table className="w-full">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-3">Número</th>
                            <th className="text-left p-3">Beneficiário</th>
                            <th className="text-right p-3">Valor</th>
                            <th className="text-left p-3">Data Pgto</th>
                            <th className="text-left p-3">Observação</th>
                            <th className="text-center p-3">Ações</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {cheques.filter(c => c.status === 'pendente').map(cheque => {
                            const hoje = new Date().toISOString().split('T')[0];
                            const vencido = cheque.data_pagamento < hoje;
                            return (
                              <tr 
                                key={cheque.id} 
                                className={`hover:bg-muted/50 transition-colors ${vencido ? 'bg-red-500/10' : ''}`}
                              >
                                <td className="p-3 font-mono font-bold">#{cheque.numero}</td>
                                <td className="p-3">{cheque.beneficiario}</td>
                                <td className="p-3 text-right font-mono font-bold">
                                  R$ {cheque.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className={`p-3 ${vencido ? 'text-red-500 font-bold' : ''}`}>
                                  {formatDateDisplay(cheque.data_pagamento)}
                                  {vencido && <span className="ml-1 text-xs">(Vencido)</span>}
                                </td>
                                <td className="p-3 text-sm text-muted-foreground">{cheque.observacao || '-'}</td>
                                <td className="p-3">
                                  <div className="flex items-center justify-center gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setChequeToCompensar(cheque);
                                        setCompensarContaId(cheque.conta_id || '');
                                        setCompensarDialogOpen(true);
                                      }}
                                      className="text-green-500 hover:text-green-600"
                                      title="Compensar"
                                    >
                                      <Check className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDevolverCheque(cheque.id)}
                                      className="text-orange-500 hover:text-orange-600"
                                      title="Devolver"
                                    >
                                      <RefreshCw className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteCheque(cheque.id)}
                                      className="text-red-500 hover:text-red-600"
                                      title="Excluir"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Cards */}
                    <div className="md:hidden space-y-3">
                      {cheques.filter(c => c.status === 'pendente').map(cheque => {
                        const hoje = new Date().toISOString().split('T')[0];
                        const vencido = cheque.data_pagamento < hoje;
                        return (
                          <div 
                            key={cheque.id} 
                            className={`p-3 rounded-lg border ${vencido ? 'border-red-500 bg-red-500/10' : 'border-border'}`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-mono font-bold text-sm">#{cheque.numero}</p>
                                <p className="text-sm">{cheque.beneficiario}</p>
                              </div>
                              <span className="font-mono font-bold text-sm ml-2">
                                R$ {cheque.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <div className="text-xs text-muted-foreground">
                                <span className={vencido ? 'text-red-500 font-bold' : ''}>
                                  {formatDateDisplay(cheque.data_pagamento)}
                                  {vencido && ' (Vencido)'}
                                </span>
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => {
                                    setChequeToCompensar(cheque);
                                    setCompensarContaId(cheque.conta_id || '');
                                    setCompensarDialogOpen(true);
                                  }}
                                >
                                  <Check className="h-3 w-3 text-green-500" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => handleDevolverCheque(cheque.id)}
                                >
                                  <RefreshCw className="h-3 w-3 text-orange-500" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => handleDeleteCheque(cheque.id)}
                                >
                                  <Trash2 className="h-3 w-3 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Tabela de Cheques Compensados/Devolvidos */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-green-500"></span>
                  Cheques Compensados / Devolvidos
                </CardTitle>
              </CardHeader>
              <CardContent>
                {cheques.filter(c => c.status !== 'pendente').length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhum cheque compensado ou devolvido</p>
                  </div>
                ) : (
                  <>
                    {/* Mobile: Cards */}
                    <div className="md:hidden space-y-2">
                      {cheques.filter(c => c.status !== 'pendente').map(cheque => (
                        <div 
                          key={cheque.id} 
                          className={`p-3 border rounded-lg ${cheque.status === 'compensado' ? 'bg-green-500/5 border-green-500/30' : 'bg-red-500/5 border-red-500/30'}`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-mono font-bold">#{cheque.numero}</p>
                              <p className="text-sm">{cheque.beneficiario}</p>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              cheque.status === 'compensado' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                            }`}>
                              {cheque.status === 'compensado' ? 'Compensado' : 'Devolvido'}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="font-mono font-bold">R$ {cheque.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                            <span className="text-xs text-muted-foreground">{formatDateDisplay(cheque.data_compensacao || cheque.data_pagamento)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Desktop: Tabela */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-3">Número</th>
                            <th className="text-left p-3">Beneficiário</th>
                            <th className="text-right p-3">Valor</th>
                            <th className="text-left p-3">Data Pgto</th>
                            <th className="text-left p-3">Status</th>
                            <th className="text-left p-3">Data Compensação</th>
                            <th className="text-left p-3">Observação</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {cheques.filter(c => c.status !== 'pendente').map(cheque => (
                            <tr 
                              key={cheque.id} 
                              className={`hover:bg-muted/50 transition-colors ${cheque.status === 'compensado' ? 'bg-green-500/5' : 'bg-red-500/5'}`}
                            >
                              <td className="p-3 font-mono font-bold">#{cheque.numero}</td>
                              <td className="p-3">{cheque.beneficiario}</td>
                              <td className="p-3 text-right font-mono font-bold">
                                R$ {cheque.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="p-3">{formatDateDisplay(cheque.data_pagamento)}</td>
                              <td className="p-3">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  cheque.status === 'compensado'
                                    ? 'bg-green-500/20 text-green-500'
                                    : 'bg-red-500/20 text-red-500'
                                }`}>
                                  {cheque.status === 'compensado' ? 'Compensado' : 'Devolvido'}
                                </span>
                              </td>
                              <td className="p-3 text-sm">
                                {cheque.data_compensacao ? formatDateDisplay(cheque.data_compensacao) : '-'}
                              </td>
                              <td className="p-3 text-sm text-muted-foreground">{cheque.observacao || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ==================== CONTAS BANCÁRIAS ==================== */}
        {activeTab === 'contas' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Contas Bancárias</h2>
              <Button onClick={() => {
                setEditingBankAccount(null);
                setBankAccountFormData({ nome: '', banco: '', saldo: '', conta_cartao: false, limite_cheque_especial: '' });
                setBankAccountDialogOpen(true);
              }}>
                <Plus className="h-4 w-4 mr-2" />
                Nova Conta
              </Button>
            </div>

            {/* Cards de Resumo */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-border bg-gradient-to-br from-green-500/10 to-transparent">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Saldo Total</CardTitle>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6" 
                    onClick={toggleHideValues}
                    title={hideValues ? "Mostrar valores" : "Ocultar valores"}
                  >
                    {hideValues ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold font-mono ${totalBalance >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {formatMoney(totalBalance, hideValues)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{bankAccounts.length} contas</p>
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Limite Cheque Especial</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold font-mono text-blue-500">
                    {formatMoney(totalLimite, hideValues)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Disponível se necessário</p>
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Disponível</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold font-mono ${totalDisponivel >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {formatMoney(totalDisponivel, hideValues)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Saldo + Limite</p>
                </CardContent>
              </Card>
            </div>

            {/* Lista de Contas */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {bankAccounts.map(account => (
                <Card key={account.id} className={`border-border ${account.saldo < 0 ? 'border-red-500/50' : ''}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-medium flex items-center gap-2">
                        <Landmark className="h-5 w-5" />
                        {account.nome}
                      </CardTitle>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenExtratoConta(account)} title="Ver Extrato">
                          <ArrowRightLeft className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleEditBankAccount(account)} title="Editar">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteBankAccount(account.id)} title="Excluir">
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-2">{account.banco}</p>
                    <div className={`text-2xl font-bold font-mono ${account.saldo >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {formatMoney(account.saldo, hideValues)}
                    </div>
                    
                    {/* Limite e Disponível */}
                    <div className="mt-3 pt-3 border-t border-border space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Limite:</span>
                        <span className="font-mono text-blue-500">
                          {formatMoney(account.limite_cheque_especial || 0, hideValues)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Disponível:</span>
                        <span className={`font-mono font-bold ${(account.saldo + (account.limite_cheque_especial || 0)) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {formatMoney(account.saldo + (account.limite_cheque_especial || 0), hideValues)}
                        </span>
                      </div>
                    </div>
                    
                    {account.conta_cartao && (
                      <Badge variant="secondary" className="mt-2">Conta Cartão</Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* ========== ABA EMPRÉSTIMOS (CONTA CORRENTE COM TERCEIROS) ========== */}
        {activeTab === 'emprestimos' && (
          <div className="space-y-6" data-testid="emprestimos-tab">
            {/* Cabeçalho */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-xl font-bold">Empréstimos / Conta Corrente</h2>
                <p className="text-sm text-muted-foreground">Controle de dinheiro emprestado de terceiros</p>
              </div>
              <Button onClick={() => { setEditingPersonalLoan(null); setPersonalLoanFormData({ credor_nome: '', credor_telefone: '', valor_inicial: '', data_inicio: new Date().toISOString().split('T')[0], observacoes: '', tipo_emprestimo: 'normal', taxa_juros_mensal: '', tipo_juros: 'composto' }); setPersonalLoanDialogOpen(true); }} data-testid="new-loan-btn">
                <Plus className="h-4 w-4 mr-2" />
                Novo Empréstimo
              </Button>
            </div>

            {/* Cards Resumo */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="border-purple-500/30 bg-gradient-to-br from-purple-500/10 to-transparent">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Devido</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold font-mono text-purple-500">
                    R$ {personalLoans.filter(l => l.status === 'ativo').reduce((acc, l) => acc + l.saldo_devedor, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{personalLoans.filter(l => l.status === 'ativo').length} empréstimo(s) ativo(s)</p>
                </CardContent>
              </Card>

              <Card className="border-green-500/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Pago</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold font-mono text-green-500">
                    R$ {personalLoans.reduce((acc, l) => acc + l.total_pago, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Ao longo do tempo</p>
                </CardContent>
              </Card>

              <Card className="border-blue-500/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total Pego</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold font-mono text-blue-500">
                    R$ {personalLoans.reduce((acc, l) => acc + l.total_pego, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Total emprestado</p>
                </CardContent>
              </Card>

              <Card className="border-red-500/30 bg-gradient-to-br from-red-500/10 to-transparent">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Total em Juros</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold font-mono text-red-500">
                    R$ {personalLoans.reduce((acc, l) => acc + (l.total_juros || 0), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{personalLoans.filter(l => l.tipo_emprestimo === 'agiota').length} empréstimo(s) com juros</p>
                </CardContent>
              </Card>
            </div>

            {/* Lista de Empréstimos */}
            {personalLoans.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <ArrowRightLeft className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Nenhum empréstimo cadastrado</p>
                  <p className="text-sm text-muted-foreground">Clique em "Novo Empréstimo" para começar</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {personalLoans.map(loan => (
                  <Card key={loan.id} className={`${loan.status === 'quitado' ? 'opacity-60' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-bold">{loan.credor_nome}</h3>
                            <Badge variant={loan.status === 'ativo' ? 'default' : 'secondary'} className={loan.status === 'ativo' ? 'bg-purple-500' : 'bg-green-500'}>
                              {loan.status === 'ativo' ? 'Ativo' : 'Quitado'}
                            </Badge>
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-3">
                            {loan.credor_telefone && (
                              <a href={getWhatsAppLink(loan.credor_telefone)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-green-500">
                                📱 {loan.credor_telefone}
                              </a>
                            )}
                            <span>Início: {formatDateDisplay(loan.data_inicio)}</span>
                            {loan.tipo_emprestimo === 'agiota' && loan.taxa_juros_mensal && (
                              <Badge variant="outline" className="border-red-500/50 text-red-500">
                                {loan.taxa_juros_mensal}% a.m.
                              </Badge>
                            )}
                            {loan.tipo_emprestimo === 'agiota' && loan.taxa_juros_mensal && (
                              <Badge variant="outline" className="border-amber-500/50 text-amber-500">
                                {loan.tipo_juros === 'simples' ? 'Juros Simples' : 'Juros Compostos'}
                              </Badge>
                            )}
                          </div>

                          <div className={`grid ${loan.tipo_emprestimo === 'agiota' ? 'grid-cols-4' : 'grid-cols-3'} gap-4 p-3 bg-muted/50 rounded-lg`}>
                            <div>
                              <p className="text-xs text-muted-foreground">Saldo Devedor</p>
                              <p className="text-lg font-bold font-mono text-purple-500">
                                R$ {loan.saldo_devedor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Total Pego</p>
                              <p className="font-mono text-blue-500">
                                R$ {loan.total_pego.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Total Pago</p>
                              <p className="font-mono text-green-500">
                                R$ {loan.total_pago.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </p>
                            </div>
                            {loan.tipo_emprestimo === 'agiota' && (
                              <div>
                                <p className="text-xs text-muted-foreground">Total Juros</p>
                                <p className="font-mono text-red-500">
                                  R$ {(loan.total_juros || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </p>
                              </div>
                            )}
                          </div>

                          {loan.observacoes && (
                            <p className="text-sm text-muted-foreground mt-2 italic">"{loan.observacoes}"</p>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleViewMovements(loan)} data-testid={`view-movements-${loan.id}`}>
                            <FileText className="h-4 w-4 mr-1" />
                            Histórico
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handlePrintLoanReport(loan)} data-testid={`print-loan-${loan.id}`}>
                            <Printer className="h-4 w-4 mr-1" />
                            Imprimir
                          </Button>
                          {loan.status === 'ativo' && (
                            <>
                              <Button variant="outline" size="sm" className="text-green-500 border-green-500/50 hover:bg-green-500/10" onClick={() => handleOpenMovementDialog(loan, 'pagamento')}>
                                <ArrowUpRight className="h-4 w-4 mr-1" />
                                Pagar
                              </Button>
                              <Button variant="outline" size="sm" className="text-blue-500 border-blue-500/50 hover:bg-blue-500/10" onClick={() => handleOpenMovementDialog(loan, 'entrada')}>
                                <ArrowDownRight className="h-4 w-4 mr-1" />
                                Pegar Mais
                              </Button>
                              {loan.tipo_emprestimo === 'agiota' && loan.taxa_juros_mensal > 0 && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="text-red-500 border-red-500/50 hover:bg-red-500/10" 
                                  onClick={() => handleAplicarJuros(loan.id)}
                                >
                                  <Percent className="h-4 w-4 mr-1" />
                                  Aplicar Juros
                                </Button>
                              )}
                            </>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleEditPersonalLoan(loan)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDeletePersonalLoan(loan.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ==================== TAB: SIMPLES NACIONAL (DAS) ==================== */}
        {activeTab === 'simples' && (
          <div className="space-y-6" data-testid="simples-tab">
            {/* Seletor de Período */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Label>Mês/Ano:</Label>
                <Select value={simplesMes.toString()} onValueChange={(v) => setSimplesMes(parseInt(v))}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                      <SelectItem key={m} value={m.toString()}>
                        {['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][m-1]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={simplesAno.toString()} onValueChange={(v) => setSimplesAno(parseInt(v))}>
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(a => (
                      <SelectItem key={a} value={a.toString()}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 ml-auto">
                <Button variant="outline" onClick={() => setFaturamentoDialogOpen(true)} title="Ajustar faturamento manualmente (sobrescreve NF-e)">
                  <Plus className="h-4 w-4 mr-1" /> Ajustar Faturamento
                </Button>
                <Button variant="outline" onClick={() => setDasPagoDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" /> DAS Pago
                </Button>
                <div className="relative">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleDasUpload}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    disabled={dasUploadLoading}
                  />
                  <Button variant="default" disabled={dasUploadLoading} className="bg-green-600 hover:bg-green-700">
                    <Upload className="h-4 w-4 mr-1" />
                    {dasUploadLoading ? 'Processando...' : 'Importar PDF'}
                  </Button>
                </div>
                <Button variant="secondary" onClick={() => setSimulacaoDialogOpen(true)}>
                  <Target className="h-4 w-4 mr-1" /> Simular
                </Button>
              </div>
            </div>

            {simplesLoading ? (
              <p className="text-center py-8 text-muted-foreground">Carregando...</p>
            ) : (
              <>
                {/* Cards de Resumo */}
                {simplesCalculo && (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card className="p-4">
                      <p className="text-xs text-muted-foreground mb-1">Faturamento do Mês</p>
                      <p className="text-xl font-bold font-mono text-primary">
                        {formatCurrency(simplesCalculo.faturamento_mes)}
                      </p>
                    </Card>
                    <Card className="p-4">
                      <p className="text-xs text-muted-foreground mb-1">Faturamento 12 Meses</p>
                      <p className="text-xl font-bold font-mono">
                        {formatCurrency(simplesCalculo.faturamento_12_meses)}
                      </p>
                      <p className="text-xs text-muted-foreground">Faixa {simplesCalculo.faixa}</p>
                    </Card>
                    <Card className="p-4">
                      <p className="text-xs text-muted-foreground mb-1">Alíquota Efetiva</p>
                      <p className="text-xl font-bold font-mono text-blue-500">
                        {simplesCalculo.aliquota_efetiva}%
                      </p>
                      <p className="text-xs text-muted-foreground">Nominal: {simplesCalculo.aliquota_nominal}%</p>
                    </Card>
                    <Card className="p-4 bg-destructive/10">
                      <p className="text-xs text-muted-foreground mb-1">DAS Estimado</p>
                      <p className="text-xl font-bold font-mono text-destructive">
                        {formatCurrency(simplesCalculo.valor_das_estimado)}
                      </p>
                    </Card>
                    <Card className="p-4 bg-green-500/10">
                      <p className="text-xs text-muted-foreground mb-1">DAS Pago</p>
                      <p className="text-xl font-bold font-mono text-green-600">
                        {simplesCalculo.valor_das_pago ? formatCurrency(simplesCalculo.valor_das_pago) : '-'}
                      </p>
                      {simplesCalculo.diferenca !== null && (
                        <p className={`text-xs ${simplesCalculo.diferenca > 0 ? 'text-destructive' : 'text-green-500'}`}>
                          Diferença: {formatCurrency(simplesCalculo.diferenca)}
                        </p>
                      )}
                    </Card>
                  </div>
                )}

                {/* Alerta de Proximidade de Faixa */}
                {simplesCalculo?.alerta_faixa?.nivel_alerta && (
                  <Card className={`border-2 ${
                    simplesCalculo.alerta_faixa.nivel_alerta === 'urgente' 
                      ? 'border-red-500 bg-red-500/10' 
                      : simplesCalculo.alerta_faixa.nivel_alerta === 'atencao'
                        ? 'border-orange-500 bg-orange-500/10'
                        : 'border-yellow-500 bg-yellow-500/10'
                  }`}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-full ${
                          simplesCalculo.alerta_faixa.nivel_alerta === 'urgente' 
                            ? 'bg-red-500 text-white' 
                            : simplesCalculo.alerta_faixa.nivel_alerta === 'atencao'
                              ? 'bg-orange-500 text-white'
                              : 'bg-yellow-500 text-black'
                        }`}>
                          <AlertTriangle className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <p className={`font-bold ${
                            simplesCalculo.alerta_faixa.nivel_alerta === 'urgente' 
                              ? 'text-red-600' 
                              : simplesCalculo.alerta_faixa.nivel_alerta === 'atencao'
                                ? 'text-orange-600'
                                : 'text-yellow-600'
                          }`}>
                            {simplesCalculo.alerta_faixa.nivel_alerta === 'urgente' 
                              ? '⚠️ ALERTA URGENTE - Próximo ao limite da faixa!' 
                              : simplesCalculo.alerta_faixa.nivel_alerta === 'atencao'
                                ? '🔔 ATENÇÃO - Aproximando-se do limite da faixa'
                                : '📊 AVISO - Faturamento em crescimento'}
                          </p>
                          <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground">Limite da Faixa {simplesCalculo.faixa}</p>
                              <p className="font-mono font-bold">{formatCurrency(simplesCalculo.alerta_faixa.limite_faixa_atual)}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Utilizado</p>
                              <p className="font-mono font-bold">{simplesCalculo.alerta_faixa.percentual_utilizado}%</p>
                              <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                <div 
                                  className={`h-2 rounded-full ${
                                    simplesCalculo.alerta_faixa.nivel_alerta === 'urgente' 
                                      ? 'bg-red-500' 
                                      : simplesCalculo.alerta_faixa.nivel_alerta === 'atencao'
                                        ? 'bg-orange-500'
                                        : 'bg-yellow-500'
                                  }`}
                                  style={{ width: `${Math.min(simplesCalculo.alerta_faixa.percentual_utilizado, 100)}%` }}
                                />
                              </div>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Falta para limite</p>
                              <p className="font-mono font-bold text-green-600">{formatCurrency(simplesCalculo.alerta_faixa.falta_para_limite)}</p>
                            </div>
                          </div>
                          {simplesCalculo.alerta_faixa.proxima_faixa && (
                            <div className="mt-3 p-2 bg-muted/50 rounded text-sm">
                              <p className="text-muted-foreground">
                                Ao ultrapassar o limite, você entrará na <strong>Faixa {simplesCalculo.alerta_faixa.proxima_faixa.faixa}</strong> com 
                                alíquota nominal de <strong>{simplesCalculo.alerta_faixa.proxima_faixa.aliquota_nominal}%</strong> (efetiva estimada: ~{simplesCalculo.alerta_faixa.proxima_faixa.aliquota_efetiva_estimada}%)
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Distribuição dos Tributos */}
                {simplesCalculo?.distribuicao_tributos && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <PieChart className="h-5 w-5" /> Distribuição do DAS
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 sm:grid-cols-7 gap-3">
                        {Object.entries(simplesCalculo.distribuicao_tributos).map(([tributo, valor]) => (
                          <div key={tributo} className="text-center p-2 bg-muted/50 rounded">
                            <p className="text-xs font-medium">{tributo}</p>
                            <p className="text-sm font-mono font-bold">{formatCurrency(valor)}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Grid: Histórico e Faturamentos */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Histórico Mensal */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <History className="h-5 w-5" /> Histórico (Últimos 12 meses)
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Mês</TableHead>
                            <TableHead className="text-right">Alíq.</TableHead>
                            <TableHead className="text-right">DAS Est.</TableHead>
                            <TableHead className="text-right">DAS Pago</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {simplesHistorico.slice(0, 12).map((h) => (
                            <TableRow key={`${h.ano}-${h.mes}`}>
                              <TableCell className="font-medium">
                                {['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][h.mes-1]}/{h.ano}
                              </TableCell>
                              <TableCell className="text-right">{h.aliquota_efetiva}%</TableCell>
                              <TableCell className="text-right font-mono text-destructive">{formatCurrency(h.das_estimado)}</TableCell>
                              <TableCell className="text-right font-mono text-green-600">
                                {h.das_pago ? formatCurrency(h.das_pago) : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>

                  {/* Faturamentos Manuais + DAS Pagos */}
                  <div className="space-y-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <TrendingUp className="h-5 w-5" /> Faturamento por Mês
                        </CardTitle>
                        <p className="text-xs text-muted-foreground">Dados das NF-es faturadas (ajuste manual se necessário)</p>
                      </CardHeader>
                      <CardContent className="p-0">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Mês/Ano</TableHead>
                              <TableHead className="text-right">Valor NF-es</TableHead>
                              <TableHead className="text-right">Valor Final</TableHead>
                              <TableHead>Origem</TableHead>
                              <TableHead></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {simplesFaturamentos.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5} className="text-center text-muted-foreground py-4">
                                  Nenhum faturamento encontrado
                                </TableCell>
                              </TableRow>
                            ) : (
                              simplesFaturamentos.slice(0, 12).map((f) => (
                                <TableRow key={`${f.ano}-${f.mes}`}>
                                  <TableCell className="font-medium">{['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][f.mes-1]}/{f.ano}</TableCell>
                                  <TableCell className="text-right font-mono text-muted-foreground">
                                    {f.qtd_nfes > 0 ? `${formatCurrency(f.valor_nfes)} (${f.qtd_nfes} NF-es)` : '-'}
                                  </TableCell>
                                  <TableCell className="text-right font-mono font-bold text-primary">{formatCurrency(f.valor_final)}</TableCell>
                                  <TableCell>
                                    <Badge variant={f.origem === 'nfe' ? 'default' : 'secondary'}>
                                      {f.origem === 'nfe' ? 'NF-e' : 'Manual'}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {f.origem === 'manual' && (
                                      <Button variant="ghost" size="sm" onClick={() => handleDeleteFaturamento(f.ano, f.mes)}>
                                        <Trash2 className="h-4 w-4 text-destructive" />
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Check className="h-5 w-5" /> DAS Pagos
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-0">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Mês/Ano</TableHead>
                              <TableHead className="text-right">Valor</TableHead>
                              <TableHead>Data Pgto</TableHead>
                              <TableHead>PDF</TableHead>
                              <TableHead></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {simplesDasPagos.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5} className="text-center text-muted-foreground py-4">
                                  Nenhum DAS pago cadastrado
                                </TableCell>
                              </TableRow>
                            ) : (
                              simplesDasPagos.map((d) => (
                                <TableRow key={`${d.ano}-${d.mes}`}>
                                  <TableCell>{['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][d.mes-1]}/{d.ano}</TableCell>
                                  <TableCell className="text-right font-mono font-bold text-green-600">{formatCurrency(d.valor_pago)}</TableCell>
                                  <TableCell>{d.data_pagamento?.split('-').reverse().join('/')}</TableCell>
                                  <TableCell>
                                    {d.pdf_url ? (
                                      <a 
                                        href={`${API_URL}${d.pdf_url}`} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-blue-500 hover:text-blue-700"
                                        title={d.pdf_filename || 'Baixar PDF'}
                                      >
                                        <Download className="h-4 w-4" />
                                        <span className="text-xs">PDF</span>
                                      </a>
                                    ) : (
                                      <span className="text-muted-foreground text-xs">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Button variant="ghost" size="sm" onClick={() => handleDeleteDasPago(d.ano, d.mes)}>
                                      <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </>
            )}
          </div>
        )}


        {/* Modal Novo/Editar Empréstimo Pessoal */}
        <Dialog open={personalLoanDialogOpen} onOpenChange={setPersonalLoanDialogOpen}>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-[1000px] sm:pb-6 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowRightLeft className="h-5 w-5" />
                {editingPersonalLoan ? 'Editar Empréstimo' : 'Novo Empréstimo'}
              </DialogTitle>
              <DialogDescription>
                Registre um empréstimo de dinheiro de terceiros
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handlePersonalLoanSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nome do Credor *</Label>
                <Input
                  placeholder="Nome de quem emprestou"
                  value={personalLoanFormData.credor_nome}
                  onChange={(e) => setPersonalLoanFormData({ ...personalLoanFormData, credor_nome: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input
                  placeholder="(00) 00000-0000"
                  value={personalLoanFormData.credor_telefone}
                  onChange={(e) => setPersonalLoanFormData({ ...personalLoanFormData, credor_telefone: formatPhone(e.target.value) })}
                />
              </div>
              {!editingPersonalLoan && (
                <>
                  <div className="space-y-2">
                    <Label>Valor Inicial *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={personalLoanFormData.valor_inicial}
                      onChange={(e) => setPersonalLoanFormData({ ...personalLoanFormData, valor_inicial: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Data de Início *</Label>
                    <Input
                      type="date"
                      value={personalLoanFormData.data_inicio}
                      onChange={(e) => setPersonalLoanFormData({ ...personalLoanFormData, data_inicio: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Conta que Recebe o Valor</Label>
                    <Select 
                      value={personalLoanFormData.conta_id} 
                      onValueChange={(v) => setPersonalLoanFormData({ ...personalLoanFormData, conta_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a conta (opcional)" />
                      </SelectTrigger>
                      <SelectContent>
                        {bankAccounts.map(account => (
                          <SelectItem key={account.id} value={account.id}>
                            {account.nome} - R$ {account.saldo?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">O valor será creditado automaticamente nesta conta</p>
                  </div>

                  {/* Tipo de Empréstimo */}
                  <div className="space-y-2">
                    <Label>Tipo de Empréstimo</Label>
                    <Select 
                      value={personalLoanFormData.tipo_emprestimo} 
                      onValueChange={(v) => setPersonalLoanFormData({ ...personalLoanFormData, tipo_emprestimo: v, taxa_juros_mensal: v === 'normal' ? '' : personalLoanFormData.taxa_juros_mensal })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">Normal (sem juros)</SelectItem>
                        <SelectItem value="agiota">Com Juros Mensal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Taxa de Juros - só mostra se for agiota */}
                  {personalLoanFormData.tipo_emprestimo === 'agiota' && (
                    <>
                      <div className="space-y-2">
                        <Label>Taxa de Juros Mensal (%)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          placeholder="Ex: 10 (para 10% ao mês)"
                          value={personalLoanFormData.taxa_juros_mensal}
                          onChange={(e) => setPersonalLoanFormData({ ...personalLoanFormData, taxa_juros_mensal: e.target.value })}
                          required
                          data-testid="loan-taxa-juros-input"
                        />
                        <p className="text-xs text-muted-foreground">Os juros são cobrados no aniversário mensal a partir da data em que o dinheiro foi pego</p>
                      </div>

                      <div className="space-y-2">
                        <Label>Cálculo dos Juros</Label>
                        <Select
                          value={personalLoanFormData.tipo_juros}
                          onValueChange={(v) => setPersonalLoanFormData({ ...personalLoanFormData, tipo_juros: v })}
                        >
                          <SelectTrigger data-testid="loan-tipo-juros-select">
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="simples">Simples (sobre valor bruto pego)</SelectItem>
                            <SelectItem value="composto">Composto (juros sobre juros)</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          {personalLoanFormData.tipo_juros === 'simples'
                            ? 'Ex.: pegou R$ 1.000 a 10% a.m. → sempre R$ 100 de juros por mês, independente do saldo.'
                            : 'Ex.: pegou R$ 1.000 a 10% a.m. → 1º mês R$ 100, 2º mês R$ 110 (sobre R$ 1.100), e assim por diante.'}
                        </p>
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Taxa de juros para edição (se for agiota) */}
              {editingPersonalLoan && editingPersonalLoan.tipo_emprestimo === 'agiota' && (
                <div className="space-y-2">
                  <Label>Taxa de Juros Mensal (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="Ex: 10 (para 10% ao mês)"
                    value={personalLoanFormData.taxa_juros_mensal}
                    onChange={(e) => setPersonalLoanFormData({ ...personalLoanFormData, taxa_juros_mensal: e.target.value })}
                  />

                  <Label className="mt-2">Cálculo dos Juros</Label>
                  <Select
                    value={personalLoanFormData.tipo_juros}
                    onValueChange={(v) => setPersonalLoanFormData({ ...personalLoanFormData, tipo_juros: v })}
                  >
                    <SelectTrigger data-testid="loan-tipo-juros-select-edit">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="simples">Simples (sobre valor bruto pego)</SelectItem>
                      <SelectItem value="composto">Composto (juros sobre juros)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Observações</Label>
                <Input
                  placeholder="Anotações sobre o empréstimo"
                  value={personalLoanFormData.observacoes}
                  onChange={(e) => setPersonalLoanFormData({ ...personalLoanFormData, observacoes: e.target.value })}
                />
              </div>
              <Button type="submit" className="w-full">
                {editingPersonalLoan ? 'Atualizar' : 'Registrar'} Empréstimo
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal Adicionar Movimentação */}
        <Dialog open={movementDialogOpen} onOpenChange={setMovementDialogOpen}>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-[1000px] sm:pb-6 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {movementFormData.tipo === 'pagamento' ? (
                  <><ArrowUpRight className="h-5 w-5 text-green-500" /> Registrar Pagamento</>
                ) : (
                  <><ArrowDownRight className="h-5 w-5 text-blue-500" /> Pegar Mais Dinheiro</>
                )}
              </DialogTitle>
              <DialogDescription>
                {selectedLoanForMovement && `Empréstimo de ${selectedLoanForMovement.credor_nome}`}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleMovementSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Valor *</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={movementFormData.valor}
                  onChange={(e) => setMovementFormData({ ...movementFormData, valor: e.target.value })}
                  required
                />
                {selectedLoanForMovement && movementFormData.tipo === 'pagamento' && (
                  <p className="text-xs text-muted-foreground">
                    Saldo devedor: R$ {selectedLoanForMovement.saldo_devedor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Data *</Label>
                <Input
                  type="date"
                  value={movementFormData.data}
                  onChange={(e) => setMovementFormData({ ...movementFormData, data: e.target.value })}
                  required
                />
              </div>
              {movementFormData.tipo === 'pagamento' && (
                <div className="space-y-2">
                  <Label>Conta para Débito *</Label>
                  <Select value={movementFormData.conta_id} onValueChange={(v) => setMovementFormData({ ...movementFormData, conta_id: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a conta..." />
                    </SelectTrigger>
                    <SelectContent>
                      {bankAccounts.map(acc => (
                        <SelectItem key={acc.id} value={acc.id}>
                          {acc.nome} (R$ {acc.saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {movementFormData.tipo === 'entrada' && (
                <div className="space-y-2">
                  <Label>Conta que Recebe o Valor</Label>
                  <Select value={movementFormData.conta_id} onValueChange={(v) => setMovementFormData({ ...movementFormData, conta_id: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a conta (opcional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {bankAccounts.map(acc => (
                        <SelectItem key={acc.id} value={acc.id}>
                          {acc.nome} (R$ {acc.saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">O valor será creditado nesta conta</p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Observação</Label>
                <Input
                  placeholder="Descrição opcional"
                  value={movementFormData.observacao}
                  onChange={(e) => setMovementFormData({ ...movementFormData, observacao: e.target.value })}
                />
              </div>
              <Button type="submit" className={`w-full ${movementFormData.tipo === 'pagamento' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
                {movementFormData.tipo === 'pagamento' ? 'Registrar Pagamento' : 'Registrar Entrada'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal Histórico de Movimentações */}
        <Dialog open={!!viewingLoanMovements} onOpenChange={() => setViewingLoanMovements(null)}>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto overflow-x-hidden pb-[1000px] sm:pb-6 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Histórico de Movimentações
              </DialogTitle>
              <DialogDescription>
                {viewingLoanMovements && `Empréstimo de ${viewingLoanMovements.credor_nome}`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {viewingLoanMovements && (
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrintLoanReport}
                    data-testid="print-loan-report-btn"
                    className="gap-2"
                  >
                    <Printer className="h-4 w-4" />
                    Imprimir Relatório
                  </Button>
                </div>
              )}
              {viewingLoanMovements && (
                <div className="grid grid-cols-3 gap-4 p-3 bg-muted/50 rounded-lg text-center">
                  <div>
                    <p className="text-xs text-muted-foreground">Saldo Devedor</p>
                    <p className="font-bold font-mono text-purple-500">
                      R$ {viewingLoanMovements.saldo_devedor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Pego</p>
                    <p className="font-mono text-blue-500">
                      R$ {viewingLoanMovements.total_pego.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Pago</p>
                    <p className="font-mono text-green-500">
                      R$ {viewingLoanMovements.total_pago.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              )}
              
              {loanMovements.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">Nenhuma movimentação registrada</p>
              ) : (
                <div className="space-y-2">
                  {loanMovements.map(mov => {
                    const isEntrada = mov.tipo === 'entrada';
                    const isJuros = mov.tipo === 'juros';
                    const bgClass = isEntrada ? 'bg-blue-500/10' : isJuros ? 'bg-red-500/10' : 'bg-green-500/10';
                    const iconColor = isEntrada ? 'text-blue-500' : isJuros ? 'text-red-500' : 'text-green-500';
                    const label = isEntrada ? 'Pegou' : isJuros ? 'Juros cobrados' : 'Pagou';
                    // Juros SOMAM ao saldo devedor (como entrada), mostram como "+" vermelho
                    const sinal = isEntrada || isJuros ? '+' : '-';
                    const valueColor = isEntrada ? 'text-blue-500' : isJuros ? 'text-red-500' : 'text-green-500';
                    return (
                      <div key={mov.id} className={`flex items-center justify-between p-3 rounded-lg ${bgClass}`}>
                        <div className="flex items-center gap-3">
                          {isEntrada ? (
                            <ArrowDownRight className={`h-5 w-5 ${iconColor}`} />
                          ) : isJuros ? (
                            <Percent className={`h-5 w-5 ${iconColor}`} />
                          ) : (
                            <ArrowUpRight className={`h-5 w-5 ${iconColor}`} />
                          )}
                          <div>
                            <p className="font-medium">{label}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatDateDisplay(mov.data)}
                              {mov.observacao && ` • ${mov.observacao}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`font-bold font-mono ${valueColor}`}>
                            {sinal} R$ {mov.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          <Button variant="ghost" size="sm" className="text-destructive h-8 w-8 p-0" onClick={() => handleDeleteMovement(viewingLoanMovements.id, mov.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal Nova/Editar Conta Bancária */}
        <Dialog open={bankAccountDialogOpen} onOpenChange={setBankAccountDialogOpen}>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-[1000px] sm:pb-6 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Landmark className="h-5 w-5" />
                {editingBankAccount ? 'Editar Conta' : 'Nova Conta Bancária'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleBankAccountSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nome da Conta *</Label>
                <Input
                  value={bankAccountFormData.nome}
                  onChange={(e) => setBankAccountFormData({ ...bankAccountFormData, nome: e.target.value })}
                  placeholder="Ex: Conta Corrente, Poupança..."
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Banco</Label>
                <Input
                  value={bankAccountFormData.banco}
                  onChange={(e) => setBankAccountFormData({ ...bankAccountFormData, banco: e.target.value })}
                  placeholder="Ex: Banco do Brasil, Itaú..."
                />
              </div>
              <div className="space-y-2">
                <Label>Saldo Inicial</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={bankAccountFormData.saldo}
                  onChange={(e) => setBankAccountFormData({ ...bankAccountFormData, saldo: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Limite Cheque Especial</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={bankAccountFormData.limite_cheque_especial}
                  onChange={(e) => setBankAccountFormData({ ...bankAccountFormData, limite_cheque_especial: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="conta_cartao"
                  checked={bankAccountFormData.conta_cartao}
                  onChange={(e) => setBankAccountFormData({ ...bankAccountFormData, conta_cartao: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label htmlFor="conta_cartao">Conta para recebimentos de cartão</Label>
              </div>
              <Button type="submit" className="w-full">
                {editingBankAccount ? 'Salvar' : <><Plus className="h-4 w-4 mr-2" />Adicionar</>}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal Extrato da Conta */}
        <Dialog open={extratoDialogOpen} onOpenChange={setExtratoDialogOpen}>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-4xl max-h-[85vh] overflow-y-auto overflow-x-hidden pb-[1000px] sm:pb-6 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowRightLeft className="h-5 w-5" />
                Extrato - {extratoAccount?.nome}
              </DialogTitle>
              <DialogDescription>
                {extratoAccount?.banco} | Saldo atual: R$ {extratoAccount?.saldo?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </DialogDescription>
            </DialogHeader>

            {/* Filtros do Extrato */}
            <div className="flex gap-4 flex-wrap">
              <Select value={extratoFilter} onValueChange={setExtratoFilter}>
                <SelectTrigger className="w-auto min-w-[120px]">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="entrada">Entradas</SelectItem>
                  <SelectItem value="saida">Saídas</SelectItem>
                </SelectContent>
              </Select>
              <Select value={extratoPeriodo} onValueChange={setExtratoPeriodo}>
                <SelectTrigger className="w-auto min-w-[120px]">
                  <SelectValue placeholder="Período" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="7">7 dias</SelectItem>
                  <SelectItem value="30">30 dias</SelectItem>
                  <SelectItem value="90">90 dias</SelectItem>
                  <SelectItem value="custom">Personalizado</SelectItem>
                </SelectContent>
              </Select>
              {extratoPeriodo === 'custom' && (
                <>
                  <Input type="date" value={extratoDataInicio} onChange={(e) => setExtratoDataInicio(e.target.value)} className="w-[150px]" />
                  <Input type="date" value={extratoDataFim} onChange={(e) => setExtratoDataFim(e.target.value)} className="w-[150px]" />
                </>
              )}
            </div>

            {/* Resumo */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-green-500/10 rounded-lg">
                <p className="text-xs text-muted-foreground">Entradas</p>
                <p className="text-lg font-bold font-mono text-green-500">
                  R$ {extratoTotais.entradas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="p-3 bg-red-500/10 rounded-lg">
                <p className="text-xs text-muted-foreground">Saídas</p>
                <p className="text-lg font-bold font-mono text-red-500">
                  R$ {extratoTotais.saidas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="p-3 bg-orange-500/10 rounded-lg">
                <p className="text-xs text-muted-foreground">Juros</p>
                <p className="text-lg font-bold font-mono text-orange-500">
                  R$ {extratoTotais.juros.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="p-3 bg-orange-500/10 rounded-lg">
                <p className="text-xs text-muted-foreground">Tarifas</p>
                <p className="text-lg font-bold font-mono text-orange-500">
                  R$ {extratoTotais.tarifas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>

            {/* Lista de Transações */}
            {extratoLoading ? (
              <div className="text-center py-8">Carregando...</div>
            ) : filteredExtratoTransactions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">Nenhuma transação encontrada</div>
            ) : (
              <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3">Data</th>
                      <th className="text-left p-3">Descrição</th>
                      <th className="text-left p-3">Categoria</th>
                      <th className="text-right p-3">Valor</th>
                      <th className="text-right p-3">Adicional</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {groupTransactionsByDay(
                      filteredExtratoTransactions.slice((extratoCurrentPage - 1) * extratoItemsPerPage, extratoCurrentPage * extratoItemsPerPage)
                    ).map(group => (
                      <React.Fragment key={group.dateKey}>
                        <DayHeader group={group} columnsForTable={5} colorAccent="blue" />
                        {group.items.map(t => {
                      const isEntrada = t.tipo !== 'despesa' && t.operacao !== 'saida';
                      return (
                        <tr key={t.id} className="hover:bg-muted/50">
                          <td className="p-3">{formatDateDisplay(t.data)}</td>
                          <td className="p-3">{t.descricao}</td>
                          <td className="p-3">{t.categoria || '-'}</td>
                          <td className={`p-3 text-right font-mono font-bold ${isEntrada ? 'text-green-500' : 'text-red-500'}`}>
                            {isEntrada ? '+' : '-'} R$ {t.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="p-3 text-right">
                            {t.valor_adicional > 0 && (
                              <span className="text-orange-500 font-mono text-sm">
                                {t.tipo_adicional === 'juros' ? 'Juros' : 'Tarifa'}: R$ {t.valor_adicional.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Paginação do Extrato */}
              {Math.ceil(filteredExtratoTransactions.length / extratoItemsPerPage) > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <div className="text-sm text-muted-foreground">
                    {((extratoCurrentPage - 1) * extratoItemsPerPage) + 1} a {Math.min(extratoCurrentPage * extratoItemsPerPage, filteredExtratoTransactions.length)} de {filteredExtratoTransactions.length}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setExtratoCurrentPage(1)}
                      disabled={extratoCurrentPage === 1}
                    >
                      «
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setExtratoCurrentPage(p => Math.max(1, p - 1))}
                      disabled={extratoCurrentPage === 1}
                    >
                      ‹
                    </Button>
                    
                    {Array.from({ length: Math.min(5, Math.ceil(filteredExtratoTransactions.length / extratoItemsPerPage)) }, (_, i) => {
                      const totalPages = Math.ceil(filteredExtratoTransactions.length / extratoItemsPerPage);
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (extratoCurrentPage <= 3) {
                        pageNum = i + 1;
                      } else if (extratoCurrentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = extratoCurrentPage - 2 + i;
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={extratoCurrentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => setExtratoCurrentPage(pageNum)}
                          className="w-8 h-8 p-0"
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                    
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setExtratoCurrentPage(p => Math.min(Math.ceil(filteredExtratoTransactions.length / extratoItemsPerPage), p + 1))}
                      disabled={extratoCurrentPage === Math.ceil(filteredExtratoTransactions.length / extratoItemsPerPage)}
                    >
                      ›
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setExtratoCurrentPage(Math.ceil(filteredExtratoTransactions.length / extratoItemsPerPage))}
                      disabled={extratoCurrentPage === Math.ceil(filteredExtratoTransactions.length / extratoItemsPerPage)}
                    >
                      »
                    </Button>
                  </div>
                </div>
              )}
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Modal para Novo Cheque */}
        <Dialog open={chequeDialogOpen} onOpenChange={setChequeDialogOpen}>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-[1000px] sm:pb-6 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Novo Cheque
              </DialogTitle>
              <DialogDescription>
                Cadastre um cheque a pagar
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateCheque} className="space-y-4">
              <div className="space-y-2">
                <Label>Número do Cheque *</Label>
                <Input
                  type="text"
                  placeholder="Ex: 000123"
                  value={chequeForm.numero}
                  onChange={(e) => setChequeForm({ ...chequeForm, numero: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Beneficiário (para quem) *</Label>
                <Input
                  type="text"
                  placeholder="Nome do beneficiário"
                  value={chequeForm.beneficiario}
                  onChange={(e) => setChequeForm({ ...chequeForm, beneficiario: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Valor *</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={chequeForm.valor}
                  onChange={(e) => setChequeForm({ ...chequeForm, valor: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Data de Pagamento *</Label>
                <Input
                  type="date"
                  value={chequeForm.data_pagamento}
                  onChange={(e) => setChequeForm({ ...chequeForm, data_pagamento: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Conta Bancária</Label>
                <Select
                  value={chequeForm.conta_id || 'none'}
                  onValueChange={(v) => setChequeForm({ ...chequeForm, conta_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a conta" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma</SelectItem>
                    {bankAccounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>
                        {acc.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Observação</Label>
                <Input
                  type="text"
                  placeholder="Observação opcional"
                  value={chequeForm.observacao}
                  onChange={(e) => setChequeForm({ ...chequeForm, observacao: e.target.value })}
                />
              </div>

              <Button type="submit" className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Cadastrar Cheque
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal para Compensar Cheque */}
        <Dialog open={compensarDialogOpen} onOpenChange={setCompensarDialogOpen}>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-[1000px] sm:pb-6 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                Compensar Cheque
              </DialogTitle>
              <DialogDescription>
                Confirme a conta para débito do cheque
              </DialogDescription>
            </DialogHeader>
            {chequeToCompensar && (
              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Cheque:</span>
                    <span className="font-mono font-bold">#{chequeToCompensar.numero}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Beneficiário:</span>
                    <span className="font-medium">{chequeToCompensar.beneficiario}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Valor:</span>
                    <span className="font-mono font-bold text-red-500">
                      R$ {chequeToCompensar.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Conta para Débito *</Label>
                  <Select
                    value={compensarContaId || 'none'}
                    onValueChange={(v) => setCompensarContaId(v === 'none' ? '' : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a conta" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhuma (não debitar)</SelectItem>
                      {bankAccounts.map(acc => (
                        <SelectItem key={acc.id} value={acc.id}>
                          {acc.nome} (R$ {acc.saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setCompensarDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={handleCompensarCheque}>
                    <Check className="h-4 w-4 mr-2" />
                    Confirmar Compensação
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        <Dialog open={prolaboreDialogOpen} onOpenChange={setProlaboreDialogOpen}>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-[1000px] sm:pb-6 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Novo Pró-labore
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateProlabore} className="space-y-4">
              <div className="space-y-2">
                <Label>Beneficiário *</Label>
                <Select
                  value={prolaboreForm.beneficiario}
                  onValueChange={(v) => setProlaboreForm({ ...prolaboreForm, beneficiario: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o beneficiário" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Lucas">Lucas</SelectItem>
                    <SelectItem value="Alexandre">Alexandre</SelectItem>
                    <SelectItem value="Outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Valor *</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={prolaboreForm.valor}
                  onChange={(e) => setProlaboreForm({ ...prolaboreForm, valor: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Data *</Label>
                <Input
                  type="date"
                  value={prolaboreForm.data}
                  onChange={(e) => setProlaboreForm({ ...prolaboreForm, data: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Conta Bancária (débito)</Label>
                <Select
                  value={prolaboreForm.conta_id || 'none'}
                  onValueChange={(v) => setProlaboreForm({ ...prolaboreForm, conta_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a conta" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma (não debitar)</SelectItem>
                    {bankAccounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>
                        {acc.nome} (R$ {acc.saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Observação</Label>
                <Input
                  placeholder="Observação (opcional)"
                  value={prolaboreForm.observacao}
                  onChange={(e) => setProlaboreForm({ ...prolaboreForm, observacao: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setProlaboreDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit">
                  Registrar Pró-labore
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal para Nova Conta a Receber Avulsa */}
        <Dialog open={receivableDialogOpen} onOpenChange={(open) => {
          setReceivableDialogOpen(open);
          if (!open) {
            setClienteSearch('');
            setClienteMode('existente');
            setShowClienteDropdown(false);
          }
        }}>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden pb-[1000px] sm:pb-6 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Nova Conta a Receber
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateReceivable} className="space-y-4">
              {/* Seleção de Cliente */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Cliente *</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={clienteMode === 'existente' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setClienteMode('existente');
                        setReceivableForm({ ...receivableForm, cliente_id: '', cliente_nome: '' });
                      }}
                    >
                      Existente
                    </Button>
                    <Button
                      type="button"
                      variant={clienteMode === 'novo' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setClienteMode('novo');
                        setReceivableForm({ ...receivableForm, cliente_id: '', cliente_nome: '' });
                      }}
                    >
                      + Novo
                    </Button>
                    <Button
                      type="button"
                      variant={clienteMode === 'avulso' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setClienteMode('avulso');
                        setReceivableForm({ ...receivableForm, cliente_id: '' });
                      }}
                    >
                      Avulso
                    </Button>
                  </div>
                </div>

                {clienteMode === 'existente' && (
                  <div className="relative">
                    <Input
                      placeholder="Digite para pesquisar cliente..."
                      value={clienteSearch}
                      onChange={(e) => {
                        setClienteSearch(e.target.value);
                        setShowClienteDropdown(true);
                      }}
                      onFocus={() => setShowClienteDropdown(true)}
                      className="w-full"
                    />
                    {showClienteDropdown && clienteSearch && (
                      <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {customers
                          .filter(c => 
                            searchMatch(c.nome, clienteSearch) ||
                            c.telefone?.includes(clienteSearch)
                          )
                          .slice(0, 10)
                          .map(c => (
                            <div
                              key={c.id}
                              className="px-3 py-2 cursor-pointer hover:bg-muted flex justify-between items-center"
                              onClick={() => {
                                setReceivableForm({ ...receivableForm, cliente_id: c.id, cliente_nome: c.nome });
                                setClienteSearch(c.nome);
                                setShowClienteDropdown(false);
                              }}
                            >
                              <div>
                                <div className="font-medium">{c.nome}</div>
                                <div className="text-xs text-muted-foreground">{c.telefone}</div>
                              </div>
                            </div>
                          ))}
                        {customers.filter(c => 
                          searchMatch(c.nome, clienteSearch) ||
                          c.telefone?.includes(clienteSearch)
                        ).length === 0 && (
                          <div className="px-3 py-2 text-muted-foreground text-sm">
                            Nenhum cliente encontrado
                          </div>
                        )}
                      </div>
                    )}
                    {receivableForm.cliente_id && (
                      <div className="mt-2 p-2 bg-muted rounded flex justify-between items-center">
                        <span className="text-sm">
                          Cliente: <strong>{customers.find(c => c.id === receivableForm.cliente_id)?.nome}</strong>
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setReceivableForm({ ...receivableForm, cliente_id: '', cliente_nome: '' });
                            setClienteSearch('');
                          }}
                        >
                          Limpar
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {clienteMode === 'novo' && (
                  <div className="grid grid-cols-2 gap-3 p-4 bg-muted/50 rounded border">
                    <Input
                      placeholder="Nome *"
                      value={novoCliente.nome}
                      onChange={(e) => setNovoCliente({ ...novoCliente, nome: e.target.value })}
                      required
                    />
                    <Input
                      placeholder="Telefone"
                      value={novoCliente.telefone}
                      onChange={(e) => setNovoCliente({ ...novoCliente, telefone: formatPhone(e.target.value) })}
                    />
                    <Input
                      placeholder="Email"
                      value={novoCliente.email}
                      onChange={(e) => setNovoCliente({ ...novoCliente, email: e.target.value })}
                    />
                    <Input
                      placeholder="CPF/CNPJ"
                      value={novoCliente.cpf}
                      onChange={(e) => setNovoCliente({ ...novoCliente, cpf: e.target.value })}
                    />
                    <div className="col-span-2">
                      <Input
                        placeholder="Endereço"
                        value={novoCliente.endereco}
                        onChange={(e) => setNovoCliente({ ...novoCliente, endereco: e.target.value })}
                      />
                    </div>
                    <p className="col-span-2 text-xs text-muted-foreground">
                      O cliente será cadastrado automaticamente ao criar a conta a receber.
                    </p>
                  </div>
                )}

                {clienteMode === 'avulso' && (
                  <Input
                    placeholder="Nome ou descrição"
                    value={receivableForm.cliente_nome}
                    onChange={(e) => setReceivableForm({ ...receivableForm, cliente_nome: e.target.value })}
                    required
                  />
                )}
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="valor_rec">Valor Total (R$) *</Label>
                  <Input
                    id="valor_rec"
                    type="number"
                    step="0.01"
                    placeholder="0,00"
                    value={receivableForm.valor}
                    onChange={(e) => setReceivableForm({ ...receivableForm, valor: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="num_parcelas">Nº Parcelas</Label>
                  <Input
                    id="num_parcelas"
                    type="number"
                    min="1"
                    max="48"
                    value={receivableForm.num_parcelas}
                    onChange={(e) => setReceivableForm({ ...receivableForm, num_parcelas: parseInt(e.target.value) || 1 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="data_vencimento_rec">1ª Parcela *</Label>
                  <Input
                    id="data_vencimento_rec"
                    type="date"
                    value={receivableForm.data_vencimento}
                    onChange={(e) => setReceivableForm({ ...receivableForm, data_vencimento: e.target.value })}
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="observacao_rec">Observação</Label>
                <Input
                  id="observacao_rec"
                  placeholder="Observação (opcional)"
                  value={receivableForm.observacao}
                  onChange={(e) => setReceivableForm({ ...receivableForm, observacao: e.target.value })}
                />
              </div>

              {/* Preview das parcelas */}
              {parcelasPreview.length > 0 && (
                <div className="border rounded p-3 bg-muted/30 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Preview das Parcelas</span>
                    <span className="text-xs text-muted-foreground">
                      Total: R$ {parseFloat(receivableForm.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {parcelasPreview.map((parcela, idx) => (
                      <div key={`parcela-preview-${parcela.numero}-${idx}`} className="flex items-center gap-2 text-sm py-1 border-b border-dashed last:border-0">
                        <span className="text-muted-foreground w-16">{parcela.numero}/{parcelasPreview.length}</span>
                        <Input
                          type="date"
                          value={parcela.data_vencimento}
                          onChange={(e) => {
                            const newParcelas = [...parcelasPreview];
                            newParcelas[idx].data_vencimento = e.target.value;
                            setParcelasPreview(newParcelas);
                          }}
                          className="h-7 text-xs flex-1"
                        />
                        <div className="flex items-center gap-1">
                          <span className="text-xs">R$</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={parcela.valor.toFixed(2)}
                            onChange={(e) => {
                              const newParcelas = [...parcelasPreview];
                              newParcelas[idx].valor = parseFloat(e.target.value) || 0;
                              setParcelasPreview(newParcelas);
                            }}
                            className="h-7 text-xs w-24 font-mono"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" className="flex-1" onClick={() => {
                  setReceivableDialogOpen(false);
                  setParcelasPreview([]);
                }}>
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1">
                  <Plus className="h-4 w-4 mr-1" />
                  Adicionar {parcelasPreview.length > 1 ? `${parcelasPreview.length} Parcelas` : ''}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal para Confirmar Recebimento */}
        <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-[1000px] sm:pb-6 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                Registrar Recebimento
              </DialogTitle>
            </DialogHeader>
            {receivableToPay && (
              <div className="space-y-4">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Cliente</p>
                  <p className="font-medium">{receivableToPay.cliente_nome}</p>
                  <p className="text-sm text-muted-foreground mt-2">Parcela</p>
                  <p className="font-medium">{receivableToPay.numero_parcela}/{receivableToPay.total_parcelas}</p>
                  <p className="text-sm text-muted-foreground mt-2">Valor</p>
                  <p className="font-mono text-lg text-green-500">
                    R$ {receivableToPay.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>Forma de Pagamento</Label>
                  <select
                    className="w-full p-2 border rounded bg-background text-base md:text-sm"
                    value={payForm.forma_pagamento}
                    onChange={(e) => setPayForm({ ...payForm, forma_pagamento: e.target.value })}
                  >
                    <option value="PIX">PIX</option>
                    <option value="DINHEIRO">Dinheiro</option>
                    <option value="TRANSFERENCIA">Transferência</option>
                    <option value="CARTAO_DEBITO">Cartão Débito</option>
                    <option value="CARTAO_CREDITO">Cartão Crédito</option>
                    <option value="BOLETO">Boleto</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <Label>Conta de Recebimento *</Label>
                  <select
                    className="w-full p-2 border rounded bg-background text-base md:text-sm"
                    value={payForm.conta_id}
                    onChange={(e) => setPayForm({ ...payForm, conta_id: e.target.value })}
                    required
                  >
                    <option value="">Selecione a conta...</option>
                    {bankAccounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.nome} - Saldo: R$ {account.saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setPayDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="button" className="flex-1 bg-green-600 hover:bg-green-700" onClick={handleConfirmPay}>
                    <Check className="h-4 w-4 mr-1" />
                    Confirmar Recebimento
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Modal de Edição de Transação */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-[1000px] sm:pb-6 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle className="font-manrope">Editar Transação</DialogTitle>
              <DialogDescription>Altere os dados da transação</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-descricao">Descrição</Label>
                <Input
                  id="edit-descricao"
                  value={editFormData.descricao}
                  onChange={(e) => setEditFormData({ ...editFormData, descricao: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-valor">Valor</Label>
                <Input
                  id="edit-valor"
                  type="number"
                  step="0.01"
                  value={editFormData.valor}
                  onChange={(e) => setEditFormData({ ...editFormData, valor: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-data">Data</Label>
                <Input
                  id="edit-data"
                  type="date"
                  value={editFormData.data}
                  onChange={(e) => setEditFormData({ ...editFormData, data: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select 
                  value={editFormData.categoria || 'none'} 
                  onValueChange={(value) => setEditFormData({ ...editFormData, categoria: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a categoria" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={4}>
                    <SelectItem value="none">Sem categoria</SelectItem>
                    {/* Categorias de Receita */}
                    <SelectItem value="receita" className="text-green-600 font-medium">
                      💰 Receita de Vendas
                    </SelectItem>
                    <SelectItem value="receita_servicos">Receita de Serviços</SelectItem>
                    <SelectItem value="recebimento">Recebimento de Cliente</SelectItem>
                    <SelectItem value="transferencia_entrada">Transferência Recebida</SelectItem>
                    <SelectItem value="outras_receitas">Outras Receitas</SelectItem>
                    {/* Categorias de Despesa */}
                    {transactionCategories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Conta Bancária</Label>
                <Select 
                  value={editFormData.conta_id || 'none'} 
                  onValueChange={(value) => setEditFormData({ ...editFormData, conta_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a conta" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma</SelectItem>
                    {bankAccounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>{acc.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setEditDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1">
                  Salvar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* ==================== MODAIS DE CONTAS A PAGAR ==================== */}
        
        {/* Modal Nova Conta */}
        <Dialog open={billDialogOpen} onOpenChange={setBillDialogOpen}>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-[1000px] sm:pb-6 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle>Nova Conta a Pagar</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleBillSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Descrição *</Label>
                <Input
                  value={billFormData.descricao}
                  onChange={(e) => setBillFormData({ ...billFormData, descricao: e.target.value })}
                  placeholder="Ex: Aluguel, Luz, Internet..."
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Valor *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={billFormData.valor}
                  onChange={(e) => setBillFormData({ ...billFormData, valor: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Data de Vencimento *</Label>
                <Input
                  type="date"
                  value={billFormData.dataVencimento}
                  onChange={(e) => setBillFormData({ ...billFormData, dataVencimento: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select value={billFormData.categoria} onValueChange={(v) => {
                  const autoDre = getAutoClassificacaoDRE(v);
                  setBillFormData({ 
                    ...billFormData, 
                    categoria: v,
                    classificacao_dre: autoDre || billFormData.classificacao_dre 
                  });
                }}>
                  <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {billCategories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Classificação DRE {billFormData.classificacao_dre && <span className="text-xs text-emerald-500">(auto-preenchido)</span>}</Label>
                <Select value={billFormData.classificacao_dre} onValueChange={(v) => setBillFormData({ ...billFormData, classificacao_dre: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecione para DRE..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custo_variavel">Custo Variável</SelectItem>
                    <SelectItem value="custo_fixo">Custo Fixo</SelectItem>
                    <SelectItem value="despesa_operacional">Despesa Operacional</SelectItem>
                    <SelectItem value="investimento">Investimento</SelectItem>
                    <SelectItem value="pagamento_obrigacao">Pagamento de Obrigação</SelectItem>
                    <SelectItem value="estoque">Estoque (Ativo)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Classifique para cálculo correto do DRE</p>
              </div>
              <Button type="submit" className="w-full"><Plus className="h-4 w-4 mr-2" />Adicionar</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal Conta Recorrente */}
        <Dialog open={recurringDialogOpen} onOpenChange={setRecurringDialogOpen}>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-[1000px] sm:pb-6 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5" />
                Nova Conta Fixa Recorrente
              </DialogTitle>
              <DialogDescription>
                A conta será gerada automaticamente todo mês.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleRecurringSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Descrição *</Label>
                <Input
                  value={recurringFormData.descricao}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, descricao: e.target.value })}
                  placeholder="Ex: Aluguel, Plano de Saúde..."
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Tipo de Valor</Label>
                <Select value={recurringFormData.tipo_valor} onValueChange={(v) => setRecurringFormData({ ...recurringFormData, tipo_valor: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixo">Valor Fixo (mesmo todo mês)</SelectItem>
                    <SelectItem value="variavel">Valor Variável (informar ao gerar)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {recurringFormData.tipo_valor === 'fixo' && (
                <div className="space-y-2">
                  <Label>Valor Fixo *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={recurringFormData.valor}
                    onChange={(e) => setRecurringFormData({ ...recurringFormData, valor: e.target.value })}
                    placeholder="0.00"
                    required={recurringFormData.tipo_valor === 'fixo'}
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label>Dia do Vencimento *</Label>
                <Select value={recurringFormData.dia_vencimento} onValueChange={(v) => setRecurringFormData({ ...recurringFormData, dia_vencimento: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[...Array(28)].map((_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>Dia {i + 1}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select value={recurringFormData.categoria} onValueChange={(v) => {
                  const autoDre = getAutoClassificacaoDRE(v);
                  setRecurringFormData({ 
                    ...recurringFormData, 
                    categoria: v,
                    classificacao_dre: autoDre || recurringFormData.classificacao_dre 
                  });
                }}>
                  <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {billCategories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Classificação DRE {recurringFormData.classificacao_dre && <span className="text-xs text-emerald-500">(auto)</span>}</Label>
                <Select value={recurringFormData.classificacao_dre} onValueChange={(v) => setRecurringFormData({ ...recurringFormData, classificacao_dre: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecione para DRE..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custo_variavel">Custo Variável</SelectItem>
                    <SelectItem value="custo_fixo">Custo Fixo</SelectItem>
                    <SelectItem value="despesa_operacional">Despesa Operacional</SelectItem>
                    <SelectItem value="investimento">Investimento</SelectItem>
                    <SelectItem value="pagamento_obrigacao">Pagamento de Obrigação</SelectItem>
                    <SelectItem value="estoque">Estoque (Ativo)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full"><Plus className="h-4 w-4 mr-2" />Criar Conta Fixa</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal Conta Parcelada */}
        <Dialog open={installmentDialogOpen} onOpenChange={(open) => { setInstallmentDialogOpen(open); if (!open) { setShowParcelasPreview(false); setParcelas([]); } }}>
          <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto overflow-x-hidden pb-[1000px] sm:pb-6 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Conta Parcelada
              </DialogTitle>
              <DialogDescription>
                Gere múltiplas contas de uma compra parcelada.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Descrição *</Label>
                  <Input
                    value={installmentFormData.descricao}
                    onChange={(e) => setInstallmentFormData({ ...installmentFormData, descricao: e.target.value })}
                    placeholder="Ex: Compra de equipamento"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Categoria</Label>
                  <Select value={installmentFormData.categoria} onValueChange={(v) => {
                    const autoDre = getAutoClassificacaoDRE(v);
                    setInstallmentFormData({ 
                      ...installmentFormData, 
                      categoria: v,
                      classificacao_dre: autoDre || installmentFormData.classificacao_dre 
                    });
                  }}>
                    <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      {billCategories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Classificação DRE {installmentFormData.classificacao_dre && <span className="text-xs text-emerald-500">(auto)</span>}</Label>
                  <Select value={installmentFormData.classificacao_dre} onValueChange={(v) => setInstallmentFormData({ ...installmentFormData, classificacao_dre: v })}>
                    <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custo_variavel">Custo Variável</SelectItem>
                      <SelectItem value="custo_fixo">Custo Fixo</SelectItem>
                      <SelectItem value="despesa_operacional">Despesa Operacional</SelectItem>
                      <SelectItem value="investimento">Investimento</SelectItem>
                      <SelectItem value="pagamento_obrigacao">Pagamento de Obrigação</SelectItem>
                      <SelectItem value="estoque">Estoque (Ativo)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Valor Total *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={installmentFormData.valorTotal}
                    onChange={(e) => setInstallmentFormData({ ...installmentFormData, valorTotal: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nº Parcelas *</Label>
                  <Select value={installmentFormData.numParcelas} onValueChange={(v) => setInstallmentFormData({ ...installmentFormData, numParcelas: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 18, 24].map(n => (
                        <SelectItem key={n} value={String(n)}>{n}x</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>1ª Parcela *</Label>
                  <Input
                    type="date"
                    value={installmentFormData.dataPrimeiraParcela}
                    onChange={(e) => setInstallmentFormData({ ...installmentFormData, dataPrimeiraParcela: e.target.value })}
                  />
                </div>
              </div>
              <Button type="button" variant="outline" className="w-full" onClick={gerarParcelas}>
                Gerar Parcelas
              </Button>
              {showParcelasPreview && parcelas.length > 0 && (
                <div className="border rounded-lg p-3 sm:p-4 space-y-2 max-h-64 overflow-y-auto">
                  <h4 className="font-medium text-sm sm:text-base">Parcelas a criar:</h4>
                  {/* Mobile: Cards | Desktop: Table */}
                  <div className="block sm:hidden space-y-2">
                    {parcelas.map((p, i) => (
                      <div key={`parcela-mobile-${p.numero}-${i}`} className="border rounded-lg p-3 space-y-2 bg-muted/30">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-sm">Parcela {p.numero}</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={p.valor}
                            onChange={(e) => atualizarParcela(i, 'valor', e.target.value)}
                            className="h-8 text-sm w-24 text-right"
                            placeholder="Valor"
                          />
                        </div>
                        <Input
                          value={p.descricao}
                          onChange={(e) => atualizarParcela(i, 'descricao', e.target.value)}
                          className="h-8 text-sm"
                          placeholder="Descrição"
                        />
                        <Input
                          type="date"
                          value={p.dataVencimento}
                          onChange={(e) => atualizarParcela(i, 'dataVencimento', e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                    ))}
                    <div className="flex justify-between items-center font-bold pt-2 border-t">
                      <span>Total:</span>
                      <span>R$ {parcelas.reduce((s, p) => s + p.valor, 0).toFixed(2)}</span>
                    </div>
                  </div>
                  {/* Desktop: Table */}
                  <table className="hidden sm:table w-full text-sm">
                    <thead>
                      <tr className="text-muted-foreground">
                        <th className="text-left p-1">#</th>
                        <th className="text-left p-1">Descrição</th>
                        <th className="text-left p-1">Vencimento</th>
                        <th className="text-right p-1">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parcelas.map((p, i) => (
                        <tr key={`parcela-desktop-${p.numero}-${i}`} className="border-t">
                          <td className="p-1">{p.numero}</td>
                          <td className="p-1">
                            <Input
                              value={p.descricao}
                              onChange={(e) => atualizarParcela(i, 'descricao', e.target.value)}
                              className="h-7 text-sm"
                            />
                          </td>
                          <td className="p-1">
                            <Input
                              type="date"
                              value={p.dataVencimento}
                              onChange={(e) => atualizarParcela(i, 'dataVencimento', e.target.value)}
                              className="h-7 text-sm"
                            />
                          </td>
                          <td className="p-1 text-right">
                            <Input
                              type="number"
                              step="0.01"
                              value={p.valor}
                              onChange={(e) => atualizarParcela(i, 'valor', e.target.value)}
                              className="h-7 text-sm w-24 text-right"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="font-bold border-t">
                        <td colSpan="3" className="p-1 text-right">Total:</td>
                        <td className="p-1 text-right">R$ {parcelas.reduce((s, p) => s + p.valor, 0).toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                  <Button className="w-full mt-4" onClick={handleInstallmentSubmit}>
                    <Plus className="h-4 w-4 mr-2" />
                    Criar {parcelas.length} Parcelas
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal Pagar Conta */}
        <Dialog open={billPayDialogOpen} onOpenChange={setBillPayDialogOpen}>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-[1000px] sm:pb-6 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle>Pagar Conta</DialogTitle>
              <DialogDescription>
                {selectedBill?.descricao}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {!selectedBill?.valor && (
                <div className="space-y-2">
                  <Label>Valor *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={billPayValor}
                    onChange={(e) => setBillPayValor(e.target.value)}
                    placeholder="0.00"
                    required
                  />
                </div>
              )}
              {selectedBill?.valor && (
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Valor da conta:</p>
                  <p className="text-2xl font-bold font-mono">R$ {selectedBill.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
              )}
              
              {/* Juros/Tarifas */}
              <div className="space-y-2">
                <Label>Juros/Tarifas Adicionais</Label>
                <Select value={tipoAdicionalPagamento} onValueChange={setTipoAdicionalPagamento}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nenhum">Nenhum</SelectItem>
                    <SelectItem value="juros">Juros</SelectItem>
                    <SelectItem value="tarifa">Tarifa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {tipoAdicionalPagamento !== 'nenhum' && (
                <div className="space-y-2">
                  <Label>Valor do {tipoAdicionalPagamento === 'juros' ? 'Juros' : 'Tarifa'} (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={valorAdicionalPagamento}
                    onChange={(e) => setValorAdicionalPagamento(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              )}

              {/* Toggle pagamento dividido */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="splitPaymentBill"
                  checked={splitPayment}
                  onChange={(e) => setSplitPayment(e.target.checked)}
                  className="h-4 w-4"
                />
                <Label htmlFor="splitPaymentBill">Dividir pagamento entre contas</Label>
              </div>

              {!splitPayment ? (
                <div className="space-y-2">
                  <Label>Conta para débito</Label>
                  <Select value={billPayContaId} onValueChange={setBillPayContaId}>
                    <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhuma (não debitar)</SelectItem>
                      {bankAccounts.map(acc => (
                        <SelectItem key={acc.id} value={acc.id}>
                          {acc.nome} (R$ {acc.saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Dividir entre contas:</Label>
                  {splitPayments.map((sp, idx) => (
                    <div key={`split-payment-${sp.conta_id || 'new'}-${idx}`} className="flex gap-2">
                      <Select value={sp.conta_id} onValueChange={(v) => {
                        const newSplit = [...splitPayments];
                        newSplit[idx].conta_id = v;
                        setSplitPayments(newSplit);
                      }}>
                        <SelectTrigger className="flex-1"><SelectValue placeholder="Conta" /></SelectTrigger>
                        <SelectContent>
                          {bankAccounts.map(acc => (
                            <SelectItem key={acc.id} value={acc.id}>{acc.nome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Valor"
                        className="w-28"
                        value={sp.valor}
                        onChange={(e) => {
                          const newSplit = [...splitPayments];
                          newSplit[idx].valor = e.target.value;
                          setSplitPayments(newSplit);
                        }}
                      />
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => setSplitPayments([...splitPayments, { conta_id: '', valor: '' }])}>
                    + Adicionar conta
                  </Button>
                </div>
              )}

              {/* Upload de comprovante */}
              <div className="space-y-2">
                <Label>Comprovante de pagamento (opcional)</Label>
                <Input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp"
                  onChange={(e) => setBillPayComprovante(e.target.files[0])}
                  data-testid="comprovante-input"
                />
                {billPayComprovante && (
                  <p className="text-xs text-muted-foreground">
                    Arquivo selecionado: {billPayComprovante.name}
                  </p>
                )}
                {selectedBill?.comprovante_url && !billPayComprovante && (
                  <div className="flex items-center gap-2 text-xs text-green-600">
                    <Check className="h-3 w-3" />
                    <a href={`${API_URL}${selectedBill.comprovante_url}`} target="_blank" rel="noopener noreferrer" className="underline">
                      Ver comprovante anexado
                    </a>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setBillPayDialogOpen(false)}>Cancelar</Button>
                <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={handleBillPay} disabled={uploadingComprovante}>
                  {uploadingComprovante ? (
                    <>Enviando...</>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Confirmar Pagamento
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal Gerar Conta Fixa */}
        <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-[1000px] sm:pb-6 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle>Gerar Conta do Mês</DialogTitle>
              <DialogDescription>
                {selectedRecurring?.descricao}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {selectedRecurring?.tipo_valor === 'fixo' ? (
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Valor fixo:</p>
                  <p className="text-2xl font-bold font-mono">R$ {selectedRecurring.valor?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Valor deste mês *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={generateValor}
                    onChange={(e) => setGenerateValor(e.target.value)}
                    placeholder="0.00"
                    required
                  />
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setGenerateDialogOpen(false)}>Cancelar</Button>
                <Button className="flex-1" onClick={handleGenerateBill}>
                  <Play className="h-4 w-4 mr-2" />
                  Gerar Conta
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: Cadastrar Faturamento Mensal */}
        <Dialog open={faturamentoDialogOpen} onOpenChange={setFaturamentoDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ajustar Faturamento Mensal</DialogTitle>
              <DialogDescription>Sobrescreve o valor das NF-es para este mês (use apenas se necessário ajuste)</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Mês</Label>
                  <Select value={faturamentoForm.mes.toString()} onValueChange={(v) => setFaturamentoForm({ ...faturamentoForm, mes: parseInt(v) })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                        <SelectItem key={m} value={m.toString()}>
                          {['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][m-1]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Ano</Label>
                  <Select value={faturamentoForm.ano.toString()} onValueChange={(v) => setFaturamentoForm({ ...faturamentoForm, ano: parseInt(v) })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map(a => (
                        <SelectItem key={a} value={a.toString()}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Valor do Faturamento *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={faturamentoForm.valor}
                  onChange={(e) => setFaturamentoForm({ ...faturamentoForm, valor: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Observação</Label>
                <Input
                  value={faturamentoForm.observacao}
                  onChange={(e) => setFaturamentoForm({ ...faturamentoForm, observacao: e.target.value })}
                  placeholder="Ex: NF-es 1001 a 1050"
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setFaturamentoDialogOpen(false)}>Cancelar</Button>
                <Button className="flex-1" onClick={handleSaveFaturamento}>Salvar</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: Cadastrar DAS Pago */}
        <Dialog open={dasPagoDialogOpen} onOpenChange={setDasPagoDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Registrar DAS Pago</DialogTitle>
              <DialogDescription>Informe o valor do DAS efetivamente pago</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Mês Referência</Label>
                  <Select value={dasPagoForm.mes.toString()} onValueChange={(v) => setDasPagoForm({ ...dasPagoForm, mes: parseInt(v) })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                        <SelectItem key={m} value={m.toString()}>
                          {['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][m-1]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Ano</Label>
                  <Select value={dasPagoForm.ano.toString()} onValueChange={(v) => setDasPagoForm({ ...dasPagoForm, ano: parseInt(v) })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map(a => (
                        <SelectItem key={a} value={a.toString()}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Valor Pago *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={dasPagoForm.valor_pago}
                  onChange={(e) => setDasPagoForm({ ...dasPagoForm, valor_pago: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Data do Pagamento *</Label>
                <Input
                  type="date"
                  value={dasPagoForm.data_pagamento}
                  onChange={(e) => setDasPagoForm({ ...dasPagoForm, data_pagamento: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Observação</Label>
                <Input
                  value={dasPagoForm.observacao}
                  onChange={(e) => setDasPagoForm({ ...dasPagoForm, observacao: e.target.value })}
                  placeholder="Ex: Documento 12345"
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setDasPagoDialogOpen(false)}>Cancelar</Button>
                <Button className="flex-1" onClick={handleSaveDasPago}>Registrar</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: Simulação de Cenários */}
        <Dialog open={simulacaoDialogOpen} onOpenChange={setSimulacaoDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" /> Simulação de Cenários
              </DialogTitle>
              <DialogDescription>Simule o impacto de diferentes faturamentos no DAS</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Faturamento Mensal Simulado *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={simulacaoForm.faturamento_mes}
                    onChange={(e) => setSimulacaoForm({ ...simulacaoForm, faturamento_mes: e.target.value })}
                    placeholder="Ex: 150000"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Quantidade de Meses</Label>
                  <Select value={simulacaoForm.meses_futuros.toString()} onValueChange={(v) => setSimulacaoForm({ ...simulacaoForm, meses_futuros: parseInt(v) })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 mês</SelectItem>
                      <SelectItem value="3">3 meses</SelectItem>
                      <SelectItem value="6">6 meses</SelectItem>
                      <SelectItem value="12">12 meses</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={handleSimulacao} className="w-full">
                <Calculator className="h-4 w-4 mr-2" /> Calcular Simulação
              </Button>

              {simulacaoResultado && (
                <div className="mt-4 p-4 bg-muted rounded-lg">
                  <p className="text-sm font-medium mb-2">
                    Faturamento atual (12 meses): <span className="font-mono">{formatCurrency(simulacaoResultado.faturamento_12_meses_atual)}</span>
                  </p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Mês</TableHead>
                        <TableHead className="text-right">Fat. Estimado 12M</TableHead>
                        <TableHead className="text-right">Faixa</TableHead>
                        <TableHead className="text-right">Alíquota</TableHead>
                        <TableHead className="text-right">DAS Estimado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {simulacaoResultado.simulacoes.map((s) => (
                        <TableRow key={s.mes}>
                          <TableCell>Mês {s.mes}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(s.faturamento_12_meses_estimado)}</TableCell>
                          <TableCell className="text-right">{s.faixa}</TableCell>
                          <TableCell className="text-right">{s.aliquota_efetiva}%</TableCell>
                          <TableCell className="text-right font-mono text-destructive font-bold">{formatCurrency(s.valor_das_estimado)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: Confirmar Dados do DAS Importado via PDF */}
        <Dialog open={dasConfirmDialogOpen} onOpenChange={setDasConfirmDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-600">
                <FileText className="h-5 w-5" /> Dados Extraídos do DAS
              </DialogTitle>
              <DialogDescription>
                Confira os dados extraídos do PDF. Você pode editar antes de salvar.
              </DialogDescription>
            </DialogHeader>
            
            {dasExtractedData && (
              <div className="space-y-6">
                {/* Dados Gerais */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
                  <div>
                    <Label className="text-xs text-muted-foreground">Período de Apuração</Label>
                    <p className="font-bold text-lg">{dasExtractedData.periodo_apuracao || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Data de Vencimento</Label>
                    <Input 
                      type="text"
                      value={dasExtractedData.data_vencimento || ''}
                      onChange={(e) => setDasExtractedData({...dasExtractedData, data_vencimento: e.target.value})}
                      placeholder="DD/MM/AAAA"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Mês</Label>
                    <Input 
                      type="number"
                      value={dasExtractedData.mes || ''}
                      onChange={(e) => setDasExtractedData({...dasExtractedData, mes: parseInt(e.target.value)})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Ano</Label>
                    <Input 
                      type="number"
                      value={dasExtractedData.ano || ''}
                      onChange={(e) => setDasExtractedData({...dasExtractedData, ano: parseInt(e.target.value)})}
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Valor Total */}
                <div className="p-4 bg-green-500/10 border border-green-500 rounded-lg">
                  <Label className="text-sm text-green-600 font-medium">Valor Total do Documento</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-2xl font-bold text-green-600">R$</span>
                    <Input 
                      type="number"
                      step="0.01"
                      value={dasExtractedData.valor_total || ''}
                      onChange={(e) => setDasExtractedData({...dasExtractedData, valor_total: parseFloat(e.target.value)})}
                      className="text-2xl font-bold font-mono w-48"
                    />
                  </div>
                </div>

                {/* Composição dos Tributos */}
                {dasExtractedData.tributos && Object.values(dasExtractedData.tributos).some(v => v > 0) && (
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <Label className="text-sm font-medium mb-3 block">Composição dos Tributos</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.entries(dasExtractedData.tributos).map(([tributo, valor]) => (
                        <div key={tributo} className="p-2 bg-background rounded border">
                          <Label className="text-xs text-muted-foreground">{tributo}</Label>
                          <Input 
                            type="number"
                            step="0.01"
                            value={valor || ''}
                            onChange={(e) => setDasExtractedData({
                              ...dasExtractedData, 
                              tributos: {...dasExtractedData.tributos, [tributo]: parseFloat(e.target.value) || 0}
                            })}
                            className="font-mono text-sm mt-1"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Informações adicionais */}
                {(dasExtractedData.cnpj || dasExtractedData.razao_social) && (
                  <div className="text-sm text-muted-foreground">
                    {dasExtractedData.razao_social && <p><strong>Empresa:</strong> {dasExtractedData.razao_social}</p>}
                    {dasExtractedData.cnpj && <p><strong>CNPJ:</strong> {dasExtractedData.cnpj}</p>}
                    {dasExtractedData.numero_documento && <p><strong>Nº Documento:</strong> {dasExtractedData.numero_documento}</p>}
                  </div>
                )}

                {/* Botões de ação */}
                <div className="flex gap-3 justify-end pt-4 border-t">
                  <Button variant="outline" onClick={() => {
                    setDasConfirmDialogOpen(false);
                    setDasExtractedData(null);
                  }}>
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleConfirmDasUpload}
                    className="bg-green-600 hover:bg-green-700"
                    disabled={!dasExtractedData.mes || !dasExtractedData.ano || !dasExtractedData.valor_total}
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Confirmar e Salvar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}